import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { apiFetch as fetch } from './utils/apiFetch';
import ReportingInfoModal from "./ReportingInfoModal";
import ProjectDetailsModal from "./ProjectDetailsModal";
import RollOffEditModal from "./RollOffEditModal";
import AddEditCandidateModal from "./AddEditCandidateModal";
import CandidateUpdateModal from "./components/CandidateUpdateModal";
import BulkEmailModal from "./BulkEmailModal";
import AccountManagerModal from "./AccountManagerModal";
import ActivityLogSystem from "./ActivityLogSystem";
import InterviewPanel from "./InterviewPanel";
import Survey from "./Survey";
import CandidateHistoryModal from "./CandidateHistoryModal";
import Dashboard from "./Dashboard";
import AdminDashboard from "./AdminDashboard";
import CandidatesTab from "./CandidatesTab";
import RollOffTab from "./RollOffTab";
import RDTeamSection from "./rd/RDTeamSection";

import RDAllCandidatesSection from "./rd/RDAllCandidatesSection";

import { BsFire } from "react-icons/bs";
import RDDashboard from "./rd/RDDashboard";
import HotlistHub from "./components/HotlistHub";
import AISearchPanel from "./components/AISearchPanel";
import BUDrilldownModal from "./BUDrilldownModal";
import ClientDrilldownModal from "./ClientDrilldownModal";
import InterviewedCandidates from "./InterviewedCandidates";
import Header from "./Header";
import GoogleSheetsSyncModal from "./GoogleSheetsSyncModal";
import TutorialModal from "./TutorialModal";
import ActiveRequirements from "./ActiveRequirements";
import ExtensionNotificationModal from "./ExtensionNotificationModal";

import {
  Users,
  UserPlus,
  RefreshCw,
  Search,
  Download,
  Upload,
  Edit2,
  Trash2,
  Eye,
  EyeOff,
  ChevronLeft,
  ChevronRight,
  Phone,
  Mail,
  MoreVertical,
  Plus,
  X,
  Clock,
  Settings,
  Bell,
  User,
  LogOut,
  ChevronDown,
  Briefcase,
  Save,
  AlertTriangle,
  Copy,
  Archive,
  Send,
  UserCheck,
  UserX,
  Users2,
  Star,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  Info,
  Menu,
  ArrowRight,
  TrendingUp,
  Calendar,
  Filter,
  Target,
  Zap,
  Award,
  Activity,
  Building,
  CalendarDays,
  FileText,
  MapPin,
  DollarSign,
  Table,
  XCircle,
  Sparkles,
  Edit,
  Linkedin,
} from "lucide-react";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";
import { log } from "three";

// Error Boundary Component - ADD THIS HERE ??
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              Something went wrong
            </h2>
            <p className="text-slate-600 mb-4">
              We're sorry, but something unexpected happened.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Add this component before your main LandingPage component
const AnimatedCounter = ({ end, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (isComplete) return;

    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);

      setCount(Math.floor(progress * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        setIsComplete(true);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [end, duration, isComplete]);

  return <span>{count.toLocaleString()}</span>;
};

// Add this AFTER the AnimatedCounter component (around line 142)
const ProjectFormStatus = ({ candidate }) => {
  const getFormStatus = () => {
    if (candidate.project_form_completed_at) {
      return {
        status: "completed",
        text: "Form Completed",
        color: "bg-green-100 text-green-800 border-green-200",
        icon: CheckCircle,
      };
    } else if (candidate.project_form_sent_at) {
      const hoursElapsed =
        (new Date() - new Date(candidate.project_form_sent_at)) /
        (1000 * 60 * 60);
      const daysElapsed = Math.floor(hoursElapsed / 24);

      if (hoursElapsed >= 24) {
        return {
          status: "critical_overdue",
          text: `OVERDUE ${daysElapsed}d (${Math.floor(hoursElapsed)}h)`,
          color: "bg-red-100 text-red-800 border-red-200 animate-pulse",
          icon: AlertTriangle,
          hoursElapsed: Math.floor(hoursElapsed),
        };
      } else {
        const hoursRemaining = 24 - Math.floor(hoursElapsed);
        return {
          status: "pending",
          text: `Sent (${hoursRemaining}h left)`,
          color: "bg-yellow-100 text-yellow-800 border-yellow-200",
          icon: Clock,
          hoursElapsed: Math.floor(hoursElapsed),
        };
      }
    } else {
      return {
        status: "not_sent",
        text: "Form Not Sent",
        color: "bg-gray-100 text-gray-800 border-gray-200",
        icon: AlertCircle,
      };
    }
  };

  const formStatus = getFormStatus();
  const Icon = formStatus.icon;

  return (
    <div
      className={`flex items-center space-x-2 px-3 py-1 rounded-full text-xs font-medium border ${formStatus.color}`}
      title={`Form sent: ${candidate.project_form_sent_at ? new Date(candidate.project_form_sent_at).toLocaleString() : "Not sent"}`}
    >
      <Icon className="w-3 h-3" />
      <span>{formStatus.text}</span>
      {formStatus.status === "critical_overdue" && (
        <span className="ml-1 text-red-600 font-bold">??</span>
      )}
    </div>
  );
};

// Add this component before your main LandingPage component
const ProgressBar = ({ value, total, label, color = "blue" }) => {
  const percentage = (value / Math.max(total, 1)) * 100;

  const colorClasses = {
    blue: "bg-gradient-to-r from-blue-600 to-purple-600",
    green: "bg-gradient-to-r from-green-500 to-emerald-500",
    yellow: "bg-gradient-to-r from-yellow-500 to-orange-500",
    red: "bg-gradient-to-r from-red-500 to-pink-500",
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        <div className="flex items-center space-x-2">
          <span className="text-sm font-bold text-slate-900">
            {value}/{total}
          </span>
          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
            {Math.round(percentage)}%
          </span>
        </div>
      </div>
      <div className="w-full bg-slate-200 rounded-full h-3">
        <div
          className={`h-full ${colorClasses[color]} transition-all duration-700 ease-out rounded-full`}
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
    </div>
  );
};

// Enhanced Calendar Date Picker Component
const CustomDatePicker = ({
  value,
  onChange,
  placeholder = "Select date",
  label,
  error,
  className = "",
  minDate,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    value ? new Date(value) : null,
  );
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const daysOfWeek = ["S", "M", "T", "W", "T", "F", "S"];

  const formatDisplayDate = (date) => {
    if (!date) return placeholder;
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Previous month's trailing days
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const prevDay = new Date(year, month, -i);
      days.push({
        date: prevDay,
        isCurrentMonth: false,
        isToday: false,
        isSelected: false,
      });
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(year, month, day);
      const today = new Date();
      const isToday = currentDate.toDateString() === today.toDateString();
      const isSelected =
        selectedDate &&
        currentDate.toDateString() === selectedDate.toDateString();

      days.push({
        date: currentDate,
        isCurrentMonth: true,
        isToday,
        isSelected,
      });
    }

    // Next month's leading days
    const remainingSlots = 42 - days.length; // 6 rows ? 7 days
    for (let day = 1; day <= remainingSlots; day++) {
      const nextDay = new Date(year, month + 1, day);
      days.push({
        date: nextDay,
        isCurrentMonth: false,
        isToday: false,
        isSelected: false,
      });
    }

    return days;
  };

  const handleDateSelect = (date) => {
    if (minDate && date < new Date(minDate)) return;

    setSelectedDate(date);
    const formattedDate = date.toISOString().split("T")[0]; // yyyy-mm-dd format
    onChange(formattedDate);
    setIsOpen(false);
  };

  const handleMonthChange = (increment) => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + increment);
      return newMonth;
    });
  };

  const handleYearChange = (increment) => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      newMonth.setFullYear(prev.getFullYear() + increment);
      return newMonth;
    });
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentMonth(today);
    handleDateSelect(today);
  };

  const clearDate = () => {
    setSelectedDate(null);
    onChange("");
    setIsOpen(false);
  };

  const days = getDaysInMonth(currentMonth);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest(".date-picker-container")) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div className={`flex flex-col gap-2 ${className} date-picker-container`}>
      {label && (
        <label className="block text-sm font-medium text-slate-700">
          {label}
        </label>
      )}
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 text-left flex items-center justify-between bg-white transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
            error ? "border-red-500" : "border-slate-300"
          }`}
        >
          <span className={selectedDate ? "text-slate-900" : "text-slate-500"}>
            {formatDisplayDate(selectedDate)}
          </span>
          <CalendarDays className="w-4 h-4 text-slate-400" />
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl z-50 p-4 w-80">
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <select
                  value={currentMonth.getMonth()}
                  onChange={(e) => {
                    const newMonth = new Date(currentMonth);
                    newMonth.setMonth(parseInt(e.target.value));
                    setCurrentMonth(newMonth);
                  }}
                  className="text-sm font-semibold bg-transparent border-none focus:outline-none"
                >
                  {months.map((month, index) => (
                    <option key={month} value={index}>
                      {month}
                    </option>
                  ))}
                </select>
                <select
                  value={currentMonth.getFullYear()}
                  onChange={(e) => {
                    const newMonth = new Date(currentMonth);
                    newMonth.setFullYear(parseInt(e.target.value));
                    setCurrentMonth(newMonth);
                  }}
                  className="text-sm font-semibold bg-transparent border-none focus:outline-none"
                >
                  {Array.from(
                    { length: 20 },
                    (_, i) => currentMonth.getFullYear() - 10 + i,
                  ).map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-1">
                <button
                  type="button"
                  onClick={() => handleYearChange(-1)}
                  className="p-1 hover:bg-slate-100 rounded"
                  title="Previous Year"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleMonthChange(-1)}
                  className="p-1 hover:bg-slate-100 rounded"
                  title="Previous Month"
                >
                  <ChevronLeft className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  onClick={() => handleMonthChange(1)}
                  className="p-1 hover:bg-slate-100 rounded"
                  title="Next Month"
                >
                  <ChevronRight className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  onClick={() => handleYearChange(1)}
                  className="p-1 hover:bg-slate-100 rounded"
                  title="Next Year"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Days of Week Header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {daysOfWeek.map((day) => (
                <div
                  key={day}
                  className="text-center text-xs font-medium text-slate-500 p-2"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1 mb-4">
              {days.map((day, index) => {
                const isDisabled = minDate && day.date < new Date(minDate);
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() =>
                      !isDisabled &&
                      day.isCurrentMonth &&
                      handleDateSelect(day.date)
                    }
                    disabled={isDisabled || !day.isCurrentMonth}
                    className={`
                      p-2 text-sm rounded transition-colors
                      ${!day.isCurrentMonth ? "text-slate-300" : ""}
                      ${day.isToday ? "bg-blue-100 text-blue-600 font-semibold" : ""}
                      ${day.isSelected ? "bg-blue-600 text-white font-semibold" : ""}
                      ${day.isCurrentMonth && !day.isSelected && !day.isToday ? "hover:bg-slate-100" : ""}
                      ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}
                    `}
                  >
                    {day.date.getDate()}
                  </button>
                );
              })}
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-between pt-3 border-t border-slate-200">
              <button
                type="button"
                onClick={clearDate}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={goToToday}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Today
              </button>
            </div>

            <div className="flex justify-end mt-3 space-x-2">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="px-3 py-1 text-sm text-slate-600 hover:text-slate-800"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};

// Helper function to format JS date to MySQL DATETIME
function toMySQLDatetime(dateInput) {
  if (!dateInput) return null;
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 19).replace("T", " ");
}

const AdvancedExportModal = ({
  onClose,
  candidates = [],
  onExport,
  isExporting = false,
  uniqueBUHeads = [],
}) => {
  const [selectedFields, setSelectedFields] = useState({});
  const [exportFormat, setExportFormat] = useState("csv");
  const [exportFilters, setExportFilters] = useState({
    status: "all",
    dateRange: "all",
    client: "all",
    buHead: "all",
  });
  const [showPreview, setShowPreview] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({
    basic: true,
    project: true,
    financial: false,
    dates: false,
    management: false,
    additional: false,
  });

  // Sample candidates if none provided
  const sampleCandidates =
    candidates.length > 0
      ? candidates
      : [
          {
            plcCode: "PLC001",
            sapId: "SAP001",
            candidateName: "John Doe",
            candidateEmail: "john@email.com",
            client: "TechCorp",
            status: "Active",
          },
          {
            plcCode: "PLC002",
            sapId: "SAP002",
            candidateName: "Jane Smith",
            candidateEmail: "jane@email.com",
            client: "DataCorp",
            status: "Active",
          },
        ];

  // Field categories with organized structure - COMPLETE DATABASE SCHEMA
  const fieldCategories = {
    basic: {
      label: "Basic Information",
      icon: Users,
      color: "blue",
      fields: {
        plc_code: { label: "PLC Code", required: true },
        sap_id: { label: "SAP ID", required: true },
        candidate_name: { label: "Candidate Name", required: true },
        candidate_phone: { label: "Phone Number" },
        candidate_email: { label: "Email Address", required: true },
        dob: { label: "Date of Birth" },
        work_email: { label: "Work Email" },
        linkedin_url: { label: "LinkedIn URL" },
        is_real_candidate: { label: "Is Real Candidate" },
        candidate_added_source: { label: "Candidate Added Source" },
        status: { label: "Status" },
      },
    },
    project: {
      label: "Project Details",
      icon: Briefcase,
      color: "green",
      fields: {
        client: { label: "Client" },
        client_track: { label: "Client Track" },
        end_client: { label: "End Client" },
        job_title: { label: "Job Title" },
        primary_skills: { label: "Primary Skills" },
        secondary_skills: { label: "Secondary Skills" },
        project_city: { label: "Project City" },
        project_state: { label: "Project State" },
        project_country: { label: "Project Country" },
        contractor_status: { label: "Contract Type" },
        project_tech_stack: { label: "Tech Stack" },
        project_overview: { label: "Project Overview" },
        project_goals: { label: "Project Goals" },
        project_role_responsibility: { label: "Role & Responsibility" },
        key_achievement: { label: "Key Achievement" },
        project_experience_rating: { label: "Experience Rating" },
      },
    },
    financial: {
      label: "Financial Information",
      icon: DollarSign,
      color: "yellow",
      fields: {
        pay_rate: { label: "Pay Rate" },
        client_rate: { label: "Client Rate" },
        margin_value: { label: "Margin %" },
        pay_rate_currency: { label: "Pay Currency" },
        client_rate_currency: { label: "Client Currency" },
        pay_rate_payment_basis: { label: "Pay Rate Basis" },
        client_rate_payment_basis: { label: "Client Rate Basis" },
      },
    },
    dates: {
      label: "Contract Dates",
      icon: Calendar,
      color: "purple",
      fields: {
        start_date: { label: "Start Date" },
        end_date: { label: "End Date" },
        sow_end_date: { label: "SOW End Date" },
        sow_end_month: { label: "SOW End Month" },
        extended_sow_date: { label: "Extended SOW Date" },
        extended_count: { label: "Extension Count" },
        extension_date: { label: "Extension Date" },
      },
    },
    management: {
      label: "Management Team",
      icon: Award,
      color: "indigo",
      fields: {
        reporting_manager_name: { label: "Reporting Manager" },
        reporting_manager_email: { label: "Manager Email" },
        reporting_manager_phone: { label: "Manager Phone" },
        reporting_comments: { label: "Reporting Comments" },
        dal_name: { label: "DAL Name" },
        dm_name: { label: "DM Name" },
        add_name: { label: "ADD Name" },
        bu_head: { label: "BU Head" },
        bu_head_emailid: { label: "BU Head Email ID" },
        bu_name: { label: "BU Name" },
        cal_name: { label: "CAL Name" },
        passthrough_owner: { label: "Passthrough Owner" },
        passthrough_support: { label: "Passthrough Support" },
        lead_recruiter: { label: "Lead Recruiter" },
        team_lead_name: { label: "Team Lead Name" },
        recruiter_name: { label: "Recruiter Name" },
        recruiter_emp_id: { label: "Recruiter Employee ID" },
        deal_pt_ptr: { label: "Deal PT PTR" },
        client_project_manager: { label: "Client PM" },
        client_resource_manager: { label: "Client RM" },
      },
    },
    coi: {
      label: "Circle of Influences",
      icon: Users2,
      color: "teal",
      fields: {
        candidate_connected_date: { label: "Candidate Connected Date" },
        candidate_connected_comments: { label: "Candidate Connected Comments" },
        reporting_manager_location: { label: "Reporting Manager Location" },
        reporting_manager_linkedin: { label: "Reporting Manager LinkedIn" },
        client_tag_poc_name: { label: "Client Tag POC Name" },
        client_tag_poc_email: { label: "Client Tag POC Email" },
        client_tag_poc_phone: { label: "Client Tag POC Phone" },
        client_tag_poc_location: { label: "Client Tag POC Location" },
        client_tag_poc_linkedin: { label: "Client Tag POC LinkedIn" },
        client_tag_poc_connected_date: { label: "Client Tag POC Connected Date" },
        client_tag_poc_comments: { label: "Client Tag POC Comments" },
      },
    },
    vendor: {
      label: "Vendor Information",
      icon: Building,
      color: "gray",
      fields: {
        contractor_vendor_name: { label: "Vendor Name" },
        vendor_contact_person: { label: "Vendor Contact" },
        vendor_contact_number: { label: "Vendor Phone" },
        vendor_contact_email: { label: "Vendor Email" },
      },
    },
    account_managers: {
      label: "Account Managers",
      icon: Users2,
      color: "cyan",
      fields: {
        account_manager_1: { label: "Account Manager 1" },
        account_manager_2: { label: "Account Manager 2" },
        account_manager_3: { label: "Account Manager 3" },
        account_manager_4: { label: "Account Manager 4" },
        account_manager_5: { label: "Account Manager 5" },
        account_manager_6: { label: "Account Manager 6" },
        account_manager_7: { label: "Account Manager 7" },
        account_manager_8: { label: "Account Manager 8" },
      },
    },
    feedback: {
      label: "Client Feedback & Extensions",
      icon: Info,
      color: "orange",
      fields: {
        client_rating: { label: "Client Rating" },
        client_feedback: { label: "Client Feedback" },
        improvement_area_1: { label: "Improvement Area 1" },
        improvement_area_2: { label: "Improvement Area 2" },
        improvement_area_3: { label: "Improvement Area 3" },
        extension_requested: { label: "Extension Requested" },
        extension_status: { label: "Extension Status" },
        poc_name: { label: "POC Name" },
        extension_duration: { label: "Extension Duration" },
        extension_comments: { label: "Extension Comments" },
        no_extension_reason: { label: "No Extension Reason" },
        contacted_with: { label: "Contacted With" },
        pending_extension_reason: { label: "Pending Extension Reason" },
        last_working_date: { label: "Last Working Date" },
        looking_for_opportunity: { label: "Looking For Opportunity" },
      },
    },
    project_forms: {
      label: "Project Form Tracking",
      icon: FileText,
      color: "emerald",
      fields: {
        project_info_status: { label: "Project Info Status" },
        project_info_submitted_at: { label: "Project Info Submitted" },
        project_form_sent_at: { label: "Project Form Sent" },
        project_form_completed_at: { label: "Project Form Completed" },
        project_form_reminder_count: { label: "Form Reminder Count" },
        project_form_last_reminder_at: { label: "Last Form Reminder" },
      },
    },
    tracking: {
      label: "System Tracking",
      icon: Clock,
      color: "slate",
      fields: {
        created_at: { label: "Created Date" },
        updated_at: { label: "Last Updated" },
        reporting_updated_at: { label: "Reporting Updated" },
        reporting_updated_by: { label: "Reporting Updated By" },
        project_updated_at: { label: "Project Updated" },
        project_updated_by: { label: "Project Updated By" },
        feedback_updated_at: { label: "Feedback Updated" },
        feedback_updated_by: { label: "Feedback Updated By" },
        sent_to_rd: { label: "Sent to RD" },
        sent_to_rd_date: { label: "RD Forward Date" },
        extension_data_cleared_at: { label: "Extension Data Cleared At" },
        extension_data_cleared_by: { label: "Extension Data Cleared By" },
        last_extension_cycle: { label: "Last Extension Cycle" },
        extension_cycle_count: { label: "Extension Cycle Count" },
      },
    },

    // ✅ NEW: Negotiation Tracker Section
    negotiation: {
      label: "Negotiation Tracker",
      icon: DollarSign,
      color: "green",
      fields: {
        job_id: { label: "Job ID" },
        submission_id: { label: "Submission ID" },
        applicant_id: { label: "Applicant ID" },
        interview_id: { label: "Interview ID" },
        date: { label: "Negotiation Date" },
        candidate_name: { label: "Candidate Name" },
        phone_number: { label: "Phone Number" },
        email_id: { label: "Email ID" },
        candidate_location: { label: "Candidate Location" },
        job_title: { label: "Job Title" },
        job_code: { label: "Job Code" },
        core_skills: { label: "Core Skills" },
        client_name: { label: "Client Name" },
        client_track: { label: "Client Track" },
        end_client: { label: "End Client" },
        interview_status: { label: "Interview Status" },
        client_rate: { label: "Client Rate" },
        original_candidate_rate: { label: "Original Candidate Rate" },
        negotiated_rate: { label: "Negotiated Rate" },
        reduced_rate: { label: "Reduced Rate" },
        leaders_handling: { label: "Leaders Handling" },
        additional_comments: { label: "Additional Comments" },
        start_date: { label: "Start Date" },
        status: { label: "Negotiation Status" },
        created_at: { label: "Created Date" },
        updated_at: { label: "Last Updated" },
        created_by: { label: "Created By" },
        updated_by: { label: "Updated By" },
      },
    },
  };

  // Initialize selected fields
  useEffect(() => {
    const defaultFields = {};
    Object.keys(fieldCategories).forEach((category) => {
      Object.keys(fieldCategories[category].fields).forEach((field) => {
        const fieldInfo = fieldCategories[category].fields[field];
        defaultFields[field] =
          fieldInfo.required || category === "basic" || category === "project";
      });
    });
    setSelectedFields(defaultFields);
  }, []);

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  // Toggle all fields in category
  const toggleCategoryFields = (category, selectAll) => {
    const newFields = { ...selectedFields };
    Object.keys(fieldCategories[category].fields).forEach((field) => {
      if (!fieldCategories[category].fields[field].required) {
        newFields[field] = selectAll;
      }
    });
    setSelectedFields(newFields);
  };

  // Get filtered candidates based on export filters
  const getFilteredCandidates = () => {
  return sampleCandidates.filter((candidate) => {
    if (
      exportFilters.status !== "all" &&
      candidate.status !== exportFilters.status
    )
      return false;
    if (
      exportFilters.client !== "all" &&
      candidate.client !== exportFilters.client
    )
      return false;
    if (
      exportFilters.buHead !== "all" &&
      candidate.buHead !== exportFilters.buHead
    )
      return false;
    return true;
  });
};

  // Handle export
  const handleExport = () => {
    const filteredCandidates = getFilteredCandidates();
    const selectedFieldsList = Object.keys(selectedFields).filter(
      (field) => selectedFields[field],
    );

    onExport({
      candidates: filteredCandidates,
      fields: selectedFieldsList,
      format: exportFormat,
      filters: exportFilters,
    });
  };

  const filteredCandidates = getFilteredCandidates();
  const selectedFieldsCount =
    Object.values(selectedFields).filter(Boolean).length;
  const uniqueClients = [
    ...new Set(sampleCandidates.map((c) => c.client).filter(Boolean)),
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] lg:max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 text-white flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Download className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Advanced Export</h2>
                <p className="text-blue-100 text-sm">
                  Customize your data export
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-xl transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Main Content - Fixed Height with Scroll */}
        <div className="flex flex-col lg:flex-row flex-1 min-h-0">
          {/* Left Panel - Configuration */}
          <div className="flex-1 lg:w-2/3 p-6 overflow-y-auto lg:border-r border-gray-200">
            {/* Export Filters */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Filter className="w-5 h-5 mr-2 text-blue-600" />
                Data Filters
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={exportFilters.status}
                    onChange={(e) =>
                      setExportFilters((prev) => ({
                        ...prev,
                        status: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="all">All Status</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date Range
                  </label>
                  <select
                    value={exportFilters.dateRange}
                    onChange={(e) =>
                      setExportFilters((prev) => ({
                        ...prev,
                        dateRange: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="all">All Time</option>
                    <option value="current_month">Current Month</option>
                    <option value="last_month">Last Month</option>
                    <option value="ytd">Year to Date</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client
                  </label>
                  <select
                    value={exportFilters.client}
                    onChange={(e) =>
                      setExportFilters((prev) => ({
                        ...prev,
                        client: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="all">All Clients</option>
                    {uniqueClients.map((client) => (
                      <option key={client} value={client}>
                        {client}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Business Unit
  </label>
  <select
    value={exportFilters.buHead}
    onChange={(e) =>
      setExportFilters((prev) => ({
        ...prev,
        buHead: e.target.value,
      }))
    }
    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
  >
    <option value="all">All BUs</option>
    {uniqueBUHeads.map((bu) => (
      <option key={bu} value={bu}>
        {bu}
      </option>
    ))}
  </select>
</div>
              </div>
            </div>

            {/* Field Selection */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Table className="w-5 h-5 mr-2 text-blue-600" />
                Select Fields to Export
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({selectedFieldsCount} selected)
                </span>
              </h3>

              <div className="space-y-3">
                {Object.entries(fieldCategories).map(
                  ([categoryKey, category]) => {
                    const Icon = category.icon;
                    const isExpanded = expandedCategories[categoryKey];
                    const categoryFields = Object.keys(category.fields);
                    const selectedInCategory = categoryFields.filter(
                      (field) => selectedFields[field],
                    ).length;

                    return (
                      <div
                        key={categoryKey}
                        className="border border-gray-200 rounded-lg overflow-hidden"
                      >
                        <div className="bg-gray-50 px-4 py-3">
                          <div className="flex items-center justify-between">
                            <button
                              onClick={() => toggleCategory(categoryKey)}
                              className="flex items-center space-x-3 text-left flex-1"
                            >
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4 text-gray-500" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-500" />
                              )}
                              <Icon
                                className={`w-5 h-5 text-${category.color}-600`}
                              />
                              <span className="font-medium text-gray-900">
                                {category.label}
                              </span>
                              <span className="text-sm text-gray-500">
                                ({selectedInCategory}/{categoryFields.length})
                              </span>
                            </button>

                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() =>
                                  toggleCategoryFields(categoryKey, true)
                                }
                                className="text-xs text-blue-600 hover:text-blue-800"
                              >
                                Select All
                              </button>
                              <button
                                onClick={() =>
                                  toggleCategoryFields(categoryKey, false)
                                }
                                className="text-xs text-gray-600 hover:text-gray-800"
                              >
                                Clear
                              </button>
                            </div>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="p-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                              {Object.entries(category.fields).map(
                                ([fieldKey, field]) => (
                                  <label
                                    key={fieldKey}
                                    className="flex items-center space-x-2 cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={
                                        selectedFields[fieldKey] || false
                                      }
                                      onChange={(e) =>
                                        setSelectedFields((prev) => ({
                                          ...prev,
                                          [fieldKey]: e.target.checked,
                                        }))
                                      }
                                      disabled={field.required}
                                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <span
                                      className={`text-sm ${field.required ? "font-medium text-gray-900" : "text-gray-700"}`}
                                    >
                                      {field.label}
                                      {field.required && (
                                        <span className="text-red-500 ml-1">
                                          *
                                        </span>
                                      )}
                                    </span>
                                  </label>
                                ),
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  },
                )}
              </div>
            </div>

            {/* Export Format */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-blue-600" />
                Export Format
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  {
                    value: "csv",
                    label: "CSV",
                    description: "Comma-separated values",
                    icon: "??",
                  },
                  {
                    value: "excel",
                    label: "Excel",
                    description: "Microsoft Excel format",
                    icon: "??",
                  },
                  {
                    value: "json",
                    label: "JSON",
                    description: "JavaScript Object Notation",
                    icon: "??",
                  },
                ].map((format) => (
                  <label key={format.value} className="cursor-pointer">
                    <input
                      type="radio"
                      name="exportFormat"
                      value={format.value}
                      checked={exportFormat === format.value}
                      onChange={(e) => setExportFormat(e.target.value)}
                      className="sr-only"
                    />
                    <div
                      className={`p-4 border-2 rounded-lg transition-all ${
                        exportFormat === format.value
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="text-center">
                        <div className="text-2xl mb-2">{format.icon}</div>
                        <div className="font-medium text-gray-900">
                          {format.label}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {format.description}
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Right Panel - Preview */}
          <div className="w-full lg:w-1/3 p-6 bg-gray-50 lg:border-t-0 border-t border-gray-200 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Eye className="w-5 h-5 mr-2 text-blue-600" />
                Export Summary
              </h3>
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
              >
                {showPreview ? (
                  <EyeOff className="w-4 h-4 mr-1" />
                ) : (
                  <Eye className="w-4 h-4 mr-1" />
                )}
                {showPreview ? "Hide" : "Show"} Preview
              </button>
            </div>

            {/* Summary Stats */}
            <div className="space-y-4 mb-6">
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    Total Records
                  </span>
                  <span className="text-lg font-bold text-blue-600">
                    {filteredCandidates.length}
                  </span>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    Selected Fields
                  </span>
                  <span className="text-lg font-bold text-green-600">
                    {selectedFieldsCount}
                  </span>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    Export Format
                  </span>
                  <span className="text-sm font-bold text-purple-600 uppercase">
                    {exportFormat}
                  </span>
                </div>
              </div>
            </div>

            {/* Data Preview */}
            {showPreview && filteredCandidates.length > 0 && (
  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
    <div className="px-4 py-2 bg-gray-100 border-b">
      <h4 className="text-sm font-medium text-gray-900">
        Data Preview (First 3 records)
      </h4>
    </div>
    <div className="max-h-64 overflow-auto">
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50">
          <tr>
            {Object.keys(selectedFields)
  .filter((field) => selectedFields[field])
  .map((field) => (
    <th
                  key={field}
                  className="px-2 py-1 text-left font-medium text-gray-900 border-b whitespace-nowrap"
                >
                  {Object.values(fieldCategories).find(
                    (cat) => cat.fields[field],
                  )?.fields[field]?.label || field}
                </th>
              ))}
          </tr>
        </thead>
        <tbody>
          {filteredCandidates.slice(0, 3).map((candidate, index) => {
            // Map snake_case field keys to actual camelCase candidate data
            const previewFieldMap = {
              plc_code: candidate.plcCode || candidate.plc_code,
              sap_id: candidate.sapId || candidate.sap_id,
              candidate_name: candidate.candidateName || candidate.candidate_name,
              candidate_phone: candidate.candidatePhone || candidate.candidate_phone,
              candidate_email: candidate.candidateEmail || candidate.candidate_email,
              work_email: candidate.workEmail || candidate.work_email,
              status: candidate.status,
              client: candidate.client,
              client_track: candidate.clientTrack || candidate.client_track,
              end_client: candidate.endClient || candidate.end_client,
              job_title: candidate.jobTitle || candidate.job_title,
              primary_skills: candidate.primarySkills || candidate.primary_skills,
              secondary_skills: candidate.secondarySkills || candidate.secondary_skills,
              start_date: candidate.startDate || candidate.start_date,
              end_date: candidate.endDate || candidate.end_date,
              sow_end_date: candidate.sowEndDate || candidate.sow_end_date,
              project_city: candidate.projectCity || candidate.project_city,
              project_state: candidate.projectState || candidate.project_state,
              project_country: candidate.projectCountry || candidate.project_country,
              contractor_status: candidate.contractorStatus || candidate.contractor_status,
              pay_rate: candidate.payRate || candidate.pay_rate,
              client_rate: candidate.clientRate || candidate.client_rate,
              margin_value: candidate.marginValue || candidate.margin_value,
              dal_name: candidate.dalName || candidate.dal_name,
              dm_name: candidate.dmName || candidate.dm_name,
              add_name: candidate.addName || candidate.add_name,
              bu_head: candidate.buHead || candidate.bu_head,
              reporting_manager_name: candidate.reportingManagerName || candidate.reporting_manager_name,
              reporting_manager_email: candidate.reportingManagerEmail || candidate.reporting_manager_email,
              client_project_manager: candidate.clientProjectManager || candidate.client_project_manager,
              client_resource_manager: candidate.clientResourceManager || candidate.client_resource_manager,
              contractor_vendor_name: candidate.contractorVendorName || candidate.contractor_vendor_name,
              client_rating: candidate.clientRating || candidate.client_rating,
              extension_status: candidate.extensionStatus || candidate.extension_status,
              extension_requested: candidate.extensionRequested || candidate.extension_requested,
              account_manager_1: candidate.accountManager1 || candidate.account_manager_1,
              account_manager_2: candidate.accountManager2 || candidate.account_manager_2,
              created_at: candidate.created_at,
              updated_at: candidate.updated_at,
            };

            return (
              <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                {Object.keys(selectedFields)
  .filter((field) => selectedFields[field])
  .map((field) => {
    const value = previewFieldMap[field] ?? candidate[field];
                    return (
                      <td
                        key={field}
                        className="px-2 py-1.5 text-gray-700 truncate max-w-[120px]"
                        title={value || ''}
                      >
                        {value || (
                          <span className="text-gray-300 italic">empty</span>
                        )}
                      </td>
                    );
                  })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    <div className="px-4 py-2 bg-gray-50 border-t text-xs text-gray-500">
      Showing all {Object.values(selectedFields).filter(Boolean).length} selected fields · {filteredCandidates.length} total records
    </div>
  </div>
)}
          </div>
        </div>

        {/* FIXED Footer - Better Layout */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            {/* Left side - Summary text */}
            <div className="text-sm text-gray-600 order-2 sm:order-1">
              Ready to export{" "}
              <span className="font-semibold text-gray-900">
                {filteredCandidates.length}
              </span>{" "}
              records with{" "}
              <span className="font-semibold text-gray-900">
                {selectedFieldsCount}
              </span>{" "}
              fields
            </div>

            {/* Right side - Buttons */}
            <div className="flex items-center gap-3 order-1 sm:order-2 w-full sm:w-auto">
              <button
                onClick={onClose}
                className="flex-1 sm:flex-none px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                disabled={
                  isExporting ||
                  filteredCandidates.length === 0 ||
                  selectedFieldsCount === 0
                }
                className="flex-1 sm:flex-none px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 font-medium min-w-[140px]"
              >
                {isExporting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Exporting...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    <span>Export Data</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LandingPage = () => {
  const API_BASE_URL = "https://test.vdpl.co/Candidates/Candidates"; // ADD THIS LINE
  const RD_API_BASE_URL = "https://test.vdpl.co/api/index.php/";
  const RD_UPDATE_ENDPOINT = `${RD_API_BASE_URL}save_candidate`;

  const RD_UPDATE_ENDPOINT_FALLBACK =
    "https://test.vdpl.co/api/index.php/save_candidate";

  const RD_SEARCH_ENDPOINT = `${RD_API_BASE_URL}api_pilot_search`;

  const RD_CREATE_HOTLIST_GROUP_ENDPOINT = `${RD_API_BASE_URL}createHotlistGroup`;
  const RD_ADD_HOTLIST_CANDIDATE_ENDPOINT = `${RD_API_BASE_URL}addHotlistCandidate`;

  const [activeTab, setActiveTab] = useState("dashboard");
  const [candidates, setCandidates] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rdBenchFilter, setRdBenchFilter] = useState("all");
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showTutorialModal, setShowTutorialModal] = useState(false);

  const [currentUser, setCurrentUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const withCandidateUpdateMeta = useCallback(
    (candidateId, data = {}) => ({
      ...data,
      id: data?.id ?? candidateId ?? null,
      candidate_id: data?.candidate_id ?? data?.id ?? candidateId ?? null,
      updatedBy: currentUser
        ? `${currentUser.first_name ?? ''} ${currentUser.last_name ?? ''}`.trim() || currentUser.email
        : null,
      current_user_id: currentUser?.id ?? null,
    }),
    [currentUser],
  );

  // Get unique values for filter dropdowns
  const uniqueClients = [
    ...new Set(candidates?.map((c) => c.client).filter(Boolean) || []),
  ];
  const uniqueBUHeads = [
    ...new Set(candidates?.map((c) => c.buHead).filter(Boolean) || []),
  ];
  const uniqueLocations = [
  ...new Set(
    candidates
      ?.filter((c) => c.projectCity && c.projectState)
      .map((c) => `${c.projectCity}, ${c.projectState}`) || [],
  ),
];
  const uniqueContractTypes = [
    ...new Set(
      candidates?.map((c) => c.contractorStatus).filter(Boolean) || [],
    ),
  ];
  const uniqueClientProjectManagers = [
    ...new Set(
      candidates?.map((c) => c.clientProjectManager).filter(Boolean) || [],
    ),
  ];
  const uniqueClientResourceManagers = [
    ...new Set(
      candidates?.map((c) => c.clientResourceManager).filter(Boolean) || [],
    ),
  ];
  const uniqueReportingManagers = [
    ...new Set(
      candidates?.map((c) => c.reportingManagerName).filter(Boolean) || [],
    ),
  ];

  const uniqueCertifications = useMemo(() => {
  const allCertifications = new Set();
  
  candidates?.forEach((candidate) => {
    // Handle both comma-separated and array formats
    const certifications = candidate.certifications || candidate.certification;
    
    if (certifications) {
      if (Array.isArray(certifications)) {
        certifications.forEach(cert => {
          if (cert && cert.trim()) allCertifications.add(cert.trim());
        });
      } else if (typeof certifications === 'string') {
        certifications.split(',').forEach(cert => {
          if (cert && cert.trim()) allCertifications.add(cert.trim());
        });
      }
    }
  });
  return Array.from(allCertifications).sort();
}, [candidates]);


  const [searchTerm, setSearchTerm] = useState("");
  const [searchScope, setSearchScope] = useState("all");
  const [rdAdvancedFilters, setRdAdvancedFilters] = useState({
    skills: "",
    jobTitle: "",
    location: "",
    availability: "all",
    experience: "",
    employmentType: "",
  });
  const [filterStatus, setFilterStatus] = useState("all");

  const [dateFilter, setDateFilter] = useState("all");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  const [showRecentOnboarded, setShowRecentOnboarded] = useState(false);
  const [showActivityLogModal, setShowActivityLogModal] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCandidateUpdateModal, setShowCandidateUpdateModal] =
    useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReportingInfoModal, setShowReportingInfoModal] = useState(false);
  const [showRollOffEditModal, setShowRollOffEditModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showCandidateDetails, setShowCandidateDetails] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showBulkEmailPanel, setShowBulkEmailPanel] = useState(false);

  // Advanced filter states
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  // AFTER
const [clientFilter, setClientFilter] = useState([]);
const [clientProjectManagerFilter, setClientProjectManagerFilter] =
  useState([]);
const [clientResourceManagerFilter, setClientResourceManagerFilter] =
  useState([]);
const [reportingManagerFilter, setReportingManagerFilter] = useState([]);
const [extensionStatusFilter, setExtensionStatusFilter] = useState([]);
const [buHeadFilter, setBuHeadFilter] = useState([]);
const [skillsFilter, setSkillsFilter] = useState("");
const [locationFilter, setLocationFilter] = useState([]);
const [contractTypeFilter, setContractTypeFilter] = useState([]);
  const [rateRangeFilter, setRateRangeFilter] = useState("all");
  const [marginFilter, setMarginFilter] = useState("all");
  const [reportingStatusFilter, setReportingStatusFilter] = useState("all");
  const [projectStatusFilter, setProjectStatusFilter] = useState("all");
  const [endDateRangeFilter, setEndDateRangeFilter] = useState("all");
  const [customEndDateStart, setCustomEndDateStart] = useState("");
  const [customEndDateEnd, setCustomEndDateEnd] = useState("");

  const [showImportInstructions, setShowImportInstructions] = useState(false);
  const [showProjectDetailsModal, setShowProjectDetailsModal] = useState(false);

  const [showBUDrilldown, setShowBUDrilldown] = useState(null);
  const [showClientDrilldown, setShowClientDrilldown] = useState(null);

  const [dataLoading, setDataLoading] = useState(true);
  const [showMoreActions, setShowMoreActions] = useState(null);
  const [showRollOffMoreActions, setShowRollOffMoreActions] = useState(null);
  const [showAccountManagerModal, setShowAccountManagerModal] = useState(false);

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedCandidateHistory, setSelectedCandidateHistory] =
    useState(null);
  const [candidateHistory, setCandidateHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [showNotesModal, setShowNotesModal] = useState(false);
  const [candidateNotes, setCandidateNotes] = useState("");
  const [candidateNotesList, setCandidateNotesList] = useState([]);
  const [noteType, setNoteType] = useState("general");
  const [isImportantNote, setIsImportantNote] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);

  const [recentCandidates, setRecentCandidates] = useState([]);

  const [selectedCandidatesForEmail, setSelectedCandidatesForEmail] = useState(
    [],
  );
  const [sendingAccountManagerEmail, setSendingAccountManagerEmail] =
    useState(false);

  const [showAdvancedExportModal, setShowAdvancedExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showUserManagementModal, setShowUserManagementModal] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditUserModal, setShowEditUserModal] = useState(false);

  const [extensionFilter, setExtensionFilter] = useState("all");
const [certificationFilter, setCertificationFilter] = useState([]);
  const [sessionExpired, setSessionExpired] = useState(false);
const [showSessionExpiredBanner, setShowSessionExpiredBanner] = useState(false);

useEffect(() => {
  const handleSessionExpired = () => {
    setCurrentUser(null);
    setSessionExpired(true);
    setShowSessionExpiredBanner(true);
  };
  window.addEventListener('session-expired', handleSessionExpired);
  return () => window.removeEventListener('session-expired', handleSessionExpired);
}, []);

  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);

  const [showExtensionNotificationModal, setShowExtensionNotificationModal] = useState(false);

  const [bulkUpdateType, setBulkUpdateType] = useState("");
  const [selectedRollOffCandidates, setSelectedRollOffCandidates] = useState(
    [],
  );

  const [selectedReasonModal, setSelectedReasonModal] = useState(null);
  const [selectedBUModal, setSelectedBUModal] = useState(null);
  const [selectedClientModal, setSelectedClientModal] = useState(null);
  const [viewMode, setViewMode] = useState("dashboard");
  const [rdViewMode, setRdViewMode] = useState("all-candidates");
  const [rdBenchSearchTerm, setRdBenchSearchTerm] = useState("");
  const [rdAllCandidatesSearchTerm, setRdAllCandidatesSearchTerm] = useState("");
  const [rdHotlistSelection, setRdHotlistSelection] = useState("");

  const handleRdHotlistAdd = useCallback(
    async (payload) => {
      const target = String(payload?.hotlistName || "").trim();

      if (!target) return { ok: false, message: "Hotlist name is required." };

      const selectedCandidates = Array.isArray(payload?.candidates)
        ? payload.candidates
        : [];

      const selectedCandidateIds = selectedCandidates

        .map(
          (candidate) =>
            candidate?.candidate_id ?? candidate?.candidateId ?? candidate?.id,
        )

        .filter(
          (id) =>
            id !== null && id !== undefined && String(id).trim().length > 0,
        );

      const currentCandidateId =
        payload?.candidate?.candidate_id ??
        payload?.candidate?.candidateId ??
        payload?.candidate?.id ??
        null;

      const candidateIds = Array.from(
        new Set(
          [
            ...selectedCandidateIds,

            ...(currentCandidateId !== null && currentCandidateId !== undefined
              ? [currentCandidateId]
              : []),
          ].map((id) => String(id)),
        ),
      );

      if (!candidateIds.length) {
        const message = "Select at least one candidate to add to hotlist.";

        error(message);

        return { ok: false, message };
      }

      try {
        const parseResponse = async (response) => {
          const contentType = response.headers.get("content-type") || "";

          if (contentType.includes("application/json")) {
            return response.json();
          }

          const text = await response.text();

          return text ? { message: text } : {};
        };

        if (payload?.shouldCreateGroup) {
          const formData = new FormData();

          formData.append("name", target.toString());

          formData.append("action_by", String(currentUser?.id ?? ""));

          formData.append("user_id", String(currentUser?.id ?? ""));

          formData.append("hotlist_name", target);

          const response = await fetch(RD_CREATE_HOTLIST_GROUP_ENDPOINT, {
            method: "POST",

            body: formData,
          });

          const result = await parseResponse(response);

          if (!response.ok) {
            throw new Error(result?.message || `HTTP ${response.status}`);
          }

          const isSuccess =
            result?.status === "success" ||
            result?.success === true ||
            result?.message?.toLowerCase?.().includes("success");

          if (!isSuccess) {
            throw new Error(
              result?.message || "Unable to create hotlist group.",
            );
          }
        }

        await Promise.all(
          candidateIds.map(async (candidateId) => {
            const formData = new FormData();

            formData.append("candidate_id", String(candidateId));

            formData.append("name", target);

            formData.append("group_name", target);

            formData.append("hotlist_name", target);

            formData.append(
              "section",

              String(payload?.section || "bench"),
            );

            formData.append("action_by", String(currentUser?.id ?? ""));

            formData.append("user_id", String(currentUser?.id ?? ""));

            const response = await fetch(RD_ADD_HOTLIST_CANDIDATE_ENDPOINT, {
              method: "POST",

              body: formData,
            });

            const result = await parseResponse(response);

            if (!response.ok) {
              throw new Error(result?.message || `HTTP ${response.status}`);
            }

            const isSuccess =
              result?.status === "success" ||
              result?.success === true ||
              result?.message?.toLowerCase?.().includes("success");

            if (!isSuccess) {
              throw new Error(
                result?.message || "Unable to add candidate to hotlist.",
              );
            }
          }),
        );

        setRdHotlistSelection(target);

        setRdViewMode("hotlist");

        success(
          `Added ${candidateIds.length} candidate(s) to hotlist "${target}".`,
        );

        return { ok: true };
      } catch (err) {
        const message =
          err?.message ||
          "Failed to add candidate(s) to hotlist. Please try again.";

        console.error("Add candidate to hotlist error:", err);

        error(message);

        return { ok: false, message };
      }
    },

    [
      RD_CREATE_HOTLIST_GROUP_ENDPOINT,

      RD_ADD_HOTLIST_CANDIDATE_ENDPOINT,

      currentUser?.id,
    ],
  );

  const [selectedExtensionReason, setSelectedExtensionReason] = useState(null);

  const [selectedRiskLevel, setSelectedRiskLevel] = useState(null);
  const [selectedBUDetail, setSelectedBUDetail] = useState(null);
  const [selectedClientDetail, setSelectedClientDetail] = useState(null);

  // BU and Date filter states for dashboard
  const [buFilter, setBuFilter] = useState("all");
  const [dashboardDateRange, setDashboardDateRange] = useState("all");
  const [dashboardStartDate, setDashboardStartDate] = useState("");
  const [dashboardEndDate, setDashboardEndDate] = useState("");
  const [selectedRdCandidate, setSelectedRdCandidate] = useState(null);
  const [rdSidebarCandidate, setRdSidebarCandidate] = useState(null);
  const [aiPreviewCandidate, setAiPreviewCandidate] = useState(null);
  const [rdCvUploads, setRdCvUploads] = useState({});
  const rdCvInputRef = useRef(null);
  const [rdCandidates, setRdCandidates] = useState([]);

  const openBenchCandidatesMainPage = useCallback(() => {
    setSelectedRdCandidate(null);
    setRdSidebarCandidate(null);
    setAiPreviewCandidate(null);
    setRdViewMode("candidates");
  }, []);
  
  const [rdAllCandidates, setRdAllCandidates] = useState([]);
  const [rdAllCandidatesLoading, setRdAllCandidatesLoading] = useState(false);

  const [workAuthMap, setWorkAuthMap] = useState({});
  const [workAuthMapStatus, setWorkAuthMapStatus] = useState("idle");
  const [workAuthMapError, setWorkAuthMapError] = useState("");
  const resolveWorkAuthName = useCallback(
    (value) => {
      if (!value) return "";
      const key = String(value).trim();
      if (!key || key === "0") return "";
      if (workAuthMap[key]) return workAuthMap[key];
      if (/^\d+$/.test(key)) {
        const normalized = String(Number(key));
        if (workAuthMap[normalized]) return workAuthMap[normalized];
      }
      const matchKey = Object.keys(workAuthMap).find(
        (mapKey) => workAuthMap[mapKey] === key,
      );
      if (matchKey) return workAuthMap[matchKey];
      if (/^\d+$/.test(key)) return "";
      return key;
    },
    [workAuthMap],
  );
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    const loadWorkAuthMap = async () => {
      try {
        setWorkAuthMapStatus("loading");
        setWorkAuthMapError("");
        const response = await fetch(`${RD_API_BASE_URL}work_authorization`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },

          cache: "no-store",
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const result = await response.json();
        if (result?.status !== "success") {
          throw new Error("Invalid work authorization response");
        }
        const items = Array.isArray(result?.data) ? result.data : [];
        const nextMap = items.reduce((acc, item) => {
          const key = String(item?.id ?? "").trim();
          const value = String(item?.authorization_name ?? "").trim();
          if (key && value) acc[key] = value;
          return acc;
        }, {});
        if (isMounted) {
          setWorkAuthMap(nextMap);
          setWorkAuthMapStatus("loaded");
        }
      } catch (err) {
        if (isMounted) {
          setWorkAuthMapStatus("error");
          setWorkAuthMapError("Work authorization list unavailable");
        }
      }
    };

    loadWorkAuthMap();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [RD_API_BASE_URL]);

  useEffect(() => {
    if (!workAuthMap || Object.keys(workAuthMap).length === 0) return;
    setRdCandidates((prev) =>
      prev.map((candidate) => {
        const id =
          candidate.workAuthId ||
          candidate.candidateInfo?.workAuthorizationId ||
          candidate.workAuth ||
          candidate.candidateInfo?.workAuthorization ||
          "";
        const name = resolveWorkAuthName(id);
        if (!name) return candidate;
        return {
          ...candidate,
          workAuth: name,
          candidateInfo: {
            ...(candidate.candidateInfo || {}),
            workAuthorization: name,
            workAuthorizationId:
              candidate.candidateInfo?.workAuthorizationId ||
              candidate.workAuthId ||
              "",
          },
        };
      }),
    );
  }, [workAuthMap, resolveWorkAuthName]);

  const handleSaveCandidateInfo = async ({ candidateId, info }) => {
    if (!candidateId) return false;

    const normalizedWorkMode = Array.isArray(info?.workMode)
      ? info.workMode.filter(Boolean).join(", ")
      : typeof info?.workMode === "string"
        ? info.workMode
        : "Onsite";

      const formatPhoneWithCode = (code, number) => {
      const c = String(code || "").trim();
      const n = String(number || "").trim();
      if (!n) return "";
      return c ? `${c} ${n}` : n;
    };

    const primaryPhoneCode = String(info?.phoneCountryCode || "").trim();

    const primaryPhoneNumber = String(info?.phoneNumber || "").trim();

    const secondaryPhoneCode = String(info?.secondaryPhoneCountryCode || "").trim();

    const secondaryPhoneNumber = String(
      info?.secondaryPhoneNumber || "",
    ).trim();

    const userSession = JSON.parse(localStorage.getItem("user"));

    // ? Use FormData
    const formData = new FormData();

      const visaFiles = Array.isArray(info?.visaFiles)
      ? info.visaFiles.filter((file) => file instanceof File)
      : info?.visaFile instanceof File
        ? [info.visaFile]
        : [];

    // --- Collect all fields in a single object ---
    const inputData = {
      candidateId: candidateId,
      name: info?.name || "",
      title: info?.title || "",
      emailId: info?.emailId || "",
      candidate_email_secondary: info?.secondaryEmailId || "",
      linkedinUrl: info?.linkedinUrl || "",

      phoneNumber: formatPhoneWithCode(primaryPhoneCode, primaryPhoneNumber),

      secondaryPhoneNumber: formatPhoneWithCode(
        secondaryPhoneCode,
        secondaryPhoneNumber,
      ),
      workedWithVdart: info?.workedWithVdart || "No",
      referredBy: info?.referredById || "",
      previousClient: info?.previousClient || "",
      city: info?.city || "",
      state: info?.state || "",
      country: info?.country || "",
      experience: info?.experience || "",
      timeZonePrimary: info?.timeZonePrimary || "",
      timeZoneSecondary: info?.timeZoneSecondary || "",

      relocation: info?.relocation || "No",
      workMode: normalizedWorkMode,
      expectedSalary: info?.expectedSalary || "",
      workAuthorization: info?.workAuthorization || "",
      candidate_connect: info?.candidateConnect || "",

      connected_via: info?.candidateConnectVia || "",
      benchStatus: info?.benchStatus || "",
      candidateResponse: info?.candidateResponse || "",
      candidateReason: info?.candidateReason || "",
      comments: info?.comments || "",
      primary_skills: info?.primarySkills || "",
      secondary_skills: info?.secondarySkills || "",
      created_by: info?.created_by ?? userSession?.id ?? null,

      updatedBy: userSession?.id || null,
    };
    console.log(inputData);

    // --- Append JSON as a single "input" key ---
    formData.append("input", JSON.stringify(inputData));

    // --- Append files separately (real binary) ---
    if (info?.resumeFile instanceof File) {
      formData.append("resumeFile", info.resumeFile);
    }

    if (info?.visaFile instanceof File) {
      formData.append("visaFile", info.visaFile);
    }

    try {
      const postFormData = async (url) => {
        const response = await fetch(url, {
          method: "POST",

          body: formData, // ? NO JSON encoding for files
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `HTTP error! status: ${response.status}${errorText ? ` - ${errorText}` : ""}`,
          );
        }

        return response.json();
      };

      const result = await postFormData(RD_UPDATE_ENDPOINT);

      if (result?.status !== "success") {
        throw new Error(result?.message || "Update failed");
      }
            // Update local state immediately so UI reflects changes without waiting.
      const updatedCandidatePatch = {
        name: info?.name || "",
        title: info?.title || "",
        workAuth: resolveWorkAuthName(info?.workAuthorization || "") || "",
        candidateInfo: { ...info },
      };
      const isSameCandidate = (row) => {
        const rowId = String(row?.candidate_id ?? row?.candidateId ?? row?.id ?? "").trim();
        return rowId && rowId === String(candidateId).trim();
      };


      setRdCandidates((prev) =>
        prev.map((candidate) =>
          isSameCandidate(candidate)
            ? {
                ...candidate,
                 ...updatedCandidatePatch,

                name: info?.name || candidate.name,
                title: info?.title || candidate.title,
                              }
            : candidate,
        ),
      );
      setRdAllCandidates((prev) =>
        prev.map((candidate) =>
          isSameCandidate(candidate)
            ? {
                ...candidate,
                ...updatedCandidatePatch,
                candidateName: info?.name || candidate.candidateName || candidate.name,
                jobTitle: info?.title || candidate.jobTitle || candidate.title,

              }
            : candidate,
        ),
      );
            setSelectedRdCandidate((prev) =>
        isSameCandidate(prev)
          ? {
              ...prev,
              ...updatedCandidatePatch,
              name: info?.name || prev?.name,
              title: info?.title || prev?.title,
            }
          : prev,
      );
      setRdSidebarCandidate((prev) =>
        isSameCandidate(prev)
          ? {
              ...prev,
              ...updatedCandidatePatch,
              name: info?.name || prev?.name,
              title: info?.title || prev?.title,
            }
          : prev,
      );

      // Then reload from backend to ensure latest server-modified fields are synced.
      await Promise.allSettled([fetchRDCandidates?.(), fetchRDAllCandidates?.()]);


      return true;
    } catch (err) {
      console.error("Update RD candidate error:", err);
      return false;
    }
  };

  const normalizeRdCandidateList = useCallback((payload) => {
    if (Array.isArray(payload)) return payload;
    if (payload && Array.isArray(payload.data)) return payload.data;
    if (payload && Array.isArray(payload.records)) return payload.records;
    if (payload) return [payload];
    return [];
  }, []);

  const toSingleScalar = useCallback((value) => {
    if (Array.isArray(value)) {
      const firstValid = value.find(
        (item) =>
          item !== null && item !== undefined && String(item).trim() !== "",
      );

      return toSingleScalar(firstValid);
    }

    if (value === null || value === undefined) return "";

    const text = String(value).trim();

    if (!text) return "";

    if (text.includes(",")) return text.split(",")[0].trim();

    return text;
  }, []);

 
   const fetchRDCandidateById = useCallback(
     async (candidateId) => {
       const selectedCandidateId = String(toSingleScalar(candidateId) || "").trim();
       if (!selectedCandidateId) return null;
       try {
         const response = await fetch(
           `${RD_API_BASE_URL}api_get_candidate?candidate_id=${encodeURIComponent(selectedCandidateId)}`,
           { method: "GET" },
         );
 
         if (!response.ok) {
           console.error(
             `Fetch RD candidate by id failed: HTTP ${response.status}`,
           );
 
           return null;
         }
 
         let result = null;
 
         try {
           result = await response.json();
         } catch {
           return null;
         }
         if (!result) return null;
         if (
           result?.status &&
           result.status !== "success" &&
           !result?.data
         ) {
           return null;
         }
 


        const list = normalizeRdCandidateList(result?.data ?? result);
        const item =
          list.find((entry) => {
            const entryId =
              entry?.candidate_id ?? entry?.candidateId ?? entry?.id ?? null;

                      return String(entryId ?? "").trim() === selectedCandidateId;
          }) ?? (list.length === 1 ? list[0] : null);

        if (!item) return null;

        const workAuthIdRaw =
          item.work_authorization_id ||
          item.work_authorization ||
          item.workAuth ||
          "";
        const workAuthId = workAuthIdRaw ? String(workAuthIdRaw).trim() : "";
        const workAuthName = resolveWorkAuthName(workAuthId);

        const rawBenchFlag = String(
          item.is_candidate_on_bench ?? item.isCandidateOnBench ?? "0",
        )
          .trim()

          .toLowerCase();

        const isOnBench = rawBenchFlag === "1" || rawBenchFlag === "true";
        const resolvedCandidateId = item.candidate_id ?? item.id ?? null;
        const mapped = {
          id: resolvedCandidateId,
          candidate_id: resolvedCandidateId,
          candidateId: resolvedCandidateId,
          name: item.candidate_name || "",
          title: item.job_title || "",
          location:
            item.project_city && item.project_state
              ? `${item.project_city}, ${item.project_state}`
              : item.project_city ||
                item.project_state ||
                item.project_country ||
                "",
          status: isOnBench ? "On Bench" : item.status || "",

          is_candidate_on_bench: isOnBench ? "1" : "0",

          isCandidateOnBench: isOnBench,
          benchDays: item.bench_days || 0,
          experienceYears: item.experience_years ?? item.experience ?? "",
          availabilityStatus: item.availability_status || "",
          availability: item.availability || "",
          client: item.client || "",
          workAuth: workAuthName,
          workAuthId,
          locationDetail: item.project_country || "",
          score: item.score || 0,
          contractDetails: {
            noticePeriod: item.notice_period || "",
            rate: item.pay_rate ? `${item.pay_rate}` : "",
            expectedSalary: item.expected_salary || "",
            employmentType: item.contractor_status || "",
          },
          skillGroups: {
            primary: (item.primary_skills || "")
              .split(",")
              .map((skill) => skill.trim())
              .filter(Boolean),
            secondary: (item.secondary_skills || "")
              .split(",")
              .map((skill) => skill.trim())
              .filter(Boolean),
          },
          experienceItems: [],
          documents: [],
          pipeline: [],
          candidateInfo: {
            name: item.candidate_name || "",
            title: item.job_title || "",
            emailId: item.candidate_email || "",
            secondaryEmailId: item.candidate_email_secondary || "",
            linkedinUrl: item.linkedin_url || "",
            phoneNumber: item.candidate_phone || "",
            secondaryPhoneNumber: item.candidate_phone_secondary || "",
            referredBy: item.referred_by || "",

            referredById:
              item.referred_by_id || item.ref_id || item.referredById || "",
            workedWithVdart:
              item.worked_with_vdart === "yes"
                ? "Yes"
                : item.worked_with_vdart === "no"
                  ? "No"
                  : item.worked_with_vdart || "No",
            experience: item.experience_years || "",
            Client: item.previous_client || "",
            city: item.project_city || "",
            state: item.project_state || "",
            country: item.project_country || "",
            timeZonePrimary: item.timezone_primary || "",
            workMode: item.work_mode || "",
            expectedSalary: item.expected_salary || "",
            workAuthorization: workAuthName,
            workAuthorizationId: workAuthId,
            benchStatus:
              item.bench_status_id && String(item.bench_status_id) !== "0"
                ? String(item.bench_status_id)
                : "",

            is_candidate_on_bench: isOnBench ? "1" : "0",

            isCandidateOnBench: isOnBench,
            candidateResponse: item.candidate_response || "",
            candidateReason: item.candidate_reason || "",
            comments: item.candidate_comments || "",
            primarySkills: item.primary_skills || "",
            secondarySkills: item.secondary_skills || "",
          },
        };

        setRdCandidates((prev) => {
          const exists = prev.some((candidate) => candidate.id === mapped.id);
          if (!exists) return [mapped, ...prev];
          return prev.map((candidate) =>
            candidate.id === mapped.id
              ? { ...candidate, ...mapped }
              : candidate,
          );
        });

        return mapped;
      } catch (err) {
        console.error("Fetch RD candidate by id error:", err);
        return null;
      }
    },
    [
      RD_API_BASE_URL,
      normalizeRdCandidateList,
      
      resolveWorkAuthName,
      toSingleScalar,
    ],
  );
  const handleOpenAiCandidatePreview = useCallback(
    async (aiCandidate) => {
      const candidateId =
        aiCandidate?.candidateId ??
        aiCandidate?.id ??
        aiCandidate?.candidate_id;
      if (!candidateId) return;
      const fullCandidate = await fetchRDCandidateById(candidateId);
      const candidateToShow = fullCandidate
        ? {
            ...fullCandidate,
            id: fullCandidate.id ?? candidateId,
            candidate_id: fullCandidate.candidate_id ?? candidateId,
            candidateId: fullCandidate.candidateId ?? candidateId,
          }
        : {
            ...aiCandidate,
            id: aiCandidate?.id ?? candidateId,
            candidate_id: aiCandidate?.candidate_id ?? candidateId,
            candidateId: aiCandidate?.candidateId ?? candidateId,
          };
      setAiPreviewCandidate(candidateToShow);
    },
    [fetchRDCandidateById],
  );
  const [showAddRdModal, setShowAddRdModal] = useState(false);
  const [newRdCandidate, setNewRdCandidate] = useState({
    name: "",
    title: "",
    location: "",
    country: "",

    countryCode: "",

    state: "",

    stateCode: "",

    city: "",

    referredById: "",
    status: "On Bench",
    referredBy: "",
    workedWithVdart: "No",
    skills: "",
    availability: "",
    client: "",
    benchDays: "",
    experienceYears: "",
    availabilityStatus: "Available now",
    workAuth: "",
    locationDetail: "",
    noticePeriod: "",
    rate: "",
    employmentType: "",
    expectedSalary: "",
  });
  const [showExperienceModal, setShowExperienceModal] = useState(false);
  const [newExperience, setNewExperience] = useState({
    role: "",
    company: "",
    period: "",
    status: "Active",
    completedOn: "",
    summary: "",
    tags: "",
  });
  const getQuarterDefaults = (date = new Date()) => {
    const quarter = Math.floor(date.getMonth() / 3) + 1;
    return {
      quarter: String(quarter),
      week: "",
      date: "",
      role: "",
      customRole: "",
      requirementSource: "",
      client: "",
      customClient: "",
      bu: "",
      recruiter: "",
      teamName: "",
      submissionType: "",
      submissionStatus: "",
      reason: "",
    };
  };
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [submissionForm, setSubmissionForm] = useState(() =>
    getQuarterDefaults(),
  );
  const [submissionRoleOptions, setSubmissionRoleOptions] = useState([
    "Full Stack Developer",
    "Sr Data Architect",
    "iOS Developer",
    "Senior .Net Developer",
  ]);
  const [rdSubmissions, setRdSubmissions] = useState({});
  const [pipelineSelectedStage, setPipelineSelectedStage] = useState("Bench");
  const [submissionClientOptions, setSubmissionClientOptions] = useState([]);

  useEffect(() => {
    let isActive = true;
    const controller = new AbortController();

    const normalizeClientName = (entry) => {
      if (!entry) return "";
      return String(
        entry.client_name ||
          entry.clientName ||
          entry.client ||
          entry.name ||
          "",
      ).trim();
    };

    const loadSubmissionClients = async () => {
      try {
        const response = await fetch(`${RD_API_BASE_URL}api_getClient`, {
          signal: controller.signal,
        });
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `HTTP ${response.status}${errorText ? ` - ${errorText}` : ""}`,
          );
        }
        const result = await response.json();
        const rawList = Array.isArray(result?.data)
          ? result.data
          : Array.isArray(result)
            ? result
            : [];
        const names = rawList.map(normalizeClientName).filter(Boolean);
        if (!isActive) return;
        setSubmissionClientOptions(
          Array.from(new Set(names)).sort((a, b) => a.localeCompare(b)),
        );
      } catch (err) {
        if (err?.name === "AbortError") return;
        console.error("Failed to load submission clients:", err);
      }
    };

    loadSubmissionClients();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [RD_API_BASE_URL]);

  const rdStatusOptions = [
    "all",
    ...new Set(
      rdCandidates.map((candidate) => candidate.status).filter(Boolean),
    ),
  ];
  const rdLocationOptions = [
    "all",
    ...new Set(
      rdCandidates.map((candidate) => candidate.location).filter(Boolean),
    ),
  ];
  const rdExperienceOptions = [
    "all",
    ...new Set(
      rdCandidates
        .map((candidate) => candidate.experienceYears)
        .filter(Boolean),
    ),
  ];
  const rdSortedCandidates = [...rdCandidates].sort(
    (a, b) => (b.score || 0) - (a.score || 0),
  );

const rdAdvancedFilteredCandidates = rdSortedCandidates.filter(
    (candidate) => {
      const candidateLocation = (candidate.location || "").toLowerCase();
      const candidateExperience = (
        candidate.experienceYears || ""
      ).toLowerCase();
      const candidateEmploymentType = (
        candidate?.contractDetails?.employmentType ||
        candidate?.employmentType ||
        candidate?.candidateInfo?.employmentType ||
        candidate?.candidateInfo?.contractor_status ||
        candidate?.contractor_status ||
        ""
      ).toLowerCase();
      const candidateTitle = (
        candidate.title ||
        candidate.headline ||
        candidate.candidateInfo?.title ||
        candidate.job_title ||
        ""
      ).toLowerCase();
      const toSkillTokens = (value) =>
        String(value || "")
          .toLowerCase()
          .split(/[,\s]+/)
          .map((token) => token.trim())
          .filter(Boolean);
      const toSkillList = (value) => {
        if (Array.isArray(value)) {
          return value
            .map((item) => String(item || "").trim().toLowerCase())
            .filter(Boolean);
        }
        return String(value || "")
          .split(",")
          .map((item) => item.trim().toLowerCase())
          .filter(Boolean);
      };
      const skillTokens = toSkillTokens(rdAdvancedFilters.skills);
      const primarySkillPool = [
        ...toSkillList(candidate?.skillGroups?.primary),
        ...toSkillList(
          candidate?.candidateInfo?.primarySkills ??
            candidate?.candidateInfo?.primary_skills,
        ),
        ...toSkillList(candidate?.primarySkills ?? candidate?.primary_skills),
      ];
      const secondarySkillPool = [
        ...toSkillList(candidate?.skillGroups?.secondary),
        ...toSkillList(
          candidate?.candidateInfo?.secondarySkills ??
            candidate?.candidateInfo?.secondary_skills,
        ),
        ...toSkillList(candidate?.secondarySkills ?? candidate?.secondary_skills),
      ];
      const fallbackSkills = toSkillList(candidate?.skills);
      const combinedSkills = [
        ...primarySkillPool,
        ...secondarySkillPool,
        ...fallbackSkills,
      ];
      const matchesSkills =
        skillTokens.length === 0 ||
        skillTokens.every((token) =>
          combinedSkills.some((skill) => skill.includes(token)),
        );
      const matchesLocation =
        !String(rdAdvancedFilters.location || "").trim() ||
        String(rdAdvancedFilters.location || "").toLowerCase() === "all" ||
        candidateLocation.includes(
          String(rdAdvancedFilters.location || "").toLowerCase().trim(),
        );
      const matchesExperience =
        !String(rdAdvancedFilters.experience || "").trim() ||
        String(rdAdvancedFilters.experience || "").toLowerCase() === "all" ||
        candidateExperience.includes(
          String(rdAdvancedFilters.experience || "").toLowerCase().trim(),
        );
      const matchesJobTitle =
        !String(rdAdvancedFilters.jobTitle || "").trim() ||
        candidateTitle.includes(
          String(rdAdvancedFilters.jobTitle || "").toLowerCase().trim(),
        );
      const matchesEmploymentType =
        !String(rdAdvancedFilters.employmentType || "").trim() ||
        String(rdAdvancedFilters.employmentType || "").toLowerCase() ===
          "all" ||
        candidateEmploymentType.includes(
          String(rdAdvancedFilters.employmentType || "")
            .toLowerCase()
            .trim(),
        );
      return (
        matchesSkills &&
        matchesLocation &&
        matchesExperience &&
        matchesJobTitle &&
        matchesEmploymentType
      );
    },
  );


  const rdVisibleCandidates =
    rdBenchFilter === "all"
      ? rdAdvancedFilteredCandidates
      : rdAdvancedFilteredCandidates.filter(
          (candidate) => candidate.status === rdBenchFilter,
        );
  const rdOverviewCandidate = rdSidebarCandidate || rdCandidates[0];
  const getRdPipelineStage = (submission) => {
    const status = (submission?.submissionStatus || "").toLowerCase();
    const type = (submission?.submissionType || "").toLowerCase();

    if (status.includes("not joined") || status.includes("closure"))
      return "Not Joined";
    if (status.includes("placement")) return "Placement";
    if (status.includes("confirmation")) return "Confirmation";
    if (status.includes("interview")) return "Interview";
    if (type.includes("client submission")) return "Client Submission";
    if (submission) return "Submission";
    return "Bench";
  };
  const getRdAvailabilityStatus = (candidate, submission) => {
    const stage = getRdPipelineStage(submission);
    if (stage === "Bench") return "Available now";
    if (stage === "Submission") return "Submitted";
    if (stage === "Client Submission") return "Client submitted";
    if (stage === "Interview") return "In interview";
    if (stage === "Confirmation") return "Offer in review";
    if (stage === "Placement") return "Placed";
    if (stage === "Not Joined") return "Not joined";
    return candidate?.availabilityStatus || "Available now";
  };

  // Debug modal states useEffect - moved inside component
  useEffect(() => {}, [
    showCandidateDetails,
    showEditModal,
    showReportingInfoModal,
    showDeleteModal,
    selectedCandidate,
  ]);

  useEffect(() => {
    const hasOpenModal =
      showAddModal ||
      showEditModal ||
      showDeleteModal ||
      showReportingInfoModal ||
      showRollOffEditModal ||
      showCandidateDetails ||
      showProjectDetailsModal ||
      showHistoryModal ||
      showNotesModal ||
      showActivityLogModal ||
      showAdvancedExportModal ||
      showAccountManagerModal ||
      showBulkEmailPanel ||
      showImportInstructions ||
      showCandidateUpdateModal;

    if (hasOpenModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [
    showAddModal,
    showEditModal,
    showDeleteModal,
    showReportingInfoModal,
    showRollOffEditModal,
    showCandidateDetails,
    showProjectDetailsModal,
    showHistoryModal,
    showNotesModal,
    showActivityLogModal,
    showAdvancedExportModal,
    showAccountManagerModal,
    showBulkEmailPanel,
    showImportInstructions,
  ]);

  const itemsPerPage = 10;

  const filteredCandidates = useMemo(() => {
    return candidates.filter((candidate) => {
      // ========================================
      // 1. SEARCH FILTER
      // ========================================
      const candidateSkills =
        `${candidate.primarySkills || ""} ${candidate.secondarySkills || ""}`.toLowerCase();
      const candidateName = (candidate.candidateName || "").toLowerCase();
      const candidateLocation =
        `${candidate.projectCity || ""} ${candidate.projectState || ""}`.toLowerCase();
      const candidateAll =
  `${candidate.candidateName || ""} ${candidate.client || ""} ${candidate.jobTitle || ""} ${candidate.candidateEmail || ""} ${candidate.workEmail || ""} ${candidateSkills} ${candidate.plcCode || ""} ${candidate.sapId || ""} ${candidate.projectCity || ""} ${candidate.projectState || ""} ${candidate.buHead || ""} ${candidate.contractorStatus || ""} ${candidate.reportingManagerName || ""}`.toLowerCase();

      const searchTokens = searchTerm
        .toLowerCase()
        .split(/[,\s]+/)
        .filter(Boolean);
      const matchesSearchTokens = (text) =>
        searchTokens.length === 0 ||
        searchTokens.every((token) => text.includes(token));

      const matchesSearch = (() => {
        if (searchScope === "name") return matchesSearchTokens(candidateName);
        if (searchScope === "location")
          return matchesSearchTokens(candidateLocation);
        if (searchScope === "all") return matchesSearchTokens(candidateAll);
        return matchesSearchTokens(candidateSkills);
      })();

      if (!matchesSearch) return false;

      // ========================================
      // 2. STATUS FILTER
      // ========================================
      const matchesStatus =
        filterStatus === "all" ||
        (candidate.status || "").toLowerCase() === filterStatus.toLowerCase();

      if (!matchesStatus) return false;

      // ========================================
      // 3. DATE FILTER
      // ========================================
      let matchesDate = true;
      if (showRecentOnboarded || startDateFilter || endDateFilter) {
        const candidateStartDate = new Date(candidate.startDate);

        if (showRecentOnboarded) {
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(new Date().getDate() - 30);
          matchesDate =
            candidateStartDate >= thirtyDaysAgo &&
            candidateStartDate <= new Date();
        } else if (startDateFilter && endDateFilter) {
          const startFilter = new Date(startDateFilter);
          const endFilter = new Date(endDateFilter);
          matchesDate =
            candidateStartDate >= startFilter &&
            candidateStartDate <= endFilter;
        } else if (startDateFilter) {
          const startFilter = new Date(startDateFilter);
          matchesDate = candidateStartDate >= startFilter;
        } else if (endDateFilter) {
          const endFilter = new Date(endDateFilter);
          matchesDate = candidateStartDate <= endFilter;
        }

        if (!matchesDate) return false;
      }

      // ========================================
      // 4. CLIENT FILTER - MULTI-SELECT SUPPORT
      // ========================================
      const matchesClient = (() => {
        if (!Array.isArray(clientFilter) || clientFilter.length === 0) {
          return true; // No filter applied
        }
        return clientFilter.includes(candidate.client);
      })();

      if (!matchesClient) return false;

      // ========================================
      // 5. BU HEAD FILTER - MULTI-SELECT SUPPORT
      // ========================================
      const matchesBUHead = (() => {
        if (!Array.isArray(buHeadFilter) || buHeadFilter.length === 0) {
          return true; // No filter applied
        }
        return buHeadFilter.includes(candidate.buHead);
      })();

      if (!matchesBUHead) return false;

      // ========================================
      // 6. SKILLS FILTER
      // ========================================
      const matchesSkills =
        !skillsFilter ||
        (candidate.primarySkills || "")
          .toLowerCase()
          .includes(skillsFilter.toLowerCase()) ||
        (candidate.secondarySkills || "")
          .toLowerCase()
          .includes(skillsFilter.toLowerCase());

      if (!matchesSkills) return false;

      // ========================================
      // 7. LOCATION FILTER - MULTI-SELECT SUPPORT
      // ========================================
      const matchesLocation = (() => {
        if (!Array.isArray(locationFilter) || locationFilter.length === 0) {
          return true; // No filter applied
        }
        const candidateLocation = `${candidate.projectCity}, ${candidate.projectState}`;
        return locationFilter.includes(candidateLocation);
      })();

      if (!matchesLocation) return false;

      // ========================================
      // 8. CONTRACT TYPE FILTER - MULTI-SELECT SUPPORT
      // ========================================
      const matchesContractType = (() => {
        if (
          !Array.isArray(contractTypeFilter) ||
          contractTypeFilter.length === 0
        ) {
          return true; // No filter applied
        }
        return contractTypeFilter.includes(candidate.contractorStatus);
      })();

      if (!matchesContractType) return false;

      // ========================================
      // 9. CLIENT PROJECT MANAGER FILTER - MULTI-SELECT SUPPORT
      // ========================================
      const matchesClientProjectManager = (() => {
        if (
          !Array.isArray(clientProjectManagerFilter) ||
          clientProjectManagerFilter.length === 0
        ) {
          return true; // No filter applied
        }
        const managerValue =
          candidate.clientProjectManager ||
          candidate.client_project_manager ||
          "";
        return clientProjectManagerFilter.includes(managerValue);
      })();

      if (!matchesClientProjectManager) return false;

      // ========================================
      // 10. CLIENT RESOURCE MANAGER FILTER - MULTI-SELECT SUPPORT
      // ========================================
      const matchesClientResourceManager = (() => {
        if (
          !Array.isArray(clientResourceManagerFilter) ||
          clientResourceManagerFilter.length === 0
        ) {
          return true; // No filter applied
        }
        const managerValue =
          candidate.clientResourceManager ||
          candidate.client_resource_manager ||
          "";
        return clientResourceManagerFilter.includes(managerValue);
      })();

      if (!matchesClientResourceManager) return false;

      // ========================================
      // 11. REPORTING MANAGER FILTER - MULTI-SELECT SUPPORT
      // ========================================
      const matchesReportingManager = (() => {
        if (
          !Array.isArray(reportingManagerFilter) ||
          reportingManagerFilter.length === 0
        ) {
          return true; // No filter applied
        }
        const managerValue =
          candidate.reportingManagerName ||
          candidate.reporting_manager_name ||
          "";
        return reportingManagerFilter.includes(managerValue);
      })();

      if (!matchesReportingManager) return false;

      // ========================================
      // 12. EXTENSION STATUS FILTER - MULTI-SELECT + PROPER VALUE CHECKING
      // ========================================
      let matchesExtensionStatus = true;
if (
  Array.isArray(extensionStatusFilter) &&
  extensionStatusFilter.length > 0
) {
  const extensionRequested =
    candidate.extension_requested ?? candidate.extensionRequested ?? null;
  const reqValue = parseInt(extensionRequested);

  matchesExtensionStatus = extensionStatusFilter.some((filter) => {
    if (filter === "Extension Request - Yes") {
      return reqValue === 1;
    } else if (filter === "Extension Request - No") {
      return reqValue === 2;
    } else if (filter === "Pending") {
      return !reqValue || reqValue === 0 || isNaN(reqValue);
    }
    return false;
  });

  if (!matchesExtensionStatus) return false;
}

      // ========================================
      // 13. RATE RANGE FILTER
      // ========================================
      let matchesRateRange = true;
      if (rateRangeFilter !== "all") {
        const rate = parseFloat(candidate.clientRate || 0);
        switch (rateRangeFilter) {
          case "low":
            matchesRateRange = rate < 100;
            break;
          case "medium":
            matchesRateRange = rate >= 100 && rate < 200;
            break;
          case "high":
            matchesRateRange = rate >= 200;
            break;
          default:
            matchesRateRange = true;
        }

        if (!matchesRateRange) return false;
      }

      // ========================================
      // 14. MARGIN FILTER
      // ========================================
      let matchesMargin = true;
      if (marginFilter !== "all") {
        const margin = parseFloat(candidate.marginValue || 0);
        switch (marginFilter) {
          case "low":
            matchesMargin = margin < 20;
            break;
          case "medium":
            matchesMargin = margin >= 20 && margin < 35;
            break;
          case "high":
            matchesMargin = margin >= 35;
            break;
          default:
            matchesMargin = true;
        }

        if (!matchesMargin) return false;
      }

      // ========================================
      // 15. REPORTING STATUS FILTER
      // ========================================
      let matchesReportingStatus = true;
      if (reportingStatusFilter !== "all") {
        const hasReportingInfo =
          candidate.workEmail ||
          candidate.reportingManagerName ||
          candidate.reportingManagerEmail;

        matchesReportingStatus =
          reportingStatusFilter === "completed"
            ? hasReportingInfo
            : !hasReportingInfo;

        if (!matchesReportingStatus) return false;
      }

      // ========================================
      // 16. PROJECT STATUS FILTER
      // ========================================
      let matchesProjectStatus = true;
      if (projectStatusFilter !== "all") {
        const hasProjectInfo =
          candidate.current_technologies ||
          candidate.project_description ||
          candidate.project_experience_rating;

        matchesProjectStatus =
          projectStatusFilter === "completed"
            ? hasProjectInfo
            : !hasProjectInfo;

        if (!matchesProjectStatus) return false;
      }

      // ========================================
      // 17. END DATE RANGE FILTER
      // ========================================
      let matchesEndDateRange = true;
      if (endDateRangeFilter !== "all") {
        const rawEndDate = candidate.sowEndDate || candidate.endDate;
const [ey, em, ed] = String(rawEndDate).slice(0, 10).split('-').map(Number);
const endDate = new Date(ey, em - 1, ed);
const today = new Date();
today.setHours(0, 0, 0, 0);

switch (endDateRangeFilter) {
          case "ending_7": {
            const cutoff = new Date();
            cutoff.setDate(today.getDate() + 7);
            cutoff.setHours(23, 59, 59, 999);
            matchesEndDateRange = endDate >= today && endDate <= cutoff;
            break;
          }
          case "ending_30": {
            const cutoff = new Date();
            cutoff.setDate(today.getDate() + 30);
            cutoff.setHours(23, 59, 59, 999);
            matchesEndDateRange = endDate >= today && endDate <= cutoff;
            break;
          }
          case "ending_60": {
            const cutoff = new Date();
            cutoff.setDate(today.getDate() + 60);
            cutoff.setHours(23, 59, 59, 999);
            matchesEndDateRange = endDate >= today && endDate <= cutoff;
            break;
          }
          case "custom": {
            if (customEndDateStart && customEndDateEnd) {
              const startFilter = new Date(customEndDateStart);
              startFilter.setHours(0, 0, 0, 0);
              const endFilter = new Date(customEndDateEnd);
              endFilter.setHours(23, 59, 59, 999);
              matchesEndDateRange = endDate >= startFilter && endDate <= endFilter;
            }
            break;
          }
          default:
            matchesEndDateRange = true;
        }

        if (!matchesEndDateRange) return false;
      }

      // ========================================
      // 18. CERTIFICATION FILTER
      // ========================================
      if (Array.isArray(certificationFilter) && certificationFilter.length > 0) {
        const candidateCerts = Array.isArray(candidate.certifications)
          ? candidate.certifications
          : typeof candidate.certifications === "string"
          ? candidate.certifications.split(",").map((c) => c.trim())
          : [];
        const hasCert = certificationFilter.some((cert) =>
          candidateCerts.includes(cert)
        );
        if (!hasCert) return false;
      }

      // ✅ ALL FILTERS PASSED
      return true;
    });
  }, [
    candidates,
    searchTerm,
    searchScope,
    filterStatus,
    dateFilter,
    startDateFilter,
    endDateFilter,
    showRecentOnboarded,
    clientFilter,
    buHeadFilter,
    skillsFilter,
    locationFilter,
    contractTypeFilter,
    clientProjectManagerFilter,
    clientResourceManagerFilter,
    reportingManagerFilter,
    extensionStatusFilter,
    rateRangeFilter,
    marginFilter,
    reportingStatusFilter,
    projectStatusFilter,
    endDateRangeFilter,
    customEndDateStart,
    customEndDateEnd,
    certificationFilter,
  ]);

  // const uniqueBUHeads = [...new Set(candidates.map(c => c.buHead).filter(Boolean))];

  // Filter candidates based on BU and date range for dashboard metrics
  const getFilteredCandidatesForDashboard = () => {
    return candidates.filter((candidate) => {
      // BU filter
      const matchesBU = buFilter === "all" || candidate.buHead === buFilter;

      // Date filter
      let matchesDate = true;
      const candidateStartDate = new Date(candidate.startDate);
      const today = new Date();

      if (dashboardDateRange === "current_month") {
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        matchesDate =
          candidateStartDate >= monthStart && candidateStartDate <= monthEnd;
      } else if (dashboardDateRange === "last_month") {
        const lastMonthStart = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          1,
        );
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        matchesDate =
          candidateStartDate >= lastMonthStart &&
          candidateStartDate <= lastMonthEnd;
      } else if (dashboardDateRange === "current_quarter") {
        const quarterStart = new Date(
          today.getFullYear(),
          Math.floor(today.getMonth() / 3) * 3,
          1,
        );
        matchesDate =
          candidateStartDate >= quarterStart && candidateStartDate <= today;
      } else if (dashboardDateRange === "ytd") {
        const yearStart = new Date(today.getFullYear(), 0, 1);
        matchesDate =
          candidateStartDate >= yearStart && candidateStartDate <= today;
      } else if (
        dashboardDateRange === "custom" &&
        dashboardStartDate &&
        dashboardEndDate
      ) {
        const startFilter = new Date(dashboardStartDate);
        const endFilter = new Date(dashboardEndDate);
        matchesDate =
          candidateStartDate >= startFilter && candidateStartDate <= endFilter;
      }

      return matchesBU && matchesDate;
    });
  };

  const filteredDashboardCandidates = getFilteredCandidatesForDashboard();

  // FIXED: Enhanced data processing function with proper "Not Extended" logic
  const processRealInsightsData = () => {
    const filteredCandidates =
      buFilter === "all"
        ? candidates
        : candidates.filter((c) => c.buHead === buFilter);

    // ENHANCED DEBUG: Check ALL extension-related fields
    console.log(
      "?? DEBUG - Extension field analysis:",
      filteredCandidates.slice(0, 5).map((c) => ({
        name: c.candidateName,
        // Check ALL possible extension field variations
        extension_requested: c.extension_requested,
        extensionRequested: c.extensionRequested,
        extension_status: c.extension_status,
        extensionStatus: c.extensionStatus,
        extension_count: c.extension_count,
        extended_count: c.extended_count,
        extendedCount: c.extendedCount,
        no_extension_reason: c.no_extension_reason,
        noExtensionReason: c.noExtensionReason,
        client_rating: c.client_rating,
        clientRating: c.clientRating,
        sow_end_date: c.sow_end_date,
        sowEndDate: c.sowEndDate,
        end_date: c.end_date,
        endDate: c.endDate,
        status: c.status,
      })),
    );

    console.log("?? Processing candidates:", filteredCandidates.length);

    // Group candidates by client
    const clientGroups = {};
    filteredCandidates.forEach((candidate) => {
      const clientName = candidate.client || "Unknown Client";
      if (!clientGroups[clientName]) {
        clientGroups[clientName] = {
          client: clientName,
          bu: candidate.buHead || "Unknown BU",
          candidates: [],
          totalCandidates: 0,
          activeCandidates: 0,
          endingIn60Days: 0,
          extended: 0,
          notExtended: 0,
          extensionRequestedYes: 0,
          successRate: 0,
          reasons: {},
          extensionReasons: {},
          extendedCandidates: [],
          notExtendedCandidates: [],
        };
      }
      clientGroups[clientName].candidates.push(candidate);
      clientGroups[clientName].totalCandidates++;

      if (candidate.status === "Active") {
        clientGroups[clientName].activeCandidates++;
      }
    });

    // Process each client group
    const clientData = Object.values(clientGroups).map((group) => {
      // Process ALL candidates for this client
      group.candidates.forEach((candidate) => {
        // Check if candidate was sent to RD team (exclude from dashboard counts)
        const sentToRD =
          candidate.sent_to_rd ||
          candidate.sentToRD ||
          candidate.extension_requested === 2 ||
          candidate.extension_requested === "2";

        // Extension status field
        const extensionStatusField =
          candidate.extension_status || candidate.extensionStatus;
        const extensionRequestField =
          candidate.extension_requested || candidate.extensionRequested;

        // Count extension requests (Yes = 1 or true)
        if (
          extensionRequestField === 1 ||
          extensionRequestField === "1" ||
          extensionRequestField === true ||
          extensionRequestField === "true" ||
          extensionRequestField === "Yes" ||
          extensionRequestField === "yes"
        ) {
          group.extensionRequestedYes++;
          console.log(
            `? Extension requested YES for: ${candidate.candidateName}`,
          );
        }

        // Check for actual extensions using multiple field variations
        const extendedCountField =
          candidate.extended_count ||
          candidate.extendedCount ||
          candidate.extension_count;

        // Count as extended if has extended_count > 0 OR extension_status = 'Yes'
        let isExtended = false;
        let extensionCount = 0;

        if (extendedCountField && parseInt(extendedCountField) > 0) {
          isExtended = true;
          extensionCount = parseInt(extendedCountField);
          console.log(
            `? Extended via count for: ${candidate.candidateName} (count: ${extensionCount})`,
          );
        } else if (
          extensionStatusField === "Yes" ||
          extensionStatusField === "yes" ||
          extensionStatusField === "YES"
        ) {
          isExtended = true;
          extensionCount = 1;
          console.log(`? Extended via status for: ${candidate.candidateName}`);
        }

        if (isExtended) {
          group.extended += extensionCount;

          // Determine extension reason
          let extensionReason = "Standard Extension";
          if (candidate.extensionComments || candidate.extension_comments) {
            const comments =
              candidate.extensionComments || candidate.extension_comments;
            if (comments.toLowerCase().includes("client request")) {
              extensionReason = "Client Requested";
            } else if (comments.toLowerCase().includes("project expansion")) {
              extensionReason = "Project Expansion";
            } else if (comments.toLowerCase().includes("skill shortage")) {
              extensionReason = "Critical Skills";
            } else if (comments.toLowerCase().includes("performance")) {
              extensionReason = "High Performance";
            } else {
              extensionReason = "Business Need";
            }
          }

          // Track extension reasons
          group.extensionReasons[extensionReason] =
            (group.extensionReasons[extensionReason] || 0) + extensionCount;

          group.extendedCandidates.push({
            name: candidate.candidateName,
            client: candidate.client,
            bu: candidate.buHead,
            projectEndDate: candidate.endDate || candidate.end_date,
            rating: candidate.clientRating || candidate.client_rating || 0,
            status: candidate.status,
            isExtended: true,
            extensionReason: extensionReason,
            extensionCount: extensionCount,
          });
        }

        // FIXED: Enhanced "Not Extended" logic - exclude RD forwarded candidates
        // Count as "Not Extended" ONLY if:
        // 1. extension_status = 'No' AND
        // 2. NOT sent to RD team (to exclude manual decline cases)
        if (
          (extensionStatusField === "No" ||
            extensionStatusField === "no" ||
            extensionStatusField === "NO") &&
          !sentToRD &&
          !isExtended
        ) {
          // Don't double-count if somehow both flags are true

          group.notExtended++;

          // Get the reason (prefer bulk update reasons over manual decline reasons)
          const notExtendedReason = "Contract Ended"; // Since this is from bulk end date update

          // Track not-extended reasons
          group.reasons[notExtendedReason] =
            (group.reasons[notExtendedReason] || 0) + 1;

          group.notExtendedCandidates.push({
            name: candidate.candidateName,
            client: candidate.client,
            bu: candidate.buHead,
            projectEndDate: candidate.endDate || candidate.end_date,
            rating: candidate.clientRating || candidate.client_rating || 0,
            status: candidate.status,
            reason: notExtendedReason,
            isExtended: false,
          });

          console.log(
            `? Not extended (bulk update): ${candidate.candidateName} - Status: ${extensionStatusField}, Not sent to RD`,
          );
        } else if (sentToRD) {
          console.log(
            `?? Sent to RD (excluded from count): ${candidate.candidateName}`,
          );
        }
      });

      // Calculate ending in 60 days separately
      const endingCandidates = group.candidates.filter((c) => {
        if ((!c.sowEndDate && !c.sow_end_date) || c.status !== "Active")
          return false;
        const endDate = new Date(c.sowEndDate || c.sow_end_date);
        const today = new Date();
        const sixtyDaysFromNow = new Date();
        sixtyDaysFromNow.setDate(today.getDate() + 60);
        return endDate <= sixtyDaysFromNow && endDate >= today;
      });

      group.endingIn60Days = endingCandidates.length;

      // Calculate success rate
      const totalExtensionAttempts = group.extended + group.notExtended;
      group.successRate =
        totalExtensionAttempts > 0
          ? parseFloat(
              ((group.extended / totalExtensionAttempts) * 100).toFixed(1),
            )
          : 0;

      console.log(
        `?? Client ${group.client}: Extended: ${group.extended}, Not Extended: ${group.notExtended}, Success Rate: ${group.successRate}%`,
      );
      console.log(`?? Not Extended Reasons:`, group.reasons);

      return group;
    });

    // Calculate BU data from processed client data
    const getBUData = () => {
      const buGroups = {};

      // Initialize BU groups
      filteredCandidates.forEach((candidate) => {
        const buName = candidate.buHead || "Unknown BU";
        if (!buGroups[buName]) {
          buGroups[buName] = {
            bu: buName,
            clientCount: new Set(),
            totalCandidates: 0,
            activeCandidates: 0,
            totalExtended: 0,
            totalNotExtended: 0,
            successRate: 0,
            reasons: {},
          };
        }

        buGroups[buName].clientCount.add(candidate.client);
        buGroups[buName].totalCandidates++;

        if (candidate.status === "Active") {
          buGroups[buName].activeCandidates++;
        }
      });

      return Object.values(buGroups)
        .map((bu) => {
          // Get clients for this BU from processed client data
          const buClients = clientData.filter((client) => client.bu === bu.bu);

          bu.clientCount = bu.clientCount.size;
          bu.totalExtended = buClients.reduce(
            (sum, client) => sum + client.extended,
            0,
          );
          bu.totalNotExtended = buClients.reduce(
            (sum, client) => sum + client.notExtended,
            0,
          );

          // Aggregate reasons for this BU
          buClients.forEach((client) => {
            Object.entries(client.reasons).forEach(([reason, count]) => {
              bu.reasons[reason] = (bu.reasons[reason] || 0) + count;
            });
          });

          // Calculate BU success rate
          const totalEnding = bu.totalExtended + bu.totalNotExtended;
          bu.successRate =
            totalEnding > 0
              ? parseFloat(((bu.totalExtended / totalEnding) * 100).toFixed(1))
              : 0;

          return bu;
        })
        .sort((a, b) => b.successRate - a.successRate);
    };

    const buData = getBUData();

    // Calculate overall stats
    const getOverallStats = () => {
      const totalCandidates = filteredCandidates.length;
      const activeCandidates = filteredCandidates.filter(
        (c) => c.status === "Active",
      ).length;
      const inactiveCandidates = filteredCandidates.filter(
        (c) => c.status && c.status.toLowerCase() === "inactive",
      ).length;

      // Calculate totals from client data (more accurate)
      const totalExtended = clientData.reduce(
        (sum, client) => sum + client.extended,
        0,
      );
      const totalNotExtended = clientData.reduce(
        (sum, client) => sum + client.notExtended,
        0,
      );

      const endingIn60Days = clientData.reduce(
        (sum, client) => sum + client.endingIn60Days,
        0,
      );

      // Calculate extension requests (Yes only)
      const extensionRequestedYes = filteredCandidates.filter((c) => {
        const extensionRequestField =
          c.extension_requested || c.extensionRequested;
        return (
          extensionRequestField === 1 ||
          extensionRequestField === "1" ||
          extensionRequestField === true ||
          extensionRequestField === "true" ||
          extensionRequestField === "Yes" ||
          extensionRequestField === "yes"
        );
      }).length;

      // Aggregate all reasons
      const allReasons = {};
      clientData.forEach((client) => {
        Object.entries(client.reasons).forEach(([reason, count]) => {
          allReasons[reason] = (allReasons[reason] || 0) + count;
        });
      });

      // Aggregate all extension reasons
      const allExtensionReasons = {};
      clientData.forEach((client) => {
        if (client.extensionReasons) {
          Object.entries(client.extensionReasons).forEach(([reason, count]) => {
            allExtensionReasons[reason] =
              (allExtensionReasons[reason] || 0) + count;
          });
        }
      });

      const avgSuccessRate =
        clientData.length > 0
          ? (
              clientData.reduce((sum, client) => sum + client.successRate, 0) /
              clientData.length
            ).toFixed(1)
          : 0;

      console.log("?? FINAL Overall Stats:", {
        totalCandidates,
        activeCandidates,
        inactiveCandidates,
        endingIn60Days,
        totalExtended,
        totalNotExtended,
        extensionRequestedYes,
        allReasons,
        allExtensionReasons,
      });

      return {
        totalCandidates,
        activeCandidates,
        inactiveCandidates,
        endingIn60Days,
        totalExtended,
        totalNotExtended,
        extensionRequestedYes,
        avgSuccessRate,
        allReasons,
        allExtensionReasons,
      };
    };

    const result = {
      clientData,
      buData,
      overallStats: getOverallStats(),
    };

    console.log("?? FINAL processed data:", result);
    console.log("?? FINAL allReasons:", result.overallStats.allReasons);

    return result;
  };
  // Use real data
  const insightsData = useMemo(() => {
    return processRealInsightsData();
  }, [candidates, buFilter]); // Only recalculate when these change

  // ADD THESE DEBUG LOGS TO SEE WHAT DATA WE HAVE:
  console.log("?? DEBUG: Raw candidates data:", candidates);
  console.log("?? DEBUG: Filtered candidates:", filteredCandidates);
  console.log("?? DEBUG: Processed insights data:", insightsData);
  console.log("?? DEBUG: Client data:", insightsData.clientData);
  console.log("?? DEBUG: Overall stats:", insightsData.overallStats);
  console.log("?? DEBUG: All reasons:", insightsData.overallStats.allReasons);

  // Add this complete ReasonsPieChart component
  const ReasonsPieChart = ({
    data,
    title,
    onReasonClick,
    bu = null,
    clientData = [],
  }) => {
    const [hoveredReason, setHoveredReason] = useState(null);
    const [selectedReason, setSelectedReason] = useState(null);

    // Add loading check
    if (!data || typeof data !== "object") {
      return (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">{title}</h3>
          <div className="flex items-center justify-center h-64 text-gray-500">
            <div className="text-center">
              <RefreshCw className="h-8 w-8 mx-auto mb-4 animate-spin text-blue-500" />
              <p className="text-lg font-semibold">Loading data...</p>
            </div>
          </div>
        </div>
      );
    }

    // Create client-wise breakdown for each reason
    const getClientBreakdownForReason = (reason) => {
      const clientBreakdown = {};

      if (!clientData || !Array.isArray(clientData)) {
        return clientBreakdown;
      }

      clientData.forEach((client) => {
        const isExtensionChart =
          title &&
          title.includes &&
          title.includes("Extension") &&
          !title.includes("Non-Extension");
        const reasonData = isExtensionChart
          ? client.extensionReasons
          : client.reasons;

        if (reasonData && reasonData[reason]) {
          clientBreakdown[client.client] = reasonData[reason];
        }
      });

      return clientBreakdown;
    };

    const chartData = Object.entries(data || {}).map(([reason, count]) => ({
      name: reason,
      value: count,
      clientBreakdown: getClientBreakdownForReason(reason),
      color:
        {
          "Budget Constraints": "#E74C3C",
          "Project Completion": "#27AE60",
          Restructuring: "#3498DB",
          "Contract Terms": "#F39C12",
          "Performance Issues": "#9B59B6",
          "Client Requested": "#2ECC71",
          "Project Expansion": "#3498DB",
          "Critical Skills": "#E67E22",
          "High Performance": "#9B59B6",
          "Business Need": "#34495E",
        }[reason] || "#95A5A6",
    }));

    if (chartData.length === 0) {
      return (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">{title}</h3>
          <div className="flex items-center justify-center h-64 text-gray-500">
            <div className="text-center">
              <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-500" />
              <p className="text-lg font-semibold">Perfect Performance!</p>
              <p className="text-sm">All projects successfully extended</p>
            </div>
          </div>
        </div>
      );
    }

    // Component for client-wise wave graph
    const ClientWaveGraph = ({ clientBreakdown, reasonColor }) => {
      if (!clientBreakdown || Object.keys(clientBreakdown).length === 0) {
        return (
          <div className="text-center py-4 text-gray-500 text-sm">
            No client-specific data available
          </div>
        );
      }

      const clientData = Object.entries(clientBreakdown).map(
        ([client, count]) => ({
          client: client.length > 10 ? client.substring(0, 10) + "..." : client,
          fullClient: client,
          count: count,
        }),
      );

      const maxClientCount = Math.max(...clientData.map((item) => item.count));

      return (
        <div className="mt-4">
          <p className="text-xs font-semibold text-gray-600 mb-3 uppercase tracking-wide">
            Client Distribution:
          </p>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart
              data={clientData}
              margin={{ top: 10, right: 10, left: 10, bottom: 40 }}
            >
              <CartesianGrid strokeDasharray="2 2" stroke="#f0f0f0" />
              <XAxis
                dataKey="client"
                angle={-45}
                textAnchor="end"
                height={60}
                interval={0}
                fontSize={10}
                stroke="#6b7280"
              />
              <YAxis
                stroke="#6b7280"
                fontSize={10}
                domain={[0, maxClientCount + 1]}
                width={30}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload[0]) {
                    const data = payload[0].payload;
                    return (
                      <div className="bg-white p-3 border rounded-lg shadow-lg border-gray-200">
                        <p className="font-semibold text-gray-900">
                          {data.fullClient}
                        </p>
                        <p
                          className="text-sm font-bold"
                          style={{ color: reasonColor }}
                        >
                          {data.count} candidates
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke={reasonColor}
                strokeWidth={2}
                fill={`url(#gradient-${reasonColor.replace("#", "")})`}
                dot={{
                  fill: reasonColor,
                  strokeWidth: 1,
                  r: 4,
                  cursor: "pointer",
                }}
                activeDot={{
                  r: 6,
                  fill: "#FFD700",
                  stroke: reasonColor,
                  strokeWidth: 2,
                  cursor: "pointer",
                }}
                animationDuration={1000}
              />
              <defs>
                <linearGradient
                  id={`gradient-${reasonColor.replace("#", "")}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor={reasonColor} stopOpacity={0.3} />
                  <stop
                    offset="95%"
                    stopColor={reasonColor}
                    stopOpacity={0.05}
                  />
                </linearGradient>
              </defs>
            </LineChart>
          </ResponsiveContainer>
        </div>
      );
    };

    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">{title}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Pie Chart */}
          <div>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, percent }) =>
                    `${(percent * 100).toFixed(0)}%`
                  }
                  labelLine={false}
                  fontSize={10}
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color}
                      style={{ cursor: "pointer" }}
                      onClick={() => onReasonClick(entry.name, bu)}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Reason List with Client Wave Graphs */}
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-700">
              Click to view candidates:
            </h4>
            <div className="max-h-80 overflow-y-auto space-y-4">
              {chartData.map((reason, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <div
                    className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                    onClick={() => onReasonClick(reason.name, bu)}
                  >
                    <div className="flex items-center">
                      <div
                        className="w-4 h-4 rounded-full mr-3"
                        style={{ backgroundColor: reason.color }}
                      ></div>
                      <span className="text-sm font-medium text-gray-700">
                        {reason.name}
                      </span>
                    </div>
                    <span className="text-sm font-bold text-gray-900">
                      {reason.value} candidates
                    </span>
                  </div>

                  {/* Client Wave Graph */}
                  {Object.keys(reason.clientBreakdown).length > 0 && (
                    <div className="p-3 bg-white">
                      <ClientWaveGraph
                        clientBreakdown={reason.clientBreakdown}
                        reasonColor={reason.color}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ExtensionWaveChart = ({ data, title, type, onClientClick, color }) => {
    const [hoveredClient, setHoveredClient] = useState(null);

    if (!data || data.length === 0) {
      return (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">{title}</h3>
          <div className="flex items-center justify-center h-64 text-gray-500">
            <div className="text-center">
              <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-500" />
              <p className="text-lg font-semibold">No Data Available</p>
              <p className="text-sm">No extension data to display</p>
            </div>
          </div>
        </div>
      );
    }

    const maxValue = Math.max(...data.map((item) => item.count));
    const totalCandidates = data.reduce((sum, item) => sum + item.count, 0);

    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div
              className={`w-10 h-10 ${color === "green" ? "bg-gradient-to-r from-green-500 to-emerald-500" : "bg-gradient-to-r from-red-500 to-pink-500"} rounded-xl flex items-center justify-center`}
            >
              {type === "extended" ? (
                <CheckCircle className="w-5 h-5 text-white" />
              ) : (
                <XCircle className="w-5 h-5 text-white" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">{title}</h3>
              <p className="text-sm text-gray-600">
                Total: {totalCandidates} candidates
              </p>
            </div>
          </div>
          <div className="text-right">
            <div
              className={`text-2xl font-bold ${color === "green" ? "text-green-600" : "text-red-600"}`}
            >
              {totalCandidates}
            </div>
            <div className="text-xs text-gray-500">Candidates</div>
          </div>
        </div>

        {/* Wave Chart */}
        <div className="mb-6">
          <ResponsiveContainer width="100%" height={250}>
            <LineChart
              data={data}
              margin={{ top: 10, right: 10, left: 10, bottom: 40 }}
            >
              <CartesianGrid strokeDasharray="2 2" stroke="#f0f0f0" />
              <XAxis
                dataKey="client"
                angle={-45}
                textAnchor="end"
                height={80}
                interval={0}
                fontSize={10}
                stroke="#6b7280"
              />
              <YAxis
                stroke="#6b7280"
                fontSize={10}
                domain={[0, maxValue + 2]}
                width={40}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload && payload[0]) {
                    const data = payload[0].payload;
                    return (
                      <div className="bg-white p-4 border rounded-lg shadow-lg border-gray-200">
                        <p className="font-semibold text-gray-900">{label}</p>
                        <p
                          className={`text-sm font-bold ${color === "green" ? "text-green-600" : "text-red-600"}`}
                        >
                          {data.count} candidates {type}
                        </p>
                        <p className="text-xs text-gray-500">BU: {data.bu}</p>
                        {data.successRate && (
                          <p className="text-xs text-blue-600">
                            Success Rate: {data.successRate}%
                          </p>
                        )}
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke={color === "green" ? "#10B981" : "#EF4444"}
                strokeWidth={3}
                fill={`url(#gradient-${color})`}
                dot={{
                  fill: color === "green" ? "#10B981" : "#EF4444",
                  strokeWidth: 2,
                  r: 5,
                  cursor: "pointer",
                }}
                activeDot={{
                  r: 8,
                  fill: "#FFD700",
                  stroke: color === "green" ? "#10B981" : "#EF4444",
                  strokeWidth: 3,
                  cursor: "pointer",
                }}
                animationDuration={1500}
                animationEasing="ease-out"
              />
              <defs>
                <linearGradient
                  id={`gradient-${color}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={color === "green" ? "#10B981" : "#EF4444"}
                    stopOpacity={0.3}
                  />
                  <stop
                    offset="95%"
                    stopColor={color === "green" ? "#10B981" : "#EF4444"}
                    stopOpacity={0.05}
                  />
                </linearGradient>
              </defs>
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Client List */}
        <div className="space-y-2">
          <h4 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">
            Client Breakdown:
          </h4>
          <div className="max-h-40 overflow-y-auto space-y-2">
            {data.map((client, index) => (
              <div
                key={index}
                className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                  hoveredClient === client.client
                    ? color === "green"
                      ? "bg-green-50 border border-green-200"
                      : "bg-red-50 border border-red-200"
                    : "bg-gray-50 hover:bg-gray-100 border border-gray-200"
                }`}
                onMouseEnter={() => setHoveredClient(client.client)}
                onMouseLeave={() => setHoveredClient(null)}
                onClick={() => onClientClick && onClientClick(client)}
              >
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-3 h-3 rounded-full ${color === "green" ? "bg-green-500" : "bg-red-500"}`}
                  ></div>
                  <div>
                    <span className="text-sm font-medium text-gray-900">
                      {client.client.length > 15
                        ? client.client.substring(0, 15) + "..."
                        : client.client}
                    </span>
                    <div className="text-xs text-gray-500">BU: {client.bu}</div>
                  </div>
                </div>
                <div className="text-right">
                  <span
                    className={`text-sm font-bold ${color === "green" ? "text-green-600" : "text-red-600"}`}
                  >
                    {client.count}
                  </span>
                  {client.successRate && (
                    <div className="text-xs text-blue-600">
                      {client.successRate}%
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Data processing function for extension charts
  const processExtensionData = (insightsData, buFilter) => {
    const filteredClients =
      buFilter === "all"
        ? insightsData.clientData
        : insightsData.clientData.filter((client) => client.bu === buFilter);

    // Extended candidates data
    const extendedData = filteredClients
      .filter((client) => client.extended > 0)
      .map((client) => ({
        client: client.client,
        bu: client.bu,
        count: client.extended,
        successRate: client.successRate,
      }))
      .sort((a, b) => b.count - a.count);

    // Not extended candidates data
    const notExtendedData = filteredClients
      .filter((client) => client.notExtended > 0)
      .map((client) => ({
        client: client.client,
        bu: client.bu,
        count: client.notExtended,
        successRate: client.successRate,
      }))
      .sort((a, b) => b.count - a.count);

    return { extendedData, notExtendedData };
  };

  // Main component to replace the pie charts
  const ExtensionAnalysisCharts = ({
    insightsData,
    buFilter,
    onClientClick,
  }) => {
    const { extendedData, notExtendedData } = processExtensionData(
      insightsData,
      buFilter,
    );

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Extended Candidates Wave Chart */}
        <ExtensionWaveChart
          data={extendedData}
          title={`Extended Candidates ${buFilter !== "all" ? `(${buFilter})` : "(All BUs)"}`}
          type="extended"
          color="green"
          onClientClick={(client) =>
            onClientClick &&
            onClientClick("ALL_EXTENDED", client.bu, client.client)
          }
        />

        {/* Not Extended Candidates Wave Chart */}
        <ExtensionWaveChart
          data={notExtendedData}
          title={`Not Extended Candidates ${buFilter !== "all" ? `(${buFilter})` : "(All BUs)"}`}
          type="not_extended"
          color="red"
          onClientClick={(client) =>
            onClientClick &&
            onClientClick("ALL_NOT_EXTENDED", client.bu, client.client)
          }
        />
      </div>
    );
  };

  // Candidates Modal Component - MOVED HERE
  const CandidatesModal = ({
    reason,
    bu,
    onClose,
    clientData,
    mainCandidates,
  }) => {
    if (!reason) return null;

    let candidates = [];

    if (reason === "ALL_EXTENDED") {
      // Show candidates who have been extended (using extended_count field)
      const filteredCandidates =
        bu && bu !== "all"
          ? mainCandidates.filter((c) => c.buHead === bu)
          : mainCandidates;

      // Get candidates with actual extensions (extended_count > 0)
      const extendedCandidates = filteredCandidates.filter((c) => {
        if (c.extended_count != null && c.extended_count !== "") {
          const extCount = parseInt(c.extended_count);
          return !isNaN(extCount) && extCount > 0;
        }
        return false;
      });

      // Convert to display format with extension count information
      candidates = extendedCandidates.map((c) => ({
        name: c.candidateName,
        client: c.client,
        bu: c.buHead,
        projectEndDate: c.sowEndDate || c.endDate,
        rating: c.clientRating || 0,
        status: c.status,
        isExtended: true,
        extensionReason: c.extensionComments || "Extension approved",
        extensionCount: parseInt(c.extended_count) || 1, // Show actual count
        totalExtensions: parseInt(c.extended_count) || 1, // Track total extensions
      }));
    } else if (reason === "ALL_NOT_EXTENDED") {
      // Show candidates who were not extended (extension_status = 'No')
      const filteredCandidates =
        bu && bu !== "all"
          ? mainCandidates.filter((c) => c.buHead === bu)
          : mainCandidates;

      const notExtendedCandidates = filteredCandidates.filter(
        (c) => c.extensionStatus === "No" || c.extension_status === "No",
      );

      candidates = notExtendedCandidates.map((c) => ({
        name: c.candidateName,
        client: c.client,
        bu: c.buHead,
        projectEndDate: c.sowEndDate || c.endDate,
        rating: c.clientRating || 0,
        status: c.status,
        isExtended: false,
        reason: c.noExtensionReason || c.no_extension_reason || "Not Extended",
      }));
    } else if (reason === "EXTENSION_REQUESTED_YES") {
      // Show candidates who requested extension (extension_requested = 1)
      const filteredCandidates =
        bu && bu !== "all"
          ? mainCandidates.filter((c) => c.buHead === bu)
          : mainCandidates;

      const extensionRequestedYesCandidates = filteredCandidates.filter(
        (c) =>
          c.extension_requested === 1 ||
          c.extension_requested === "1" ||
          c.extension_requested === true ||
          c.extension_requested === "true",
      );

      candidates = extensionRequestedYesCandidates.map((c) => ({
        name: c.candidateName,
        client: c.client,
        bu: c.buHead,
        projectEndDate: c.endDate || c.sowEndDate,
        rating: c.clientRating || 0,
        status: c.status,
        isExtended: c.extensionStatus === "Yes" || c.extension_status === "Yes",
        reason: "Extension Requested - Yes",
      }));
    } else if (reason === "ALL_ACTIVE") {
      // Get active candidates
      const activeCandidates = mainCandidates.filter(
        (c) => c.status === "Active",
      );

      const filteredCandidates =
        bu && bu !== "all"
          ? activeCandidates.filter((c) => c.buHead === bu)
          : activeCandidates;

      candidates = filteredCandidates.map((c) => ({
        name: c.candidateName,
        client: c.client,
        bu: c.buHead,
        projectEndDate: c.endDate || c.sowEndDate,
        rating: c.clientRating || 0,
        status: c.status,
        isExtended: false,
        reason: "Active",
      }));
    } else if (reason === "ALL_INACTIVE") {
      // Get inactive candidates
      const inactiveCandidates = mainCandidates.filter(
        (c) => c.status && c.status.toLowerCase() === "inactive",
      );

      const filteredCandidates =
        bu && bu !== "all"
          ? inactiveCandidates.filter((c) => c.buHead === bu)
          : inactiveCandidates;

      candidates = filteredCandidates.map((c) => ({
        name: c.candidateName,
        client: c.client,
        bu: c.buHead,
        projectEndDate: c.endDate || c.sowEndDate,
        rating: c.clientRating || 0,
        status: c.status,
        isExtended: false,
        reason: "Inactive",
      }));
    } else {
      // Show candidates for specific non-extension reason
      if (bu && bu !== "all") {
        const buClients = clientData.filter((item) => item.bu === bu);
        buClients.forEach((client) => {
          candidates.push(
            ...client.notExtendedCandidates.filter((c) => c.reason === reason),
          );
        });
      } else {
        clientData.forEach((client) => {
          candidates.push(
            ...client.notExtendedCandidates.filter((c) => c.reason === reason),
          );
        });
      }
    }

    const getModalTitle = () => {
      if (reason === "ALL_EXTENDED") return "Extended Candidates";
      if (reason === "ALL_NOT_EXTENDED") return "Not Extended Candidates";
      if (reason === "ALL_ACTIVE") return "Active Candidates";
      if (reason === "ALL_INACTIVE") return "Inactive Candidates";
      if (reason === "EXTENSION_REQUESTED_YES")
        return "Extension Requested - YES";
      return `Not Extended - ${reason}`;
    };

    // Calculate summary for extended candidates
    const getExtensionSummary = () => {
      if (reason !== "ALL_EXTENDED") return null;

      const totalExtensions = candidates.reduce(
        (sum, c) => sum + (c.extensionCount || 1),
        0,
      );
      const uniqueCandidates = candidates.length;
      const multipleExtensions = candidates.filter(
        (c) => (c.extensionCount || 1) > 1,
      );

      return {
        totalExtensions,
        uniqueCandidates,
        multipleExtensions: multipleExtensions.length,
      };
    };

    const extensionSummary = getExtensionSummary();

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
          <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">{getModalTitle()}</h2>
              {extensionSummary && (
                <p className="text-blue-100 text-sm">
                  {extensionSummary.totalExtensions} total extensions across{" "}
                  {extensionSummary.uniqueCandidates} candidates
                  {extensionSummary.multipleExtensions > 0 &&
                    ` (${extensionSummary.multipleExtensions} with multiple extensions)`}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6">
            <div className="mb-4">
              <p className="text-gray-600">
                {bu && bu !== "all" ? `${bu} - ` : "All BUs - "}
                {candidates.length} candidate(s)
                {reason === "ALL_EXTENDED" && extensionSummary
                  ? ` with ${extensionSummary.totalExtensions} total extensions`
                  : reason !== "ALL_EXTENDED" && reason !== "ALL_NOT_EXTENDED"
                    ? ` ${reason.toLowerCase()}`
                    : ""}
              </p>
            </div>

            {candidates.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-500" />
                <p className="text-lg font-semibold text-gray-900">
                  No candidates found
                </p>
                <p className="text-gray-600">
                  No candidates match the selected criteria
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Candidate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Client
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        BU
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Project End Date
                      </th>
                      {reason === "ALL_EXTENDED" && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Extensions
                        </th>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Rating
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {candidates.map((candidate, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {candidate.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {candidate.client}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            {candidate.bu}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {candidate.projectEndDate
                            ? new Date(
                                candidate.projectEndDate,
                              ).toLocaleDateString("en-US", {
                                month: "2-digit",
                                day: "2-digit",
                                year: "numeric",
                              })
                            : "Not set"}
                        </td>
                        {reason === "ALL_EXTENDED" && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <span className="inline-flex px-3 py-1 text-sm font-bold bg-green-100 text-green-800 rounded-full">
                                {candidate.extensionCount || 1}x
                              </span>
                              {(candidate.extensionCount || 1) > 1 && (
                                <span className="text-xs text-purple-600 font-medium">
                                  Multiple
                                </span>
                              )}
                            </div>
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Star className="h-4 w-4 text-yellow-400 mr-1" />
                            <span className="text-sm text-gray-900">
                              {candidate.rating}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              candidate.isExtended
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {candidate.isExtended
                              ? candidate.extensionReason || "Extended"
                              : candidate.reason || "Not Extended"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // BU Performance Analytics
  const getBUPerformanceData = () => {
    if (!candidates || candidates.length === 0) {
      return [];
    }

    return uniqueBUHeads
      .map((buHead) => {
        const buCandidates = filteredDashboardCandidates.filter(
          (c) => c.buHead === buHead,
        );
        const rollingOff = buCandidates.filter((c) => {
          const endDate = new Date(c.endDate);
          const today = new Date();
          const sixtyDaysFromNow = new Date();
          sixtyDaysFromNow.setDate(today.getDate() + 60);
          return (
            endDate <= sixtyDaysFromNow &&
            endDate >= today &&
            c.status === "Active"
          );
        });

        const extendedTotal = buCandidates.reduce((sum, c) => {
  if (c.extended_count != null && c.extended_count !== "") {
    const extCount = parseInt(c.extended_count);
    if (!isNaN(extCount)) {
      return sum + extCount;
    }
  }
  return sum;
}, 0);
const successRate =
  rollingOff.length > 0
    ? ((extendedTotal / rollingOff.length) * 100).toFixed(1)
    : 100;

        return {
          buHead,
          totalCandidates: buCandidates.length,
          activeCandidates: buCandidates.filter((c) => c.status === "Active")
            .length,
          rollingOff: rollingOff.length,
          extended: extendedTotal, // Change this line
          successRate: parseFloat(successRate),
          avgMargin:
            buCandidates.length > 0
              ? (
                  buCandidates.reduce(
                    (sum, c) => sum + parseFloat(c.marginValue || 0),
                    0,
                  ) / buCandidates.length
                ).toFixed(1)
              : 0,
        };
      })
      .sort((a, b) => b.successRate - a.successRate);
  };

  // Client Performance Analytics
  const getClientPerformanceData = () => {
    if (!candidates || candidates.length === 0) {
      return [];
    }

    const clientGroups = {};
    filteredDashboardCandidates.forEach((candidate) => {
      if (!clientGroups[candidate.client]) {
        clientGroups[candidate.client] = {
          client: candidate.client,
          buHead: candidate.buHead,
          candidates: [],
          totalCandidates: 0,
          activeCandidates: 0,
          rollingOff: 0,
          extended: 0,
          successRate: 0,
        };
      }
      clientGroups[candidate.client].candidates.push(candidate);
      clientGroups[candidate.client].totalCandidates++;
      if (candidate.status === "Active")
        clientGroups[candidate.client].activeCandidates++;
    });

    return Object.values(clientGroups)
      .map((group) => {
        const rollingOff = group.candidates.filter((c) => {
          const endDate = new Date(c.endDate);
          const today = new Date();
          const sixtyDaysFromNow = new Date();
          sixtyDaysFromNow.setDate(today.getDate() + 60);
          return (
            endDate <= sixtyDaysFromNow &&
            endDate >= today &&
            c.status === "Active"
          );
        });

        const extended = group.candidates.filter(
          (c) => c.extension_status === "Yes",
        );
        group.rollingOff = rollingOff.length;
        group.extended = extended.length;
        group.successRate =
          rollingOff.length > 0
            ? parseFloat(
                ((extended.length / rollingOff.length) * 100).toFixed(1),
              )
            : 100;

        return group;
      })
      .sort((a, b) => b.successRate - a.successRate);
  };

  // Extension Reasons Analytics
  const getExtensionReasonsData = () => {
    if (!candidates || candidates.length === 0) {
      return {
        totalExtended: 0,
        totalNotExtended: 0,
        reasons: {},
        extendedCandidates: [],
        notExtendedCandidates: [],
      };
    }

    const extensionData = {
      totalExtended: 0,
      totalNotExtended: 0,
      reasons: {},
      extendedCandidates: [],
      notExtendedCandidates: [],
    };

    filteredDashboardCandidates.forEach((candidate) => {
      const endDate = new Date(candidate.endDate);
      const today = new Date();
      const sixtyDaysFromNow = new Date();
      sixtyDaysFromNow.setDate(today.getDate() + 60);

      if (
        endDate <= sixtyDaysFromNow &&
        endDate >= today &&
        candidate.status === "Active"
      ) {
        if (
          candidate.extended_count != null &&
          candidate.extended_count !== ""
        ) {
          const extCount = parseInt(candidate.extended_count);
          if (!isNaN(extCount)) {
            extensionData.totalExtended += extCount;
            extensionData.extendedCandidates.push(candidate);
          }
        } else if (candidate.extensionStatus === "No") {
          extensionData.totalNotExtended++;
          extensionData.notExtendedCandidates.push(candidate);

          // ? ONLY use actual reason from database - NO MOCK REASONS
          const reason = candidate.noExtensionReason || "Not Extended";
          extensionData.reasons[reason] =
            (extensionData.reasons[reason] || 0) + 1;
        }
      }
    });

    return extensionData;
  };

  // Risk Analytics
  const getRiskAnalytics = () => {
    if (!candidates || candidates.length === 0) {
      return {
        highRisk: [],
        mediumRisk: [],
        lowRisk: [],
        totalHighRisk: 0,
        totalMediumRisk: 0,
        totalLowRisk: 0,
      };
    }

    const riskCandidates = filteredDashboardCandidates.map((candidate) => {
      let riskScore = 0;
      const endDate = new Date(candidate.endDate);
      const today = new Date();
      const daysToEnd = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));

      // Risk factors based on REAL data only
      if (daysToEnd <= 30) riskScore += 40;
      if (!candidate.extensionRequested && daysToEnd <= 60) riskScore += 30;
      if (!candidate.reportingManagerName) riskScore += 20;
      if (candidate.clientRating && parseFloat(candidate.clientRating) < 3.5)
        riskScore += 30;
      if (!candidate.sowEndDate && daysToEnd <= 45) riskScore += 25;

      return {
        ...candidate,
        riskScore: Math.min(riskScore, 100),
        riskLevel:
          riskScore >= 70 ? "High" : riskScore >= 40 ? "Medium" : "Low",
      };
    });

    return {
      highRisk: riskCandidates.filter((c) => c.riskLevel === "High"),
      mediumRisk: riskCandidates.filter((c) => c.riskLevel === "Medium"),
      lowRisk: riskCandidates.filter((c) => c.riskLevel === "Low"),
      totalHighRisk: riskCandidates.filter((c) => c.riskLevel === "High")
        .length,
      totalMediumRisk: riskCandidates.filter((c) => c.riskLevel === "Medium")
        .length,
      totalLowRisk: riskCandidates.filter((c) => c.riskLevel === "Low").length,
    };
  };

  // Financial Analytics
  const getFinancialAnalytics = () => {
    const monthlyRevenue = filteredDashboardCandidates.reduce(
      (sum, c) => sum + parseFloat(c.clientRate || 0) * 40 * 4,
      0,
    );
    const monthlyCost = filteredDashboardCandidates.reduce(
      (sum, c) => sum + parseFloat(c.payRate || 0) * 40 * 4,
      0,
    );
    const averageMargin =
      filteredDashboardCandidates.length > 0
        ? filteredDashboardCandidates.reduce(
            (sum, c) => sum + parseFloat(c.marginValue || 0),
            0,
          ) / filteredDashboardCandidates.length
        : 0;

    const atRiskRevenue = filteredDashboardCandidates
      .filter((c) => {
        const endDate = new Date(c.endDate);
        const today = new Date();
        const sixtyDaysFromNow = new Date();
        sixtyDaysFromNow.setDate(today.getDate() + 60);
        return (
          endDate <= sixtyDaysFromNow &&
          endDate >= today &&
          c.status === "Active"
        );
      })
      .reduce((sum, c) => sum + parseFloat(c.clientRate || 0) * 40 * 4, 0);

    return {
      monthlyRevenue,
      monthlyCost,
      monthlyProfit: monthlyRevenue - monthlyCost,
      averageMargin,
      atRiskRevenue,
      projectedAnnualRevenue: monthlyRevenue * 12,
    };
  };

  const buPerformanceData = getBUPerformanceData();
  const clientPerformanceData = getClientPerformanceData();
  const extensionReasonsData = getExtensionReasonsData();
  const riskAnalytics = getRiskAnalytics();
  const financialAnalytics = getFinancialAnalytics();

  // Recalculate metrics based on filtered data
  const dashboardMetrics = {
    totalCandidates: filteredDashboardCandidates.length,
    activeCandidates: filteredDashboardCandidates.filter(
      (c) => c.status === "Active",
    ).length,
    inactiveCandidates: filteredDashboardCandidates.filter(
      (c) => c.status === "Inactive",
    ).length,

    rollingOffNext60Days: filteredDashboardCandidates.filter((c) => {
      const endDate = new Date(c.endDate);
      const today = new Date();
      const sixtyDaysFromNow = new Date();
      sixtyDaysFromNow.setDate(today.getDate() + 60);
      return (
        endDate <= sixtyDaysFromNow && endDate >= today && c.status === "Active"
      );
    }).length,
    extended: filteredDashboardCandidates.filter(
      (c) => c.extension_status === "Yes",
    ).length,
    recentlyOnboarded: filteredDashboardCandidates.filter((c) => {
      const startDate = new Date(c.startDate);
      const today = new Date();
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(today.getDate() - 7);
      return startDate >= oneWeekAgo && startDate <= today;
    }).length,
    reportingInfoCollected: filteredDashboardCandidates.filter(
      (c) => c.reportingManagerName && c.reportingManagerEmail,
    ).length,
    projectInfoReceived: filteredDashboardCandidates.filter(
      (c) =>
        c.project_info_status === "completed" ||
        (c.currentTechnologies && c.projectDescription),
    ).length,
    accountManagersReached: filteredDashboardCandidates.filter(
      (c) =>
        c.accountManager1 ||
        c.accountManager2 ||
        c.accountManager3 ||
        c.accountManager4 ||
        c.accountManager5 ||
        c.accountManager6 ||
        c.accountManager7 ||
        c.accountManager8,
    ).length,
  };

  const Toast = ({ message, type = "success", onClose, duration = 4000 }) => {
    const [visible, setVisible] = useState(false);
    const [closing, setClosing] = useState(false);

    useEffect(() => {
      const showTimer = setTimeout(() => setVisible(true), 50);

      const hideTimer = setTimeout(() => {
        setClosing(true);
        setTimeout(onClose, 300);
      }, duration);

      return () => {
        clearTimeout(showTimer);
        clearTimeout(hideTimer);
      };
    }, [duration, onClose]);

    const handleClose = () => {
      setClosing(true);
      setTimeout(onClose, 300);
    };

    const getToastStyles = () => {
      const baseStyles =
        "relative w-full min-w-80 max-w-md p-4 rounded-lg shadow-2xl border backdrop-blur-sm transition-all duration-300 transform"; // Added shadow-2xl

      const typeStyles = {
        success: "bg-green-50/95 border-green-200 text-green-800",
        error: "bg-red-50/95 border-red-200 text-red-800",
        warning: "bg-yellow-50/95 border-yellow-200 text-yellow-800",
        info: "bg-blue-50/95 border-blue-200 text-blue-800",
      };

      const animationClass =
        visible && !closing
          ? "translate-x-0 opacity-100 scale-100"
          : "translate-x-full opacity-0 scale-95";

      return `${baseStyles} ${typeStyles[type]} ${animationClass}`;
    };

    const getIcon = () => {
      const iconProps = { className: "w-5 h-5 flex-shrink-0" };

      switch (type) {
        case "success":
          return (
            <CheckCircle
              {...iconProps}
              className="w-5 h-5 flex-shrink-0 text-green-600"
            />
          );
        case "error":
          return (
            <AlertCircle
              {...iconProps}
              className="w-5 h-5 flex-shrink-0 text-red-600"
            />
          );
        case "warning":
          return (
            <AlertTriangle
              {...iconProps}
              className="w-5 h-5 flex-shrink-0 text-yellow-600"
            />
          );
        case "info":
          return (
            <Info
              {...iconProps}
              className="w-5 h-5 flex-shrink-0 text-blue-600"
            />
          );
        default:
          return (
            <CheckCircle
              {...iconProps}
              className="w-5 h-5 flex-shrink-0 text-green-600"
            />
          );
      }
    };

    return (
      <div className={getToastStyles()}>
        <div className="flex items-start space-x-3">
          {getIcon()}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium leading-relaxed whitespace-pre-line">
              {message}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="flex-shrink-0 p-1 rounded-full hover:bg-black/10 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  // Toast Hook
  const useToast = () => {
    const [toasts, setToasts] = useState([]);

    const showToast = (message, type = "success", duration = 4000) => {
      const id = Date.now() + Math.random();
      const toast = { id, message, type, duration };

      console.log("?? Adding toast to state:", toast); // DEBUG LOG
      setToasts((prev) => {
        const newToasts = [...prev, toast];
        console.log("?? New toasts state:", newToasts); // DEBUG LOG
        return newToasts;
      });
    };

    const removeToast = (id) => {
      console.log("??? Removing toast:", id); // DEBUG LOG
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    };

    const success = (message, duration) => {
      console.log("? Success toast called with message:", message); // DEBUG LOG
      showToast(message, "success", duration);
    };
    const error = (message, duration) => showToast(message, "error", duration);
    const warning = (message, duration) =>
      showToast(message, "warning", duration);
    const info = (message, duration) => showToast(message, "info", duration);

    // DEBUG: Log current toasts state
    useEffect(() => {
      console.log("?? Current toasts in state:", toasts);
    }, [toasts]);

    return {
      toasts,
      showToast,
      removeToast,
      success,
      error,
      warning,
      info,
    };
  };

  // Loading Button Component
  // Loading Button Component
  const LoadingButton = ({
    loading,
    loadingText,
    children,
    className = "",
    variant = "primary",
    icon: Icon,
    ...props
  }) => {
    const getVariantStyles = () => {
      switch (variant) {
        case "primary":
          return "bg-slate-900 text-white hover:bg-slate-800 disabled:bg-slate-400";
        case "danger":
          return "bg-red-600 text-white hover:bg-red-700 disabled:bg-red-400";
        case "secondary":
          return "bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:bg-slate-100";
        case "success":
          return "bg-green-600 text-white hover:bg-green-700 disabled:bg-green-400";
        default:
          return "bg-slate-900 text-white hover:bg-slate-800 disabled:bg-slate-400";
      }
    };

    return (
      <button
        {...props}
        disabled={loading || props.disabled}
        className={`
        relative flex items-center justify-center px-4 py-2 rounded-lg font-medium text-sm
        transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed
        ${getVariantStyles()} ${className}
      `}
      >
        {loading ? (
          <>
            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
            {loadingText || "Loading..."}
          </>
        ) : (
          <>
            {Icon && <Icon className="w-4 h-4 mr-2" />}
            {children}
          </>
        )}
      </button>
    );
  };

  // Table Loading Component
  const TableLoading = ({ columns = 6, rows = 5 }) => {
    return (
      <tbody className="bg-white divide-y divide-slate-200">
        {[...Array(rows)].map((_, rowIndex) => (
          <tr key={rowIndex} className="animate-pulse">
            {[...Array(columns)].map((_, colIndex) => (
              <td key={colIndex} className="px-6 py-4">
                <div className="flex items-center space-x-3">
                  {colIndex === 0 && (
                    <div className="w-10 h-10 bg-slate-200 rounded-lg"></div>
                  )}
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                    <div className="h-3 bg-slate-100 rounded w-1/2"></div>
                  </div>
                </div>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    );
  };

  // Loading Overlay Component
  const LoadingOverlay = ({
    show,
    message = "Loading...",
    type = "default",
  }) => {
    if (!show) return null;

    const getSpinner = () => {
      switch (type) {
        case "save":
          return <RefreshCw className="w-5 h-5 animate-spin text-blue-600" />;
        case "delete":
          return <RefreshCw className="w-5 h-5 animate-spin text-red-600" />;
        case "email":
          return <RefreshCw className="w-5 h-5 animate-spin text-green-600" />;
        default:
          return <RefreshCw className="w-5 h-5 animate-spin text-slate-600" />;
      }
    };

    return (
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4">
          <div className="flex items-center space-x-3">
            {getSpinner()}
            <div>
              <p className="text-sm font-medium text-slate-900">{message}</p>
              <div className="flex space-x-1 mt-2">
                <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce"></div>
                <div
                  className="w-2 h-2 bg-slate-300 rounded-full animate-bounce"
                  style={{ animationDelay: "0.1s" }}
                ></div>
                <div
                  className="w-2 h-2 bg-slate-300 rounded-full animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Initialize toast hook
  const { toasts, removeToast, success, error, warning, info } = useToast();

  // ProjectFormActions component - defined inside main component to access handlers
  const ProjectFormActions = ({ candidate }) => {
    const [isOpen, setIsOpen] = useState(false);

    const getAvailableActions = () => {
      const actions = [];

      // Handle both snake_case and camelCase field names
      const formSentAt =
        candidate.project_form_sent_at || candidate.projectFormSentAt;
      const formCompletedAt =
        candidate.project_form_completed_at || candidate.projectFormCompletedAt;

      console.log("?? Actions Debug:", {
        candidateName: candidate.candidateName,
        formSentAt: formSentAt,
        formCompletedAt: formCompletedAt,
        hasFormSent: !!formSentAt,
        hasFormCompleted: !!formCompletedAt,
      });

      if (!formSentAt) {
        actions.push({
          label: "?? Send Form",
          action: () => handleForwardFormToCandidate(candidate),
          className: "text-blue-700 hover:bg-blue-50",
        });
      } else if (!formCompletedAt) {
        actions.push({
          label: "?? Send Reminder",
          action: () => handleSendFormReminder(candidate),
          className: "text-yellow-700 hover:bg-yellow-50",
        });
        actions.push({
          label: "? Mark Complete",
          action: () => handleMarkFormCompleted(candidate),
          className: "text-green-700 hover:bg-green-50",
        });
      }

      return actions;
    };

    const actions = getAvailableActions();

    if (actions.length === 0) {
      return (
        <span className="text-xs text-green-600 font-medium">? Completed</span>
      );
    }

    if (actions.length === 1) {
      return (
        <button
          onClick={actions[0].action}
          className={`inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded ${actions[0].className}`}
        >
          {actions[0].label}
        </button>
      );
    }

    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
        >
          Actions
          <ChevronDown className="ml-1 h-3 w-3" />
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-1 w-36 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
            <div className="py-1">
              {actions.map((action, index) => (
                <button
                  key={index}
                  onClick={() => {
                    action.action();
                    setIsOpen(false);
                  }}
                  className={`block w-full text-left px-3 py-2 text-xs ${action.className}`}
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

      const fetchRDCandidates = useCallback(async () => {

    try {
      setDataLoading(true);
      
      // const payload = {
      //   user_id: rdId || "",
      //   section:"section",

      // };
         const formData = new FormData();
           formData.append("section", "bench");
      const response = await fetch(`${RD_API_BASE_URL}api_get_candidate?section=bench`, {
        method: "GET",
        //     headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
        // body:formData,
      });


      if (!response.ok) {
        console.error(`Fetch RD candidates failed: HTTP ${response.status}`);
        setRdCandidates([]);
        return;
      }

      let result = null;
      try {
        result = await response.json();
      } catch {
        setRdCandidates([]);
        return;
      }

      if (result?.status === "success" || result?.data) {
        const list = normalizeRdCandidateList(result?.data ?? result);
        const mapped = list.map((item) => {
          const workAuthIdRaw =
            item.work_authorization_id ||
            item.work_authorization ||
            item.workAuth ||
            "";
          const workAuthId = workAuthIdRaw ? String(workAuthIdRaw).trim() : "";
          const workAuthName = resolveWorkAuthName(workAuthId);
          const rawBenchFlag = String(
            item.is_candidate_on_bench ?? item.isCandidateOnBench ?? "0",
          )
            .trim()
            .toLowerCase();
          const isOnBench = rawBenchFlag === "1" || rawBenchFlag === "true";
          const resolvedCandidateId = item.candidate_id ?? item.id ?? null;
          const resolvedRdId =
            item.rd_id ??
            item.rdId ??
            item.user_id ??
            item.userId ??
            "";
          return {
            id: resolvedCandidateId,
            candidate_id: resolvedCandidateId,
                        candidateId: resolvedCandidateId,

            rd_id: resolvedRdId,
            name: item.candidate_name || item.name || "",
            title: item.job_title || item.title || "",
            location:
              item.project_city && item.project_state
                ? `${item.project_city}, ${item.project_state}`
                : item.project_city ||
                  item.project_state ||
                  item.project_country ||
                  "",
            status: isOnBench ? "On Bench" : item.status || "",
            is_candidate_on_bench: isOnBench ? "1" : "0",
            isCandidateOnBench: isOnBench,
            benchDays: item.bench_days || item.benchDays || 0,
            experienceYears:
              item.experience_years ??
              item.experienceYears ??
              item.experience ??
              "",
            availabilityStatus:
              item.availability_status || item.availabilityStatus || "",
            availability: item.availability || "",
            candidate_connect:
              item.candidate_connect || item.candidateConnect || "",
            connected_via:
              item.connected_via ||
              item.candidate_connect_via ||
              item.connectedVia ||
              item.candidateConnectVia ||
              "",
            candidate_response_datetime:
              item.candidate_response_datetime ||
              item.candidateResponseDatetime ||
              null,
            last_updated_on:
              item.last_updated_on || item.lastUpdatedOn || null,
            reporting_updated_at:
              item.reporting_updated_at || item.reportingUpdatedAt || null,
            project_updated_at:
              item.project_updated_at || item.projectUpdatedAt || null,
            client: item.client || "",
            workAuth: workAuthName,
            workAuthId,
            locationDetail: item.project_country || item.locationDetail || "",
            score: item.score || 0,
            contractDetails: {
              noticePeriod: item.notice_period || "",
              rate: item.pay_rate || item.rate || "",
              expectedSalary: item.expected_salary || "",
              employmentType: item.contractor_status || "",
            },
            skillGroups: {
              primary: (item.primary_skills || "")
                .split(",")
                .map((skill) => skill.trim())
                .filter(Boolean),
              secondary: (item.secondary_skills || "")
                .split(",")
                .map((skill) => skill.trim())
                .filter(Boolean),
            },
            experienceItems: [],
            documents: [],
            pipeline: [],
            candidateInfo: {
              ...item,
              rd_id: resolvedRdId,
              name: item.candidate_name || item.name || "",
              title: item.job_title || item.title || "",
              emailId: item.candidate_email || item.emailId || "",
              phoneNumber: item.candidate_phone || item.phoneNumber || "",
              workAuthorization: workAuthName,
              workAuthorizationId: workAuthId,
              is_candidate_on_bench: isOnBench ? "1" : "0",
              isCandidateOnBench: isOnBench,
              previousClient: item.client || item.previousClient || "",
              experience: item.experience_years || item.experience || "",
              city: item.project_city || item.city || "",
              state: item.project_state || item.state || "",
              country: item.project_country || item.country || "",
              candidate_connect:
                item.candidate_connect || item.candidateConnect || "",
              connected_via:
                item.connected_via ||
                item.candidate_connect_via ||
                item.connectedVia ||
                item.candidateConnectVia ||
                "",
              candidate_response_datetime:
                item.candidate_response_datetime ||
                item.candidateResponseDatetime ||
                null,
              last_updated_on:
                item.last_updated_on || item.lastUpdatedOn || null,
              reporting_updated_at:
                item.reporting_updated_at || item.reportingUpdatedAt || null,
              project_updated_at:
                item.project_updated_at || item.projectUpdatedAt || null,
            },
          };
        });
        setRdCandidates(mapped);
      } else {
        console.error("Failed to fetch RD candidates:", result?.message);
        setRdCandidates([]);
      }
    } catch (err) {
      console.error("Fetch RD candidates error:", err);
      setRdCandidates([]);
    } finally {
      setDataLoading(false);
    }
  }, [
    RD_API_BASE_URL,
    normalizeRdCandidateList,
 
    resolveWorkAuthName,
  ]);

  const fetchRDAllCandidates = useCallback(async () => {
    try {
      setRdAllCandidatesLoading(true);
      const formData = new FormData();
      formData.append("section", "rd");

      // const response = await fetch(`${RD_API_BASE_URL}api_get_candidate`, {
      //   method: "POST",
      //   body: formData,
      // });
      const response = await fetch(
        `${RD_API_BASE_URL}api_get_candidate?section=rd`,
        {
          method: "GET",
          //     headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
          // body:formData,
        },
      );

      if (!response.ok) {
        console.error(
          `Fetch RD all candidates failed: HTTP ${response.status}`,
        );
        setRdAllCandidates([]);
        return;
      }

      let result = null;
      try {
        result = await response.json();
      } catch {
        setRdAllCandidates([]);
        return;
      }

      if (result?.status === "success" || result?.data) {
        const list = normalizeRdCandidateList(result?.data ?? result);
        const mapped = list.map((item) => {
          const workAuthIdRaw =
            item.work_authorization_id ||
            item.work_authorization ||
            item.workAuth ||
            "";
          const workAuthId = workAuthIdRaw ? String(workAuthIdRaw).trim() : "";
          const workAuthName = resolveWorkAuthName(workAuthId);
          const rawBenchFlag = String(
            item.is_candidate_on_bench ?? item.isCandidateOnBench ?? "0",
          )
            .trim()
            .toLowerCase();
          const isOnBench = rawBenchFlag === "1" || rawBenchFlag === "true";
          const resolvedCandidateId = item.candidate_id ?? item.id ?? null;
          const resolvedRdId =
            item.rd_id ?? item.rdId ?? item.user_id ?? item.userId ?? "";
          return {
            id: resolvedCandidateId,
            candidate_id: resolvedCandidateId,
            rd_id: resolvedRdId,
            name: item.candidate_name || item.name || "",
            title: item.job_title || item.title || "",
            location:
              item.project_city && item.project_state
                ? `${item.project_city}, ${item.project_state}`
                : item.project_city ||
                  item.project_state ||
                  item.project_country ||
                  "",
            status: isOnBench ? "On Bench" : item.status || "",
            is_candidate_on_bench: isOnBench ? "1" : "0",
            isCandidateOnBench: isOnBench,
            benchDays: item.bench_days || item.benchDays || 0,
            experienceYears:
              item.experience_years ??
              item.experienceYears ??
              item.experience ??
              "",
            availabilityStatus:
              item.availability_status || item.availabilityStatus || "",
            availability: item.availability || "",
            client: item.client || "",
            workAuth: workAuthName,
            workAuthId,
            locationDetail: item.project_country || item.locationDetail || "",
            score: item.score || 0,
            contractDetails: {
              noticePeriod: item.notice_period || "",
              rate: item.pay_rate || item.rate || "",
              expectedSalary: item.expected_salary || "",
              employmentType: item.contractor_status || "",
            },
            skillGroups: {
              primary: (item.primary_skills || "")
                .split(",")
                .map((skill) => skill.trim())
                .filter(Boolean),
              secondary: (item.secondary_skills || "")
                .split(",")
                .map((skill) => skill.trim())
                .filter(Boolean),
            },
            experienceItems: [],
            documents: [],
            pipeline: [],
            candidateInfo: {
              rd_id: resolvedRdId,
              name: item.candidate_name || item.name || "",
              title: item.job_title || item.title || "",
              emailId: item.candidate_email || item.emailId || "",
              phoneNumber: item.candidate_phone || item.phoneNumber || "",
              workAuthorization: workAuthName,
              workAuthorizationId: workAuthId,
              is_candidate_on_bench: isOnBench ? "1" : "0",
              isCandidateOnBench: isOnBench,
              previousClient: item.client || item.previousClient || "",
              experience: item.experience_years || item.experience || "",
              city: item.project_city || item.city || "",
              state: item.project_state || item.state || "",
              country: item.project_country || item.country || "",
            },
          };
        });
        setRdAllCandidates(mapped);
      } else {
        console.error("Failed to fetch RD all candidates:", result?.message);
        setRdAllCandidates([]);
      }
    } catch (err) {
      console.error("Fetch RD all candidates error:", err);
      setRdAllCandidates([]);
    } finally {
      setRdAllCandidatesLoading(false);
    }
  }, [RD_API_BASE_URL, normalizeRdCandidateList, resolveWorkAuthName]);
  const fetchRDSubmissionDetails = useCallback(
    async (candidateId) => {
      if (!candidateId) return;
      const normalizeSubmissionList = (payload) => {
        if (Array.isArray(payload)) return payload;
        if (payload?.data && Array.isArray(payload.data)) return payload.data;
        if (payload?.records && Array.isArray(payload.records))
          return payload.records;
        if (payload?.submissions && Array.isArray(payload.submissions))
          return payload.submissions;
        if (payload && typeof payload === "object") return [payload];
        return [];
      };

      const normalizeSubmissionEntry = (
        entry = {},
        fallbackCandidateId,
        index,
      ) => {
        const resolvedCandidateId =
          entry.candidate_id ||
          entry.candidateId ||
          entry.candidateID ||
          entry.candidate ||
          fallbackCandidateId ||
          candidateId ||
          null;
        const submittedAtRaw =
          entry.submittedAt ||
          entry.submission_date ||
          entry.submitted_at ||
          entry.created_at ||
          entry.updated_at ||
          entry.date ||
          null;
        const submittedByFirst =
          entry.submitted_by_first_name || entry.submittedByFirstName || "";
        const submittedByLast =
          entry.submitted_by_last_name || entry.submittedByLastName || "";
        const submittedByFull = `${submittedByFirst} ${submittedByLast}`.trim();

        return {
          id:
            entry.id ||
            entry.submission_id ||
            entry.submissionId ||
            (resolvedCandidateId
              ? `submission-${resolvedCandidateId}-${index}`
              : `submission-${index}`),
          candidateId: resolvedCandidateId,
          quarter: entry.quarter || entry.qtr || "",
          week: entry.week || entry.iso_week || "",
          role: entry.role || entry.position || "",
          client: entry.client || entry.client_name || entry.clientName || "",
          recruiter:
            entry.recruiter ||
            entry.recruiter_name ||
            entry.recruiterName ||
            entry.submitted_by ||
            entry.submittedBy ||
            submittedByFull ||
            "",
          submittedBy:
            submittedByFull ||
            entry.submittedBy ||
            entry.submitted_by ||
            entry.recruiter ||
            entry.recruiter_name ||
            "",
          submittedAt: submittedAtRaw,
          submissionType:
            entry.submission_type || entry.submissionType || entry.type || "",
          submissionStatus:
            entry.submission_status_name ||
            entry.submission_status ||
            entry.status_name ||
            entry.status ||
            entry.submissionStatus ||
            "",
          submissionStatusId:
            entry.submission_status_id ||
            entry.status_id ||
            entry.submissionStatusId ||
            null,
          reason: entry.reason || entry.notes || "",
          bu: entry.bu || entry.bu_name || entry.buName || "",
          teamName: entry.team_name || entry.teamName || "",
          sourceOfRequirement:
            entry.source_of_requirement ||
            entry.requirement_source ||
            entry.requirementSource ||
            "",
        };
      };

      try {
        const body = new URLSearchParams();
        body.append("candidate_id", String(candidateId ?? ""));
        const response = await fetch(
          `${RD_API_BASE_URL}getCandidate_submission_details`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: body.toString(),
          },
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `HTTP ${response.status}${errorText ? ` - ${errorText}` : ""}`,
          );
        }

        const result = await response.json();
        const rawList = normalizeSubmissionList(result?.data ?? result);
        if (rawList.length === 0) {
          setRdSubmissions((prev) => ({ ...prev, [candidateId]: undefined }));
          return;
        }

        const grouped = rawList.reduce((acc, entry, index) => {
          const entryCandidateId =
            entry?.candidate_id ||
            entry?.candidateId ||
            entry?.candidateID ||
            entry?.candidate ||
            candidateId ||
            null;
          const entries =
            Array.isArray(entry?.history) && entry.history.length > 0
              ? entry.history.map((historyEntry, historyIndex) =>
                  normalizeSubmissionEntry(
                    { ...entry, ...historyEntry },
                    entryCandidateId,
                    historyIndex,
                  ),
                )
              : [normalizeSubmissionEntry(entry, entryCandidateId, index)];
          entries.forEach((normalized) => {
            if (!normalized.candidateId) return;
            const key = normalized.candidateId;
            if (!acc[key]) acc[key] = [];
            acc[key].push(normalized);
          });
          return acc;
        }, {});

        const toTimeValue = (value) => {
          if (!value) return null;
          const parsed = new Date(value);
          return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
        };

        const submissionMap = Object.entries(grouped).reduce(
          (acc, [entryCandidateId, entries]) => {
            const sorted = [...entries].sort((a, b) => {
              const aTime = toTimeValue(a.submittedAt);
              const bTime = toTimeValue(b.submittedAt);
              if (aTime && bTime) return bTime - aTime;
              if (bTime) return 1;
              if (aTime) return -1;
              return 0;
            });
            const latest = sorted[0] || {};
            acc[entryCandidateId] = {
              ...latest,
              history: sorted,
            };
            return acc;
          },
          {},
        );

        setRdSubmissions((prev) => ({
          ...prev,
          ...submissionMap,
        }));
      } catch (err) {
        console.error("Fetch RD submission details error:", err);
      }
    },
    [RD_API_BASE_URL],
  );

  const fetchCurrentUser = useCallback(async () => {
    try {
      setUserLoading(true);
      console.log("?? Fetching current user session...");

      const response = await fetch(`${API_BASE_URL}/get_current_user.php`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      console.log("?? User session response status:", response.status);

      if (response.status === 401 || response.status === 403) {
        // Session expired or unauthorized
        setSessionExpired(true);
        setCurrentUser(null);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("?? User session result:", result);

      if (result.success && result.user) {
        console.log("? User authenticated:", result.user);
        setCurrentUser(result.user);
        setSessionExpired(false); // Reset session expired flag
      } else {
        console.log("? No valid user session");
        setSessionExpired(true);
        setCurrentUser(null);
      }
    } catch (error) {
      console.error("? Fetch current user error:", error);
      setSessionExpired(true);
      setCurrentUser(null);
    } finally {
      setUserLoading(false);
    }
  }, []);

  // ?? NEW: Function to filter candidates based on user role
  const getFilteredCandidatesForUser = useCallback(
    (allCandidates) => {
      if (!currentUser || userLoading) {
        console.log("?? No user or still loading, returning empty array");
        return [];
      }

      console.log("?? Filtering candidates for user:", {
        user: currentUser,
        role: currentUser.role,
        email: currentUser.email,
        totalCandidates: allCandidates.length,
      });

      // Admin and Team Lead can see all candidates
      if (currentUser.role === "Admin" || currentUser.role === "Team Lead") {
        console.log(
          "? Admin/Team Lead - showing all candidates:",
          allCandidates.length,
        );
        return allCandidates;
      }

      // BU Head can only see candidates where bu_head_emailid matches their email
      if (currentUser.role === "BU Head") {
        const userEmail = currentUser.email;
        const filteredCandidates = allCandidates.filter((candidate) => {
          const buHeadEmail =
            candidate.bu_head_emailid || candidate.buHeadEmailid;
          const isAssigned = buHeadEmail === userEmail;

          if (isAssigned) {
            console.log("? Candidate under BU Head:", {
              candidate: candidate.candidateName,
              client: candidate.client,
              buHead: candidate.buHead || candidate.bu_head,
              buHeadEmail: buHeadEmail,
            });
          }

          return isAssigned;
        });

        console.log("?? BU Head filtered results:", {
          userEmail,
          totalCandidates: allCandidates.length,
          assignedCandidates: filteredCandidates.length,
          candidateNames: filteredCandidates.map((c) => c.candidateName),
        });

        return filteredCandidates;
      }

      // Account Manager can only see their assigned candidates
      if (currentUser.role === "Account Manager") {
        const userEmail = currentUser.email;
        const filteredCandidates = allCandidates.filter((candidate) => {
          const isAssigned =
            candidate.account_manager_1 === userEmail ||
            candidate.account_manager_2 === userEmail ||
            candidate.account_manager_3 === userEmail ||
            candidate.account_manager_4 === userEmail ||
            candidate.account_manager_5 === userEmail ||
            candidate.account_manager_6 === userEmail ||
            candidate.account_manager_7 === userEmail ||
            candidate.account_manager_8 === userEmail;

          if (isAssigned) {
            console.log("? Candidate assigned to user:", {
              candidate: candidate.candidateName,
              client: candidate.client,
              assignedTo: [
                candidate.account_manager_1,
                candidate.account_manager_2,
                candidate.account_manager_3,
                candidate.account_manager_4,
                candidate.account_manager_5,
                candidate.account_manager_6,
                candidate.account_manager_7,
                candidate.account_manager_8,
              ].filter(Boolean),
            });
          }

          return isAssigned;
        });

        console.log("?? Account Manager filtered results:", {
          userEmail,
          totalCandidates: allCandidates.length,
          assignedCandidates: filteredCandidates.length,
          candidateNames: filteredCandidates.map((c) => c.candidateName),
        });

        return filteredCandidates;
      }

      console.log("? Unknown role, returning empty array:", currentUser.role);
      return [];
    },
    [currentUser, userLoading],
  );

  // Calculate recently onboarded candidates (last 7 days)
  const recentlyOnboardedCandidates = useMemo(() => {
    if (!candidates || candidates.length === 0) {
      return [];
    }

    return candidates
      .filter((c) => {
        const startDate = new Date(c.startDate);
        const today = new Date();
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(today.getDate() - 7);
        return startDate >= oneWeekAgo && startDate <= today;
      })
      .sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
  }, [candidates]);

  // Calculate roll-off candidates (ending within 60 days OR all overdue active)
const rollOffCandidates = useMemo(() => {
  if (!candidates || candidates.length === 0) {
    return [];
  }

  const today = new Date();
  const sixtyDaysFromNow = new Date();
  sixtyDaysFromNow.setDate(today.getDate() + 60);

  return candidates
    .filter((c) => {
      if (c.status !== "Active") return false;

      const rawDate = c.sowEndDate || c.endDate;
      if (!rawDate) return false;

      const endDate = new Date(rawDate);
      if (isNaN(endDate.getTime())) return false;

      // No lower bound — show ALL overdue + upcoming 60 days
      return endDate <= sixtyDaysFromNow;
    })
    .sort((a, b) => {
      const aDate = new Date(a.sowEndDate || a.endDate);
      const bDate = new Date(b.sowEndDate || b.endDate);
      return aDate - bDate; // oldest overdue appears first
    });
}, [candidates]);

  const getExtensionPendingCandidates = useCallback(() => {
  return rollOffCandidates.filter((c) => {
    const extensionRequested = c.extension_requested;
    return (
      !extensionRequested ||
      extensionRequested === 0 ||
      extensionRequested === "0" ||
      extensionRequested === null
    );
  });
}, [rollOffCandidates]);

  useEffect(() => {
    if (activeTab === "rd-dashboard") {
      setActiveTab("rd");
      setRdViewMode("dashboard");
    }
  }, [activeTab]);

  // Fetch candidates from database
  const fetchCandidates = useCallback(async () => {
    try {
      setDataLoading(true);

      const response = await fetch(`${API_BASE_URL}/get_candidates.php`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        const candidatesData = result.data || [];
        console.log("?? Raw candidates from database:", candidatesData.length);

        // ?? NEW: Apply user-based filtering
        const filteredForUser = getFilteredCandidatesForUser(candidatesData);
        console.log("?? Setting filtered candidates:", filteredForUser.length);
        setCandidates(filteredForUser);
      } else {
        console.error("Failed to fetch candidates:", result.message);
        setCandidates([]);
        error(`Failed to load candidates: ${result.message}`);
      }
    } catch (error) {
      console.error("Fetch candidates error:", error);
      error("Failed to load candidates. Please check your connection.");
      setCandidates([]);
    } finally {
      setDataLoading(false);
    }
  }, [getFilteredCandidatesForUser]);

  const handleSyncComplete = (message) => {
    success(message);
    fetchCandidates();
    setIsSyncing(false);
  };

  useEffect(() => {
    // ?? NEW: First fetch user session, then fetch candidates
    const initializeApp = async () => {
      await fetchCurrentUser();
    };

    initializeApp();
  }, [fetchCurrentUser]);

  // 🔍 NEW: Fetch candidates when user data is loaded
useEffect(() => {
    if (!userLoading && currentUser) {
      console.log("🔍 User loaded, fetching candidates...");
      fetchCandidates();
      fetchRDCandidates();
      fetchRDAllCandidates();
    }
  }, [userLoading, currentUser, fetchCandidates, fetchRDCandidates, fetchRDAllCandidates]);

  // 🔄 Redirect Account Managers to candidates tab on login
  useEffect(() => {
    if (!userLoading && currentUser) {
      const isAccountManager = currentUser.role === "Account Manager";
      
      // If Account Manager is on dashboard, redirect to candidates
      if (isAccountManager && activeTab === "dashboard") {
        console.log("🔄 Redirecting Account Manager to candidates tab");
        setActiveTab("candidates");
      }
    }
  }, [userLoading, currentUser, activeTab]);

  useEffect(() => {
    if (activeTab !== "rd" || rdViewMode !== "all-candidates") return;
    fetchRDAllCandidates();
  }, [activeTab, rdViewMode, fetchRDAllCandidates]);

  
  useEffect(() => {
    if (activeTab !== "rd" || rdViewMode !== "candidates") return;
    fetchRDCandidates();
  }, [activeTab, rdViewMode, fetchRDCandidates]);


  // Fetch submission details for AI preview candidate as well.
  useEffect(() => {
    const candidateId =
      aiPreviewCandidate?.candidate_id ??
      aiPreviewCandidate?.candidateId ??
      aiPreviewCandidate?.id ??
      null;
    if (!candidateId) return;
    fetchRDSubmissionDetails(candidateId);
  }, [
    aiPreviewCandidate?.id,
    aiPreviewCandidate?.candidate_id,
    aiPreviewCandidate?.candidateId,
    fetchRDSubmissionDetails,
  ]);

  // Fetch submission details when a candidate is selected
  useEffect(() => {
    if (!selectedRdCandidate?.id) return;
    fetchRDSubmissionDetails(selectedRdCandidate.id);
  }, [selectedRdCandidate, fetchRDSubmissionDetails]);

  // Add this useEffect to handle session timeouts during usage
  useEffect(() => {
    if (!currentUser) return;

    const checkSessionValidity = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/get_current_user.php`, {
          method: "GET",
          credentials: "include",
        });

        if (response.status === 401 || response.status === 403) {
          setSessionExpired(true);
          setCurrentUser(null);
          warning("Your session has expired. Please log in again.");
        }
      } catch (error) {
        console.error("Session validity check failed:", error);
      }
    };

    // Check session every 10 minutes
    const sessionCheckInterval = setInterval(
      checkSessionValidity,
      10 * 60 * 1000,
    );

    return () => clearInterval(sessionCheckInterval);
  }, [currentUser, warning]);

  // Delete candidate handler
  const handleDeleteCandidate = async (candidateId) => {
    try {
      setIsLoading(true);

      const response = await fetch(`${API_BASE_URL}/delete_candidate.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: candidateId }),
      });

      const result = await response.json();

      if (result.success) {
        success("Candidate deleted successfully!");
        await fetchCandidates();
        setShowDeleteModal(false);
        setSelectedCandidate(null);
      } else {
        error(`Failed to delete candidate: ${result.message}`);
      }
    } catch (error) {
      console.error("Delete candidate error:", error);
      error(
        "Failed to delete candidate: Network error. Please check if your PHP backend is running.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Update reporting info handler
  const handleUpdateReportingInfo = async (candidateId, reportingData) => {
    try {
      setIsLoading(true);

      const currentCandidate = candidates.find((c) => c.id === candidateId);
      if (!currentCandidate) {
        throw new Error("Candidate not found");
      }

      // Create complete update data with all current values preserved
      const updatedData = {
        id: candidateId,
        // Basic info (preserve existing)
        plcCode: currentCandidate.plcCode,
        sapId: currentCandidate.sapId,
        candidateName: currentCandidate.candidateName,
        candidatePhone: currentCandidate.candidatePhone,
        candidateEmail: currentCandidate.candidateEmail,
        client: currentCandidate.client,
        jobTitle: currentCandidate.jobTitle,
        primarySkills: currentCandidate.primarySkills,
        startDate: currentCandidate.startDate,
        endDate: currentCandidate.endDate,
        projectCity: currentCandidate.projectCity,
        projectState: currentCandidate.projectState,
        payRate: currentCandidate.payRate,
        clientRate: currentCandidate.clientRate,
        contractorVendorName: currentCandidate.contractorVendorName,
        dalName: currentCandidate.dalName,
        dmName: currentCandidate.dmName,
        status: currentCandidate.status,

        // Updated reporting fields
        workEmail: reportingData.workEmail || currentCandidate.workEmail || "",
        reportingManagerName:
          reportingData.reportingManagerName ||
          currentCandidate.reportingManagerName ||
          "",
        reportingManagerEmail:
          reportingData.reportingManagerEmail ||
          currentCandidate.reportingManagerEmail ||
          "",
        reportingManagerPhone:
          reportingData.reportingManagerPhone ||
          currentCandidate.reportingManagerPhone ||
          "",
        reportingComments:
          reportingData.reportingComments ||
          currentCandidate.reportingComments ||
          "",
        projectTechStack:
          reportingData.projectTechStack ||
          currentCandidate.project_tech_stack ||
          "",
        projectKeyTechnologies:
          reportingData.projectKeyTechnologies ||
          currentCandidate.project_key_technologies ||
          "",
        projectBusinessProblem:
          reportingData.projectBusinessProblem ||
          currentCandidate.project_business_problem ||
          "",
        projectScopeObjectives:
          reportingData.projectScopeObjectives ||
          currentCandidate.project_scope_objectives ||
          "",
        projectRoleResponsibility:
          reportingData.projectRoleResponsibility ||
          currentCandidate.project_role_responsibility ||
          "",
        projectExperienceRating:
          reportingData.projectExperienceRating ||
          currentCandidate.project_experience_rating ||
          0,

        // DO NOT include reportingUpdatedAt, reportingUpdatedBy, etc. - let PHP handle these
      };

      const response = await fetch(`${API_BASE_URL}/update_candidate.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include", // This is important for sending cookies/session
        body: JSON.stringify(withCandidateUpdateMeta(candidateId, updatedData)),
      });

      const result = await response.json();

      if (result.success) {
        // Add history records for changed reporting fields
        const reportingFields = [
          "workEmail",
          "reportingManagerName",
          "reportingManagerEmail",
          "reportingManagerPhone",
          "reportingComments",
          "projectTechStack",
          "projectKeyTechnologies",
          "projectBusinessProblem",
          "projectScopeObjectives",
          "projectRoleResponsibility",
          "projectExperienceRating",
        ];

        for (const field of reportingFields) {
          const oldValue =
            currentCandidate[field] ||
            currentCandidate[
              `project_${field.replace("project", "").toLowerCase()}`
            ];
          const newValue = reportingData[field];

          if (
            oldValue !== newValue &&
            newValue !== undefined &&
            newValue !== ""
          ) {
            const actionType = field.startsWith("project")
              ? "project_update"
              : "reporting_update";
            await addToHistory(
              candidateId,
              actionType,
              field,
              oldValue || "Not set",
              newValue,
              "Reporting/Project information updated",
              `${field} updated via Reporting Info modal from "${oldValue || "Not set"}" to "${newValue}"`,
            );
          }
        }

        success("Information updated successfully!");
        await fetchCandidates();
        setShowReportingInfoModal(false);
        setSelectedCandidate(null);
      } else {
        error(`Failed to update information: ${result.message}`);
      }
    } catch (error) {
      console.error("Update error:", error);
      error(`Failed to update information: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Add candidate handler
  const handleAddCandidate = async (candidateData) => {
    try {
      setIsLoading(true);

      // Basic validation
      if (!candidateData.candidateName || !candidateData.candidateEmail) {
        console.error("Missing required fields");
        alert("Please fill in candidate name and email");
        setIsLoading(false);
        return;
      }

      console.log("Sending to API:", `${API_BASE_URL}/add_candidate.php`);

      const response = await fetch(`${API_BASE_URL}/add_candidate.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(candidateData),
      });

      console.log("API Response status:", response.status);

      const responseText = await response.text();
      console.log("Raw API response:", responseText);

      if (!response.ok) {
        console.error("HTTP Error:", response.status, responseText);
        alert(`Server Error: ${response.status} - ${responseText}`);
        return;
      }

      let result;
      try {
        result = JSON.parse(responseText);
        console.log("Parsed result:", result);
      } catch (parseError) {
        console.error("JSON Parse Error:", parseError);
        alert("Invalid response from server: " + responseText);
        return;
      }

      if (result.success) {
        // ?? NEW: Add initial history record for new candidate creation
        if (result.candidate_id) {
          await addToHistory(
            result.candidate_id,
            "candidate_created",
            "candidate_creation",
            "",
            "New candidate added",
            "Initial candidate creation",
            `New candidate "${candidateData.candidateName}" added with PLC Code: ${candidateData.plcCode}, SAP ID: ${candidateData.sapId}`,
          );

          // Track initial PLC and SAP ID assignment
          if (candidateData.plcCode) {
            await addToHistory(
              result.candidate_id,
              "general_update",
              "plcCode",
              "Not set",
              candidateData.plcCode,
              "Initial PLC Code assignment",
              `PLC Code assigned: ${candidateData.plcCode}`,
            );
          }

          if (candidateData.sapId) {
            await addToHistory(
              result.candidate_id,
              "general_update",
              "sapId",
              "Not set",
              candidateData.sapId,
              "Initial SAP ID assignment",
              `SAP ID assigned: ${candidateData.sapId}`,
            );
          }
        }

        alert("Candidate added successfully!");
        setShowAddModal(false);
        await fetchCandidates();
      } else {
        console.error("? Add failed:", result.message);
        alert(
          "Failed to add candidate: " + (result.message || "Unknown error"),
        );
      }
    } catch (error) {
      console.error("=== ADD CANDIDATE ERROR ===");
      console.error("Error:", error);
      alert("Error adding candidate: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Edit candidate handler
  const handleEditCandidate = async (candidateId, candidateData) => {
    try {
      setIsLoading(true);

      const currentCandidate = candidates.find((c) => c.id === candidateId);
      if (!currentCandidate) {
        throw new Error("Candidate not found");
      }

      // Check if end date is being set for the first time or changed
      const isEndDateChanged =
        !currentCandidate.endDate && candidateData.endDate;

      if (isEndDateChanged) {
        // Show confirmation dialog for RD forwarding
        const shouldForwardToRD = window.confirm(
          `?? CAUTION: Setting an end date will mark this candidate as INACTIVE.\n\nCandidate: ${candidateData.candidateName}\nEnd Date: ${candidateData.endDate}\n\nDo you want to forward this candidate's profile to the RD team for redeployment?`,
        );

        if (shouldForwardToRD) {
          candidateData.sentToRD = true;
          candidateData.rdReason = "Contract ended - manual end date set";
          candidateData.status = "Inactive";
          success("Candidate will be forwarded to RD team for redeployment");
        } else {
          candidateData.status = "Inactive";
          warning("Candidate marked as inactive without RD forwarding");
        }
        candidateData.sowEndDate = null;
      }

      const updatedData = {
        ...candidateData,
        id: candidateId,
      };

      console.log("Updating candidate with data:", updatedData);

      const response = await fetch(`${API_BASE_URL}/update_candidate.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(withCandidateUpdateMeta(candidateId, updatedData)),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP error! status: ${response.status} - ${errorText}`,
        );
      }

      const result = await response.json();

      if (result.success) {
        // ?? FIXED: Enhanced field tracking with proper field name mapping
        const fieldsToTrack = [
          // Basic Information - FIXED MAPPING
          {
            jsField: "candidateName",
            dbField: "candidate_name",
            label: "Candidate Name",
          },
          {
            jsField: "candidatePhone",
            dbField: "candidate_phone",
            label: "Phone Number",
          },
          {
            jsField: "candidateEmail",
            dbField: "candidate_email",
            label: "Email Address",
          },
          { jsField: "workEmail", dbField: "work_email", label: "Work Email" },
          { jsField: "plcCode", dbField: "plc_code", label: "PLC Code" }, // ?? FIXED
          { jsField: "sapId", dbField: "sap_id", label: "SAP ID" }, // ?? FIXED

          // Project Information
          { jsField: "client", dbField: "client", label: "Client" },
          { jsField: "jobTitle", dbField: "job_title", label: "Job Title" },
          {
            jsField: "primarySkills",
            dbField: "primary_skills",
            label: "Primary Skills",
          },
          {
            jsField: "secondarySkills",
            dbField: "secondary_skills",
            label: "Secondary Skills",
          },
          { jsField: "startDate", dbField: "start_date", label: "Start Date" },
          { jsField: "endDate", dbField: "end_date", label: "End Date" },
          {
            jsField: "sowEndDate",
            dbField: "sow_end_date",
            label: "SOW End Date",
          },
          {
            jsField: "projectCity",
            dbField: "project_city",
            label: "Project City",
          },
          {
            jsField: "projectState",
            dbField: "project_state",
            label: "Project State",
          },

          // Financial Information
          { jsField: "payRate", dbField: "pay_rate", label: "Pay Rate" },
          {
            jsField: "clientRate",
            dbField: "client_rate",
            label: "Client Rate",
          },
          {
            jsField: "marginValue",
            dbField: "margin_value",
            label: "Margin %",
          },
          {
            jsField: "contractorStatus",
            dbField: "contractor_status",
            label: "Contract Status",
          },

          // Management Team
          { jsField: "dalName", dbField: "dal_name", label: "DAL Name" },
          { jsField: "dmName", dbField: "dm_name", label: "DM Name" },
          { jsField: "addName", dbField: "add_name", label: "ADD Name" },
          { jsField: "buHead", dbField: "bu_head", label: "BU Head" },
          { jsField: "status", dbField: "status", label: "Status" },
        ];

        // Track changes for each field
        for (const fieldConfig of fieldsToTrack) {
          const oldValue =
            currentCandidate[fieldConfig.jsField] ||
            currentCandidate[fieldConfig.dbField]; // Try both formats
          const newValue = candidateData[fieldConfig.jsField];

          // Only track if there's an actual change
          if (
            oldValue !== newValue &&
            newValue !== undefined &&
            newValue !== null
          ) {
            console.log(`?? Field Change Detected:`, {
              field: fieldConfig.jsField,
              label: fieldConfig.label,
              oldValue: oldValue || "Not set",
              newValue: newValue,
              candidateId: candidateId,
            });

            await addToHistory(
              candidateId,
              "general_update",
              fieldConfig.jsField, // Use JS field name for consistency
              oldValue || "Not set",
              newValue,
              `${fieldConfig.label} updated`,
              `${fieldConfig.label} updated via Edit Information from "${oldValue || "Not set"}" to "${newValue}"`,
            );
          }
        }

        // Special handling for RD forwarding
        if (isEndDateChanged && candidateData.sentToRD) {
          await addToHistory(
            candidateId,
            "status_change",
            "sentToRD",
            "No",
            "Yes",
            candidateData.rdReason || "Contract ended",
            `Candidate forwarded to RD team due to contract ending on ${candidateData.endDate}`,
          );
        }

        if (isEndDateChanged && candidateData.sentToRD) {
          success("Candidate updated successfully and forwarded to RD team!");
        } else {
          success("Candidate updated successfully!");
        }

        await fetchCandidates(); // Refresh candidates list
        setShowEditModal(false);
        setSelectedCandidate(null);
      } else {
        error(`Failed to update candidate: ${result.message}`);
      }
    } catch (error) {
      console.error("Update candidate error:", error);
      error(`Failed to update candidate: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Add this function after your existing handlers
  const forwardToRDTeam = async (candidateData) => {
    try {
      console.log("Forwarding candidate to RD team:", candidateData);

      const response = await fetch(`${API_BASE_URL}/forward_to_rd.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          candidate_id: candidateData.id,
          candidate_name: candidateData.candidateName,
          candidate_email: candidateData.candidateEmail,
          candidate_phone: candidateData.candidatePhone,
          client: candidateData.client,
          job_title: candidateData.jobTitle,
          primary_skills: candidateData.primarySkills,
          secondary_skills: candidateData.secondarySkills,
          end_date: candidateData.endDate,
          contract_end_date: candidateData.endDate, // Same as end_date for now
          project_city: candidateData.projectCity,
          project_state: candidateData.projectState,
          project_location:
            candidateData.projectCity && candidateData.projectState
              ? `${candidateData.projectCity}, ${candidateData.projectState}`
              : null,
          reason_for_forwarding:
            candidateData.rdReason || candidateData.noExtensionReason,
          client_rating: candidateData.clientRating,
          client_feedback: candidateData.clientFeedback,
          contacted_with: candidateData.contactedWith,
          extension_duration: candidateData.extensionDuration,
          plc_code: candidateData.plcCode,
          sap_id: candidateData.sapId,
          contract_type: candidateData.contractorStatus,
          forwarded_by: currentUser ? currentUser.id : null,
          forwarded_at: new Date().toISOString().slice(0, 19).replace("T", " "),
        }),
      });

      const result = await response.json();

      if (result.success) {
        console.log("Successfully forwarded to RD team");
        await fetchRDCandidates();
        return true;
      } else {
        console.error("Failed to forward to RD team:", result.message);
        return false;
      }
    } catch (error) {
      console.error("Error forwarding to RD team:", error);
      return false;
    }
  };

  // Roll-off edit handler
  const handleRollOffUpdate = async (candidateId, rollOffData) => {
    try {
      setIsLoading(true);

      const currentCandidate = candidates.find((c) => c.id === candidateId);
      if (!currentCandidate) {
        throw new Error("Candidate not found");
      }

      let updatedData = {
        id: candidateId,
        clientRating: rollOffData.clientRating,
        clientFeedback: rollOffData.clientFeedback,
        improvementArea1: rollOffData.improvementArea1,
        improvementArea2: rollOffData.improvementArea2,
        improvementArea3: rollOffData.improvementArea3,
        contactedWith: rollOffData.contactedWith || "",
        extensionRequested:
          rollOffData.extensionRequested === true
            ? 1
            : rollOffData.extensionRequested === false
              ? 2
              : 0,
        extensionStatus: rollOffData.extensionStatus || "",
        extensionDate: rollOffData.extensionDate || "",
        extensionDuration: rollOffData.extensionDuration || "",
        extensionComments: rollOffData.extensionComments || "",
        noExtensionReason: rollOffData.noExtensionReason || "",
      };

      // Handle SOW extension with history
      if (
        rollOffData.extensionDate &&
        rollOffData.extensionDate !== currentCandidate.sowEndDate
      ) {
        updatedData.sowEndDate = rollOffData.extensionDate;

        // Add history record for SOW extension
        await addToHistory(
          candidateId,
          "sow_extension",
          "sowEndDate",
          currentCandidate.sowEndDate,
          rollOffData.extensionDate,
          rollOffData.extensionComments || "SOW extension",
          `Extended from ${currentCandidate.sowEndDate} to ${rollOffData.extensionDate}. Reason: ${rollOffData.extensionComments || "Not specified"}`,
        );
      }

      // If extension is not requested, mark for RD forwarding
      if (rollOffData.extensionRequested === false) {
        updatedData.sentToRD = true;
        updatedData.rdReason =
          rollOffData.noExtensionReason || "Extension not requested";

        // Add history for RD forwarding
        await addToHistory(
          candidateId,
          "status_change",
          "sentToRD",
          false,
          true,
          rollOffData.noExtensionReason || "Extension not requested",
          `Candidate forwarded to RD team. Reason: ${rollOffData.noExtensionReason || "Not specified"}`,
        );
      }

      const response = await fetch(`${API_BASE_URL}/update_candidate.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(withCandidateUpdateMeta(candidateId, updatedData)),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP error! status: ${response.status} - ${errorText}`,
        );
      }

      const result = await response.json();

      if (result.success) {
        if (rollOffData.extensionRequested === false) {
          success(
            "Roll-off information updated and candidate forwarded to RD team with history tracking!",
          );
        } else {
          success(
            "Roll-off information updated successfully with history tracking!",
          );
        }

        await fetchCandidates();
        setShowRollOffEditModal(false);
        setSelectedCandidate(null);
      } else {
        error(`Failed to update roll-off information: ${result.message}`);
      }
    } catch (err) {
      console.error("Update roll-off info error:", err);
      error(`Failed to update roll-off information: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Update user role
  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      setIsLoading(true);

      const response = await fetch(`${API_BASE_URL}/update_user_role.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          user_id: userId,
          role: newRole,
        }),
      });

      const result = await response.json();
      if (result.success) {
        success("User role updated successfully!");

        setShowEditUserModal(false);
        setSelectedUser(null);
      } else {
        error(`Failed to update user role: ${result.message}`);
      }
    } catch (error) {
      console.error("Update user role error:", error);
      error("Failed to update user role");
    } finally {
      setIsLoading(false);
    }
  };

  // Update user status
  const handleUpdateUserStatus = async (userId, newStatus) => {
    try {
      setIsLoading(true);

      const response = await fetch(`${API_BASE_URL}/update_user_status.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          user_id: userId,
          status: newStatus,
        }),
      });

      const result = await response.json();
      if (result.success) {
        success("User status updated successfully!");
      } else {
        error(`Failed to update user status: ${result.message}`);
      }
    } catch (error) {
      console.error("Update user status error:", error);
      error("Failed to update user status");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReportingInfoClick = (candidate) => {
    console.log("Update Reporting Info clicked for:", candidate.candidateName);
    // Close any open modals first
    if (showCandidateDetails) {
      setShowCandidateDetails(false);
    }
    setSelectedCandidate(candidate);
    setShowReportingInfoModal(true);
  };

  const handleProjectDetailsClick = (candidate) => {
    console.log("Update Project Details clicked for:", candidate.candidateName);
    // Close any open modals first
    if (showCandidateDetails) {
      setShowCandidateDetails(false);
    }
    setSelectedCandidate(candidate);
    setShowProjectDetailsModal(true);
  };

  const handleMarkAsInterviewPanel = async (candidateId) => {
    try {
      const response = await fetch(
        `https://pmhiring.vdartdigital.com/api/candidates/${candidateId}/mark-interview-panel`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            marked_by: currentUser?.email,
            marked_at: new Date().toISOString(),
          }),
        },
      );

      if (response.ok) {
        alert("Candidate marked for interview panel successfully!");
        fetchCandidates(); // Refresh the list
      } else {
        alert("Failed to mark candidate. Please try again.");
      }
    } catch (error) {
      console.error("Error marking candidate:", error);
      alert("Error marking candidate. Please try again.");
    }
  };

  const handleUpdateProjectInfo = async (candidateId, projectData) => {
    try {
      setIsLoading(true);

      const currentCandidate = candidates.find((c) => c.id === candidateId);
      if (!currentCandidate) {
        throw new Error("Candidate not found");
      }

      const updatedData = {
        id: candidateId,
        ...currentCandidate,
        projectTechStack: projectData.projectTechStack,
        projectKeyTechnologies: projectData.projectKeyTechnologies,
        projectBusinessProblem: projectData.projectBusinessProblem,
        projectScopeObjectives: projectData.projectScopeObjectives,
        projectRoleResponsibility: projectData.projectRoleResponsibility,
        projectExperienceRating: projectData.projectExperienceRating,
      };

      const response = await fetch(`${API_BASE_URL}/update_candidate.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(withCandidateUpdateMeta(candidateId, updatedData)),
      });

      const result = await response.json();

      if (result.success) {
        // Add history records for changed project fields
        const projectFields = [
          "projectTechStack",
          "projectKeyTechnologies",
          "projectBusinessProblem",
          "projectScopeObjectives",
          "projectRoleResponsibility",
          "projectExperienceRating",
        ];

        for (const field of projectFields) {
          const oldValue =
            currentCandidate[field] ||
            currentCandidate[
              `project_${field.replace("project", "").toLowerCase()}`
            ];
          const newValue = projectData[field];

          if (
            oldValue !== newValue &&
            newValue !== undefined &&
            newValue !== ""
          ) {
            await addToHistory(
              candidateId,
              "project_update",
              field,
              oldValue || "Not set",
              newValue,
              "Project details updated",
              `${field} updated via Project Details modal from "${oldValue || "Not set"}" to "${newValue}"`,
            );
          }
        }

        success("Project information updated successfully!");
        await fetchCandidates();
        setShowProjectDetailsModal(false);
        setSelectedCandidate(null);
      } else {
        error(`Failed to update project information: ${result.message}`);
      }
    } catch (error) {
      console.error("Update project info error:", error);
      error(`Failed to update project information: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Import handler function
  const handleImport = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      setIsLoading(true);

      // Show detailed processing info
      info(
        `Processing ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)...\n\nFile type: ${file.type}\nStarting import process...`,
      );

      console.log("=== IMPORT DEBUG ===");
      console.log("File:", file.name, file.size, "bytes", file.type);
      console.log("API URL:", `${API_BASE_URL}/import.php`);

      const response = await fetch(`${API_BASE_URL}/import.php`, {
        method: "POST",
        body: formData,
      });

      console.log("Import response status:", response.status);
      console.log("Import response headers:", [...response.headers.entries()]);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Import server error:", errorText);
        throw new Error(
          `Server error: ${response.status} ${response.statusText} - ${errorText}`,
        );
      }

      const responseText = await response.text();
      console.log("Raw import response:", responseText);

      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error("JSON parse error:", parseError);
        console.error("Response text:", responseText);
        throw new Error(
          `Invalid JSON response: ${responseText.substring(0, 200)}...`,
        );
      }

      console.log("Parsed import result:", result);

      if (result.success) {
        // Extract data with fallbacks
        const imported = result.data?.imported || result.imported || 0;
        const updated = result.data?.updated || result.updated || 0; // NEW
        const skipped = result.data?.skipped || result.skipped || 0;
        const total_rows =
          result.data?.total_rows ||
          result.data?.totalRows ||
          result.total_rows ||
          0;
        const errors = result.data?.errors || result.errors || [];

        let message = `? Import completed successfully!\n\n`;
        message += `?? Summary:\n`;
        message += `? Total rows processed: ${total_rows}\n`;
        message += `? New candidates added: ${imported}\n`;

        if (updated > 0) {
          message += `? Existing candidates updated: ${updated}\n`;
        }

        message += `? Skipped/Errors: ${skipped}\n`;

        if (updated > 0) {
          message += `\n?? Updates matched by email address\n`;
          message += `?? Re-importing same emails will update existing records\n`;
        }

        if (errors && errors.length > 0) {
          message += `\n?? Issues found (first 3):\n`;
          message += errors
            .slice(0, 3)
            .map((error) => `? ${error}`)
            .join("\n");
          if (errors.length > 3) {
            message += `\n... and ${errors.length - 3} more issues`;
          }
        }

        success(message, 8000);
        await fetchCandidates(); // Refresh candidates list
      } else {
        let errorMessage = `? Import failed: ${result.message || "Unknown error"}\n\n`;

        if (result.data || result.errors) {
          const errors = result.data?.errors || result.errors || [];
          const imported = result.data?.imported || result.imported || 0;
          const skipped = result.data?.skipped || result.skipped || 0;
          const total_rows = result.data?.total_rows || result.total_rows || 0;

          errorMessage += `?? Processing Summary:\n`;
          errorMessage += `? Total rows: ${total_rows}\n`;
          errorMessage += `? Imported: ${imported}\n`;
          errorMessage += `? Skipped: ${skipped}\n\n`;

          if (errors && errors.length > 0) {
            errorMessage += `? First 5 errors:\n`;
            errorMessage += errors
              .slice(0, 5)
              .map((error) => `? ${error}`)
              .join("\n");
            if (errors.length > 5) {
              errorMessage += `\n... and ${errors.length - 5} more errors`;
            }
          }
        }

        error(errorMessage, 15000);
      }
    } catch (importError) {
      console.error("=== IMPORT ERROR ===");
      console.error("Error:", importError);
      console.error("Error message:", importError.message);

      let errorMessage = "? Import failed\n\n";

      if (
        importError.message.includes("Failed to fetch") ||
        importError.message.includes("NetworkError")
      ) {
        errorMessage += "?? Connection Error:\n";
        errorMessage += "? PHP backend server is not running\n";
        errorMessage += "? Check if localhost server is started\n";
        errorMessage += "? Verify API_BASE_URL is correct\n";
        errorMessage += `? Current API URL: ${API_BASE_URL}/import.php`;
      } else if (importError.message.includes("CORS")) {
        errorMessage += "?? CORS Error:\n";
        errorMessage += "? Add CORS headers to your PHP files\n";
        errorMessage += "? Check browser console for details";
      } else if (importError.message.includes("Server error")) {
        errorMessage += "?? Server Error:\n";
        errorMessage += `? ${importError.message}\n`;
        errorMessage += "? Check PHP error logs\n";
        errorMessage += "? Verify import.php file exists and is accessible";
      } else {
        errorMessage += `?? Technical Error:\n? ${importError.message}`;
      }

      error(errorMessage, 12000);
    } finally {
      setIsLoading(false);
    }
  };

  // File input handler
  const handleFileSelect = (event) => {
    const file = event.target.files[0];

    if (file) {
      console.log("=== FILE SELECT DEBUG ===");
      console.log("File selected:", file.name);
      console.log("File size:", file.size, "bytes");
      console.log("File type:", file.type);

      // Validate file type
      const allowedTypes = [
        "text/csv",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
      const allowedExtensions = [".csv", ".xlsx", ".xls"];
      const fileExtension = "." + file.name.split(".").pop().toLowerCase();

      if (
        !allowedTypes.includes(file.type) &&
        !allowedExtensions.includes(fileExtension)
      ) {
        warning("Please select a CSV or Excel file (.csv, .xlsx, .xls)");
        // Reset file input
        event.target.value = "";
        return;
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        warning("File size should not exceed 10MB");
        // Reset file input
        event.target.value = "";
        return;
      }

      // Start import
      console.log("Starting import for:", file.name);
      handleImport(file);

      // Reset file input so same file can be selected again
      event.target.value = "";
    }
  };

  // CSV Template download function
  const downloadTemplate = () => {
  const csvContent = `Status,plc_code,sap_id,candidate_name,candidate_phone,candidate_email,DOB,client,client_track,end_client,job_title,primary_skills,secondary_skills,start_date,end_date,sow_end_date,sow_end_month,extended_sow_date,contractor_status,project_city,project_state,project_country,pay_rate_currency,pay_rate,pay_rate_payment_basis,client_rate_currency,client_rate,client_rate_payment_basis,margin_value,contractor_vendor_name,vendor_contact_person,vendor_contact_number,vendor_contact_email,dal_name,dm_name,add_name,bu_head,BU Head Email ID,cal_name,passthrough_owner,passthrough_support,lead_recruiter,team_lead_name,recruiter_name,recruiter_emp_id,deal_pt_ptr,client_project_manager,client_resource_manager,LinkedIn URL,Certifications,account_manager_1,account_manager_2,account_manager_3,account_manager_4,account_manager_5,account_manager_6,account_manager_7,account_manager_8
Active,PLC001,SAP001,John Doe,+1-555-0101,john.doe@email.com,1990-05-15,TechCorp,Backend,Acme Inc,Senior Developer,"Python, Django","React, Node.js",01/20/2024,12/20/2024,12/20/2024,December,03/20/2025,C2C,Austin,TX,USA,USD,110,Hourly,USD,155,Hourly,29.0,AutoTech Solutions,Maria Garcia,+1-555-0778,maria.g@autotech.com,Chris Martinez,Anna Lee,John Davis,Patricia Johnson,patricia@vdart.com,Michael Chen,TechFlow Inc,GlobalTech Support,Jennifer Davis,Robert Wilson,Sarah Connor,EMP12345,DEAL001,Elon Zhang,Sarah Connor,https://linkedin.com/in/johndoe,"AWS,Python",alice@vdart.com,bob@vdart.com,charlie@vdart.com,david@vdart.com,emma@vdart.com,frank@vdart.com,grace@vdart.com,henry@vdart.com`;

  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "candidate_import_template.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);

  success(
    "Template downloaded!\n\n✅ Use MM/DD/YYYY format for all date fields.\n✅ Email is used to match existing candidates for updates.",
  );
};

  // More actions handlers
  // More actions handlers
  const handleMoreAction = async (candidate, action) => {
    switch (action) {
      case "reporting_info":
        setSelectedCandidate(candidate);
        setShowReportingInfoModal(true);
        break;
      case "duplicate":
        const duplicateData = { ...candidate };
        delete duplicateData.id;
        duplicateData.candidateName = `${candidate.candidateName} (Copy)`;
        duplicateData.plcCode = `${candidate.plcCode}_COPY`;
        duplicateData.sapId = `${candidate.sapId}_COPY`;
        handleAddCandidate(duplicateData);
        break;
      case "archive":
        handleEditCandidate(candidate.id, { ...candidate, status: "Archived" });
        break;
      case "export":
        window.open(`${API_BASE_URL}/export.php?format=csv`);
        break;
      case "activate":
        handleEditCandidate(candidate.id, { ...candidate, status: "Active" });
        break;
      case "deactivate":
        handleEditCandidate(candidate.id, { ...candidate, status: "Inactive" });
        break;
      case "email":
        await sendEmail("custom", candidate.id, {
          customSubject: `Regarding your contract at ${candidate.client}`,
          customMessage: `Dear ${candidate.candidateName},\n\nI hope this email finds you well.\n\n[Your message here]\n\nBest regards,\nCandidate Management Team`,
        });
        break;
      case "call":
        window.open(`tel:${candidate.candidatePhone}`);
        break;
      default:
        break;
    }
    setShowMoreActions(null);
    setShowRollOffMoreActions(null);
  };

  const ImportHelp = () => (
    <div className="relative group">
      <button className="ml-2 text-slate-400 hover:text-slate-600">
        <Info className="w-4 h-4" />
      </button>
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10 max-w-xs">
        <div className="text-left">
          <p className="font-medium mb-1">Supported Formats:</p>
          <p>? CSV files (.csv)</p>
          <p>? Excel files (.xlsx, .xls)</p>
          <p>? Maximum size: 10MB</p>
        </div>
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-900"></div>
      </div>
    </div>
  );
  // Import Button Component
  const ImportButton = () => (
    <div className="relative">
      <input
        type="file"
        accept=".csv,.xlsx,.xls" // UPDATED: Accept Excel files too
        onChange={handleFileSelect}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        id="csv-import"
      />
      <button className="flex items-center px-3 py-2 text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 text-sm">
        <Upload className="w-4 h-4 mr-2" />
        {isLoading ? "Importing..." : "Import CSV/Excel"} {/* UPDATED: Text */}
      </button>
    </div>
  );

  // Template Download Button Component
  const TemplateButton = () => (
    <button
      onClick={downloadTemplate}
      className="flex items-center px-3 py-2 text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 text-sm"
    >
      <Download className="w-4 h-4 mr-2" />
      Download Template
    </button>
  );

  // Calculate metrics for the main cards
  const activeCandidates = candidates.filter(
    (c) => c.status === "Active",
  ).length;
  const endingProjects = candidates.filter((c) => {
    const endDate = new Date(c.endDate);
    const today = new Date();
    const monthFromNow = new Date();
    monthFromNow.setMonth(today.getMonth() + 1);
    return endDate <= monthFromNow && endDate >= today;
  }).length;
  const extended = candidates.filter((c) => c.extendedSowDate).length;
  const recentlyOnboarded = recentlyOnboardedCandidates.length;

  // Filter and search candidates

  const totalPages = Math.ceil(filteredCandidates.length / itemsPerPage);
  const paginatedCandidates = filteredCandidates.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const StatCard = ({
    icon: Icon,
    title,
    value,
    subtitle,
    trend,
    color = "blue",
  }) => {
    const colorClasses = {
      blue: "bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200",
      green: "bg-gradient-to-r from-green-50 to-emerald-50 border-green-200",
      yellow: "bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200",
      red: "bg-gradient-to-r from-red-50 to-pink-50 border-red-200",
    };

    // Handle both string colors and hex colors for backward compatibility
    const cardBgClass =
      typeof color === "string" && colorClasses[color]
        ? colorClasses[color]
        : "bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200";

    return (
      <div
        className={`p-6 rounded-2xl border-2 shadow-lg hover:shadow-xl transition-all transform hover:scale-105 ${cardBgClass}`}
      >
        <div className="flex items-center justify-between">
          <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
            <Icon className="w-6 h-6 text-white" />
          </div>
          {trend && (
            <div className="flex items-center space-x-1">
              <TrendingUp className="w-3 h-3 text-green-500" />
              <span className="text-xs text-green-600 font-semibold">
                {trend}
              </span>
            </div>
          )}
        </div>
        <div className="mt-4">
          <div className="text-3xl font-bold text-slate-900 mb-1">
            <AnimatedCounter
              end={parseInt(value.toString().replace(/[^\d]/g, ""))}
            />
          </div>
          <div className="text-sm font-medium text-slate-600 mb-1">{title}</div>
          <div className="text-xs text-slate-500">{subtitle}</div>
        </div>
      </div>
    );
  };

  // Delete Confirmation Modal

  // More Actions Dropdown Component
  const MoreActionsDropdown = ({ candidate, onClose, position = "right" }) => (
    <div
      className={`absolute ${position === "right" ? "right-0" : "left-0"} top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 z-20`}
    >
      <div className="py-1">
        <button
          onClick={() => {
            handleReportingInfoClick(candidate);
            onClose();
          }}
          className="flex items-center w-full px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
        >
          <Users2 className="w-4 h-4 mr-3" />
          Update Reporting Info
        </button>

        <button
          onClick={() => {
            handleProjectDetailsClick(candidate);
            onClose();
          }}
          className="flex items-center w-full px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
        >
          <Briefcase className="w-4 h-4 mr-3" />
          Update Project Details
        </button>

        {/* ADD THIS HISTORY BUTTON HERE */}
        <button
          onClick={() => {
            setSelectedCandidateHistory(candidate);
            fetchCandidateHistory(candidate.id);
            setShowHistoryModal(true);
            onClose();
          }}
          className="flex items-center w-full px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
        >
          <Clock className="w-4 h-4 mr-3" />
          View History
        </button>

        <hr className="my-1" />
        <button
          onClick={async () => await handleMoreAction(candidate, "duplicate")}
          className="flex items-center w-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
        >
          <Copy className="w-4 h-4 mr-3" />
          Duplicate
        </button>
        <button
          onClick={async () => await handleMoreAction(candidate, "export")}
          className="flex items-center w-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
        >
          <Download className="w-4 h-4 mr-3" />
          Export Data
        </button>
        <button
          onClick={async () => await handleMoreAction(candidate, "email")}
          className="flex items-center w-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
        >
          <Mail className="w-4 h-4 mr-3" />
          Send Email
        </button>
        <button
          onClick={async () => await handleMoreAction(candidate, "call")}
          className="flex items-center w-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
        >
          <Phone className="w-4 h-4 mr-3" />
          Call
        </button>
        <hr className="my-1" />
        {candidate.status === "Active" ? (
          <button
            onClick={async () =>
              await handleMoreAction(candidate, "deactivate")
            }
            className="flex items-center w-full px-4 py-2 text-sm text-orange-600 hover:bg-orange-50"
          >
            <UserX className="w-4 h-4 mr-3" />
            Deactivate
          </button>
        ) : (
          <button
            onClick={async () => await handleMoreAction(candidate, "activate")}
            className="flex items-center w-full px-4 py-2 text-sm text-green-600 hover:bg-green-50"
          >
            <UserCheck className="w-4 h-4 mr-3" />
            Activate
          </button>
        )}
        <button
          onClick={async () => await handleMoreAction(candidate, "archive")}
          className="flex items-center w-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
        >
          <Archive className="w-4 h-4 mr-3" />
          Archive
        </button>
      </div>
    </div>
  );

  const CandidateDetailsModal = ({ candidate, onClose }) => {
    if (!candidate) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="bg-slate-800 px-6 py-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">
                  {candidate.candidateName}
                </h2>
                <p className="text-slate-300">
                  {candidate.jobTitle} at {candidate.client}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">
                  Basic Information
                </h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium text-slate-700">
                      PLC Code:
                    </span>{" "}
                    <span className="text-slate-600">{candidate.plcCode}</span>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">SAP ID:</span>{" "}
                    <span className="text-slate-600">{candidate.sapId}</span>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">Phone:</span>{" "}
                    <span className="text-slate-600">
                      {candidate.candidatePhone}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">
                      Personal Email:
                    </span>{" "}
                    <span className="text-slate-600">
                      {candidate.candidateEmail}
                    </span>
                  </div>
                  {candidate.workEmail && (
                    <div>
                      <span className="font-medium text-slate-700">
                        Work Email:
                      </span>{" "}
                      <span className="text-slate-600">
                        {candidate.workEmail}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">
                  Project Details
                </h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium text-slate-700">
                      Client Track:
                    </span>{" "}
                    <span className="text-slate-600">
                      {candidate.clientTrack}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">
                      End Client:
                    </span>{" "}
                    <span className="text-slate-600">
                      {candidate.endClient}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">
                      Location:
                    </span>{" "}
                    <span className="text-slate-600">
                      {candidate.projectCity}, {candidate.projectState},{" "}
                      {candidate.projectCountry}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">Status:</span>{" "}
                    <span className="text-slate-600">
                      {candidate.contractorStatus}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {(candidate.reportingManagerName ||
              candidate.reportingManagerEmail) && (
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">
                  Reporting Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {candidate.reportingManagerName && (
                    <div>
                      <span className="font-medium text-slate-700">
                        Reporting Manager:
                      </span>
                      <p className="text-slate-600 mt-1">
                        {candidate.reportingManagerName}
                      </p>
                    </div>
                  )}
                  {candidate.reportingManagerEmail && (
                    <div>
                      <span className="font-medium text-slate-700">
                        Manager Email:
                      </span>
                      <p className="text-slate-600 mt-1">
                        {candidate.reportingManagerEmail}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">
                Skills
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-slate-700">
                    Primary Skills:
                  </span>
                  <p className="text-slate-600 mt-1">
                    {candidate.primarySkills}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-slate-700">
                    Secondary Skills:
                  </span>
                  <p className="text-slate-600 mt-1">
                    {candidate.secondarySkills}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">
                Contract Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-medium text-slate-700">Duration:</span>
                  <p className="text-slate-600 mt-1">
                    {candidate.startDate} to {candidate.endDate}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-slate-700">Pay Rate:</span>
                  <p className="text-slate-600 mt-1">
                    {candidate.payRateCurrency} {candidate.payRate}/
                    {candidate.payRatePaymentBasis}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-slate-700">
                    Client Rate:
                  </span>
                  <p className="text-slate-600 mt-1">
                    {candidate.clientRateCurrency} {candidate.clientRate}/
                    {candidate.clientRatePaymentBasis}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">
                Team & Management
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <div>
                    <span className="font-medium text-slate-700">DAL:</span>{" "}
                    <span className="text-slate-600">{candidate.dalName}</span>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">DM:</span>{" "}
                    <span className="text-slate-600">{candidate.dmName}</span>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">ADD:</span>{" "}
                    <span className="text-slate-600">{candidate.addName}</span>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">BU Head:</span>{" "}
                    <span className="text-slate-600">{candidate.buHead}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div>
                    <span className="font-medium text-slate-700">
                      Client PM:
                    </span>{" "}
                    <span className="text-slate-600">
                      {candidate.clientProjectManager}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">
                      Client RM:
                    </span>{" "}
                    <span className="text-slate-600">
                      {candidate.clientResourceManager}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">
                Vendor Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <div>
                    <span className="font-medium text-slate-700">Vendor:</span>{" "}
                    <span className="text-slate-600">
                      {candidate.contractorVendorName}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">
                      Contact Person:
                    </span>{" "}
                    <span className="text-slate-600">
                      {candidate.vendorContactPerson}
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div>
                    <span className="font-medium text-slate-700">Phone:</span>{" "}
                    <span className="text-slate-600">
                      {candidate.vendorContactNumber}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">Email:</span>{" "}
                    <span className="text-slate-600">
                      {candidate.vendorContactEmail}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ADD THIS ENTIRE COMPONENT HERE ??
  const ImportInstructionsModal = ({ onClose }) => (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Import Instructions</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-bold text-blue-900 mb-2">
              ?? Email-Based Updates
            </h3>
            <p className="text-blue-800 text-sm">
              The system uses email addresses as unique identifiers. When you
              re-import a file:
            </p>
            <ul className="list-disc list-inside text-blue-800 text-sm mt-2 space-y-1">
              <li>
                <strong>Same email</strong> = Updates existing candidate
              </li>
              <li>
                <strong>New email</strong> = Creates new candidate
              </li>
              <li>
                <strong>No duplicates</strong> = One record per email address
              </li>
            </ul>
          </div>

          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h3 className="font-bold text-green-900 mb-2">
              ? Recommended Workflow
            </h3>
            <ol className="list-decimal list-inside text-green-800 text-sm space-y-1">
              <li>Download the template</li>
              <li>Fill in candidate data (email is required)</li>
              <li>Import the file</li>
              <li>Make changes in the same file</li>
              <li>Re-import to update existing records</li>
            </ol>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <h3 className="font-bold text-yellow-900 mb-2">
              ?? Important Notes
            </h3>
            <ul className="list-disc list-inside text-yellow-800 text-sm space-y-1">
              <li>Email address is required for all candidates</li>
              <li>Use consistent email addresses for updates</li>
              <li>Empty cells won't overwrite existing data</li>
              <li>File size limit: 10MB</li>
              <li>Supported formats: CSV, XLSX, XLS</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside dropdown
      if (!event.target.closest(".relative")) {
        setShowMoreActions(null);
        setShowRollOffMoreActions(null);
        setShowExportDropdown(false);
      }

      // Check if click is outside profile dropdown
      if (
        !event.target.closest(".profile-dropdown") &&
        !event.target.closest("[data-profile-trigger]")
      ) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Delete Confirmation Modal
  const DeleteConfirmationModal = ({ candidate, onConfirm, onCancel }) => {
    if (!candidate) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Confirm Deletion
                </h3>
                <p className="text-sm text-slate-600">
                  This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-slate-700 mb-2">
                Are you sure you want to delete the following candidate?
              </p>
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-medium text-slate-900">
                  {candidate.candidateName}
                </p>
                <p className="text-sm text-slate-600">
                  {candidate.candidateEmail}
                </p>
                <p className="text-sm text-slate-600">
                  {candidate.client} ? {candidate.jobTitle}
                </p>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={onCancel}
                className="flex-1 px-4 py-2 text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => onConfirm(candidate.id)}
                disabled={isLoading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {isLoading ? "Deleting..." : "Delete Candidate"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Add filtering and bulk update functions
  const getFilteredRollOffCandidates = () => {
    return rollOffCandidates.filter((candidate) => {
      // ? FIXED: Use extension_requested column properly
      // 1 = Extended, 2 = Not Extended, 0 or null = No decision yet
      const extensionRequested = candidate.extension_requested;

      let matchesExtension = true;

      if (extensionFilter === "extended") {
        matchesExtension =
          extensionRequested === 1 || extensionRequested === "1";
      } else if (extensionFilter === "not_extended") {
        matchesExtension =
          extensionRequested === 2 || extensionRequested === "2";
      }
      // For 'all', matchesExtension remains true

      return matchesExtension;
    });
  };

  const handleBulkUpdate = async (updateData) => {
    try {
      setIsLoading(true);

      const promises = selectedRollOffCandidates.map(async (candidateId) => {
        const candidate = candidates.find((c) => c.id === candidateId);
        if (!candidate) return;

        // ? FIXED: Reset extension_requested to 0 for all date updates
        const finalUpdateData = {
          ...updateData,
          extension_requested: 0, // Always reset to no decision
          extensionRequested: 0, // Reset camelCase version too
        };

        // Database field name mappings
        if (updateData.sowEndDate) {
          finalUpdateData.sow_end_date = updateData.sowEndDate;
        }

        if (updateData.endDate) {
          finalUpdateData.end_date = updateData.endDate;
        }

        const updatedCandidateData = {
          id: candidateId,
          ...candidate,
          ...finalUpdateData, // Use the enhanced data
        };

        console.log("?? Bulk update sending data:", updatedCandidateData);

        // Update candidate
        const response = await fetch(`${API_BASE_URL}/update_candidate.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(
            withCandidateUpdateMeta(candidateId, updatedCandidateData),
          ),
        });

        if (!response.ok) {
          throw new Error(`Failed to update ${candidate.candidateName}`);
        }

        // Add history record for each field that changed
        if (
          updateData.sowEndDate &&
          updateData.sowEndDate !== candidate.sowEndDate
        ) {
          await addToHistory(
            candidateId,
            "sow_extension",
            "sowEndDate",
            candidate.sowEndDate,
            updateData.sowEndDate,
            "Bulk SOW extension",
            `Extended via bulk update in roll-off management`,
          );
        }

        if (updateData.endDate && updateData.endDate !== candidate.endDate) {
          await addToHistory(
            candidateId,
            "end_date_update",
            "endDate",
            candidate.endDate,
            updateData.endDate,
            "Bulk end date update",
            `End date updated via bulk update in roll-off management`,
          );
        }

        if (updateData.status && updateData.status !== candidate.status) {
          await addToHistory(
            candidateId,
            "status_change",
            "status",
            candidate.status,
            updateData.status,
            "Bulk status change",
            `Status changed via bulk update in roll-off management`,
          );
        }

        return await response.json();
      });

      await Promise.all(promises);

      success(
        `Successfully updated ${selectedRollOffCandidates.length} candidates with proper extension_status!`,
      );
      setSelectedRollOffCandidates([]);
      setShowBulkUpdateModal(false);
      await fetchCandidates();
    } catch (error) {
      console.error("Bulk update error:", error);
      error(`Failed to update candidates: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Add this new function for PHPMailer email sending
  // Enhanced sendEmail function with better debugging
  const sendEmail = async (emailType, candidateId, additionalData = {}) => {
    try {
      console.log("?? Starting sendEmail with:", {
        emailType,
        candidateId,
        additionalData,
      });
      console.log("?? SEND EMAIL CALLED:", {
        emailType: emailType,
        candidateId: candidateId,
        recipients: additionalData.recipients,
        additionalData: additionalData,
      });
      // Validate inputs
      if (!emailType) {
        throw new Error("Email type is required");
      }

      if (!candidateId) {
        throw new Error("Candidate ID is required");
      }

      const senderEmail = currentUser?.email || "system@vdartinc.com";
      const senderName = currentUser
        ? `${currentUser.first_name} ${currentUser.last_name}`
        : "System Admin";

      const payload = {
        emailType,
        candidateId,
        senderEmail, // ADD THIS
        senderName, // ADD THIS
        sentBy: currentUser?.id, // ADD THIS
        ...additionalData,
      };

      // console.log('?? Sending payload:', payload);

      // Make the request with enhanced error handling
      const response = await fetch(`${API_BASE_URL}/send_email.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      console.log("?? Response status:", response.status);
      console.log(
        "?? Response headers:",
        Object.fromEntries(response.headers.entries()),
      );

      // Check if response is OK
      if (!response.ok) {
        const errorText = await response.text();
        console.error("? Email API HTTP error:", response.status, errorText);
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      // Parse response
      const responseText = await response.text();
      console.log("?? Raw response:", responseText);

      let result;
      try {
        result = JSON.parse(responseText);
        console.log("? Parsed result:", result);
      } catch (parseError) {
        console.error("? JSON parse error:", parseError);
        throw new Error(
          `Invalid response from server: ${responseText.substring(0, 100)}...`,
        );
      }

      // Check if email was successful
      if (result.success) {
        console.log("? Email sent successfully:", result);
        return {
          success: true,
          recipient:
            result.details?.recipient ||
            additionalData.candidateEmail ||
            "Unknown",
          subject: result.details?.subject || "Email Sent",
          type: emailType,
          message: result.message,
        };
      } else {
        console.error("? Email send failed:", result.message);
        throw new Error(result.message || "Unknown email error");
      }
    } catch (error) {
      console.error("? Email send error:", error);

      // Provide user-friendly error messages
      let userMessage = error.message;

      if (
        error.message.includes("Failed to fetch") ||
        error.message.includes("NetworkError")
      ) {
        userMessage =
          "Network error: Unable to connect to email server. Please check if the backend is running.";
      } else if (error.message.includes("CORS")) {
        userMessage =
          "CORS error: Please check CORS headers in your PHP configuration.";
      } else if (error.message.includes("Email endpoint is working")) {
        userMessage =
          "Email system is online but request failed. Check console for details.";
      }

      throw new Error(userMessage);
    }
  };

  const handleForwardFormToCandidate = async (candidate) => {
    try {
      console.log(
        "?? Starting form forward process for:",
        candidate.candidateName,
      );

      // Update the local candidate state immediately for better UX
      setCandidates((prevCandidates) =>
        prevCandidates.map((c) =>
          c.id === candidate.id
            ? {
                ...c,
                project_form_sent_at: new Date()
                  .toISOString()
                  .slice(0, 19)
                  .replace("T", " "),
                project_form_reminder_count: 0,
              }
            : c,
        ),
      );

      // Email sending (already working)
      const emailResponse = await fetch(
        `${API_BASE_URL}/send_project_form.php`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            candidateId: candidate.id,
            candidateName: candidate.candidateName,
            candidateEmail: candidate.candidateEmail,
            client: candidate.client,
            jobTitle: candidate.jobTitle,
            formSentAt: new Date().toISOString(),
          }),
        },
      );

      if (!emailResponse.ok) {
        // Revert the optimistic update on error
        setCandidates((prevCandidates) =>
          prevCandidates.map((c) =>
            c.id === candidate.id
              ? { ...c, project_form_sent_at: candidate.project_form_sent_at }
              : c,
          ),
        );
        throw new Error(`Email sending failed: HTTP ${emailResponse.status}`);
      }

      console.log("? Email sent successfully");

      // DATABASE UPDATE - FIXED DATE FORMAT
      const currentDateTime = new Date()
        .toISOString()
        .slice(0, 19)
        .replace("T", " ");

      const updateData = {
        id: candidate.id,
        project_form_sent_at: currentDateTime, // ? Correct MySQL format
        project_form_reminder_count: 0,
        project_form_last_reminder_at: null,
      };

      console.log("?? Sending update request:", updateData);

      const updateResponse = await fetch(
        `${API_BASE_URL}/update_candidate.php`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(
            withCandidateUpdateMeta(candidate.id, updateData),
          ),
        },
      );

      const responseText = await updateResponse.text();
      console.log("?? Update response:", responseText);

      if (!updateResponse.ok) {
        console.error("? Database update failed:", responseText);
        // Revert the optimistic update on error
        setCandidates((prevCandidates) =>
          prevCandidates.map((c) =>
            c.id === candidate.id
              ? { ...c, project_form_sent_at: candidate.project_form_sent_at }
              : c,
          ),
        );
        throw new Error(`Database update failed: ${responseText}`);
      }

      success(`Form forwarded to ${candidate.candidateName} successfully!`);

      // Refresh candidates list to ensure consistency
      await fetchCandidates();
    } catch (error) {
      console.error("? Error in handleForwardFormToCandidate:", error);
      error(`Failed to forward form: ${error.message}`);
    }
  };

  // ?? ALSO FIX THE REMINDER FUNCTION:
  const handleSendFormReminder = async (candidate) => {
    try {
      const currentDateTime = new Date().toISOString(); // Keep as ISO string
      const newReminderCount = (candidate.project_form_reminder_count || 0) + 1;

      // Update local state immediately for better UX
      setCandidates((prevCandidates) =>
        prevCandidates.map((c) =>
          c.id === candidate.id
            ? {
                ...c,
                project_form_reminder_count: newReminderCount,
                project_form_last_reminder_at: currentDateTime,
              }
            : c,
        ),
      );

      // First send reminder email
      const emailData = {
        emailType: "project_form_reminder",
        candidateId: candidate.id,
        candidateName: candidate.candidateName,
        candidateEmail: candidate.candidateEmail,
        reminderCount: newReminderCount,
      };

      const emailResponse = await fetch(`${API_BASE_URL}/send_email.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(emailData),
      });

      if (!emailResponse.ok) {
        // Revert optimistic update on error
        setCandidates((prevCandidates) =>
          prevCandidates.map((c) =>
            c.id === candidate.id
              ? {
                  ...c,
                  project_form_reminder_count:
                    candidate.project_form_reminder_count,
                  project_form_last_reminder_at:
                    candidate.project_form_last_reminder_at,
                }
              : c,
          ),
        );
        throw new Error(`Reminder email failed: ${emailResponse.status}`);
      }

      // Then update database with CORRECT field names
      const updateData = {
        id: candidate.id,
        project_form_reminder_count: newReminderCount, // ? Correct field name
        project_form_last_reminder_at: currentDateTime, // ? Correct field name
      };

      const updateResponse = await fetch(
        `${API_BASE_URL}/update_candidate.php`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(
            withCandidateUpdateMeta(candidate.id, updateData),
          ),
        },
      );

      if (!updateResponse.ok) {
        const errorText = await updateResponse.text();
        // Revert optimistic update on error
        setCandidates((prevCandidates) =>
          prevCandidates.map((c) =>
            c.id === candidate.id
              ? {
                  ...c,
                  project_form_reminder_count:
                    candidate.project_form_reminder_count,
                  project_form_last_reminder_at:
                    candidate.project_form_last_reminder_at,
                }
              : c,
          ),
        );
        throw new Error(`Database update failed: ${errorText}`);
      }

      const result = await updateResponse.json();
      console.log("? Reminder sent and tracked:", result);

      success(
        `Reminder #${newReminderCount} sent to ${candidate.candidateName}`,
      );
      await fetchCandidates();
    } catch (error) {
      console.error("? Error sending reminder:", error);
      error(`Failed to send reminder: ${error.message}`);
    }
  };

  // ?? ALSO FIX THE COMPLETION FUNCTION:
  const handleMarkFormCompleted = async (candidate) => {
    try {
      const currentDateTime = new Date().toISOString(); // Keep as ISO string

      // Update local state immediately for better UX
      setCandidates((prevCandidates) =>
        prevCandidates.map((c) =>
          c.id === candidate.id
            ? { ...c, project_form_completed_at: currentDateTime }
            : c,
        ),
      );

      const updateData = {
        id: candidate.id,
        project_form_completed_at: currentDateTime, // ? Correct field name
      };

      const response = await fetch(`${API_BASE_URL}/update_candidate.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(withCandidateUpdateMeta(candidate.id, updateData)),
      });

      if (!response.ok) {
        const errorText = await response.text();
        // Revert optimistic update on error
        setCandidates((prevCandidates) =>
          prevCandidates.map((c) =>
            c.id === candidate.id
              ? {
                  ...c,
                  project_form_completed_at:
                    candidate.project_form_completed_at,
                }
              : c,
          ),
        );
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      console.log("? Form marked as completed:", result);

      success(`Form marked as completed for ${candidate.candidateName}`);
      await fetchCandidates();
    } catch (error) {
      console.error("? Error marking form as completed:", error);
      error(`Failed to mark form as completed: ${error.message}`);
    }
  };

  // Add these functions AFTER the sendEmail function (around line 1076)
  const sendAccountManagerFormAlert = async (overdueFormCandidates) => {
    try {
      console.log("?? Sending 24-hour alerts to account managers...");

      // Group candidates by their account managers
      const managerGroups = {};

      overdueFormCandidates.forEach((candidate) => {
        const managers = [
          candidate.account_manager_1,
          candidate.account_manager_2,
          candidate.account_manager_3,
          candidate.account_manager_4,
          candidate.account_manager_5,
          candidate.account_manager_6,
          candidate.account_manager_7,
          candidate.account_manager_8,
        ].filter((email) => email && email.trim());

        managers.forEach((manager) => {
          if (!managerGroups[manager]) {
            managerGroups[manager] = [];
          }
          managerGroups[manager].push({
            ...candidate,
            hoursOverdue: Math.floor(
              (new Date() - new Date(candidate.project_form_sent_at)) /
                (1000 * 60 * 60),
            ),
          });
        });
      });

      console.log(
        `?? Sending alerts to ${Object.keys(managerGroups).length} account managers`,
      );

      // Send personalized alerts to each account manager
      for (const [managerEmail, candidates] of Object.entries(managerGroups)) {
        try {
          await sendEmail("project_form_24hr_alert", "alert", {
            managerEmail: managerEmail,
            candidates: candidates,
            totalCount: candidates.length,
            subject: `URGENT: ${candidates.length} candidate(s) have not completed project forms (24+ hours overdue)`,
          });

          console.log(
            `? Alert sent to ${managerEmail} for ${candidates.length} candidates`,
          );
        } catch (emailError) {
          console.error(
            `? Failed to send alert to ${managerEmail}:`,
            emailError,
          );
        }
      }

      success(
        `24-hour alerts sent to ${Object.keys(managerGroups).length} account managers`,
      );
    } catch (error) {
      console.error("? Error sending account manager alerts:", error);
      error("Failed to send account manager alerts");
    }
  };

  const checkAndSendProjectFormReminders = async () => {
    const now = new Date();
    const currentHour = now.getHours();

    // Only run at 9 AM daily
    if (currentHour !== 9) return;

    console.log("?? Checking for overdue project forms (24hr rule)...");

    try {
      // Find candidates who were sent forms more than 24 hours ago and haven't completed them
      const overdueFormCandidates = candidates.filter((candidate) => {
        // Must have form sent but not completed
        if (
          !candidate.project_form_sent_at ||
          candidate.project_form_completed_at
        )
          return false;

        const formSentDate = new Date(candidate.project_form_sent_at);
        const hoursElapsed = (now - formSentDate) / (1000 * 60 * 60); // Hours since form sent

        // Check if 24+ hours have passed
        const isOverdue = hoursElapsed >= 24;

        // Check if we haven't already sent an alert today
        const lastAlertDate = candidate.project_form_last_reminder_at
          ? new Date(candidate.project_form_last_reminder_at).toDateString()
          : null;
        const todayString = now.toDateString();
        const alertAlreadySentToday = lastAlertDate === todayString;

        return isOverdue && !alertAlreadySentToday;
      });

      console.log(
        `?? Found ${overdueFormCandidates.length} candidates with overdue forms (24+ hours)`,
      );

      if (overdueFormCandidates.length > 0) {
        // Send alerts to account managers immediately
        await sendAccountManagerFormAlert(overdueFormCandidates);

        // Update the reminder timestamp for each candidate
        for (const candidate of overdueFormCandidates) {
          await fetch(`${API_BASE_URL}/update_candidate.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(
              withCandidateUpdateMeta(candidate.id, {
                id: candidate.id,
                project_form_last_reminder_at: now.toISOString(),
                project_form_reminder_count:
                  (candidate.project_form_reminder_count || 0) + 1,
              }),
            ),
          });
        }

        success(
          `Sent alerts to account managers for ${overdueFormCandidates.length} overdue project forms`,
        );
      }
    } catch (error) {
      console.error("? Error in 24-hour reminder check:", error);
    }
  };

  // Test function for debugging
  const testEmailEndpoint = async () => {
    try {
      console.log("?? Testing email endpoint...");

      const response = await fetch(`${API_BASE_URL}/send_email.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          emailType: "test",
          candidateId: "1",
        }),
      });

      const result = await response.json();
      console.log("?? Test result:", result);
      return result;
    } catch (error) {
      console.error("?? Test failed:", error);
      return { success: false, error: error.message };
    }
  };

  const addToHistory = async (
    candidateId,
    actionType,
    fieldName,
    oldValue,
    newValue,
    reason = "",
    comments = "",
  ) => {
    try {
      // Get candidate details
      const candidate = candidates.find((c) => c.id === candidateId);

      // ?? ENHANCED: Better field name display mapping
      const fieldDisplayNames = {
        plcCode: "PLC Code",
        sapId: "SAP ID",
        candidateName: "Candidate Name",
        candidatePhone: "Phone Number",
        candidateEmail: "Email Address",
        workEmail: "Work Email",
        client: "Client",
        jobTitle: "Job Title",
        primarySkills: "Primary Skills",
        secondarySkills: "Secondary Skills",
        startDate: "Start Date",
        endDate: "End Date",
        sowEndDate: "SOW End Date",
        projectCity: "Project City",
        projectState: "Project State",
        payRate: "Pay Rate",
        clientRate: "Client Rate",
        marginValue: "Margin %",
        contractorStatus: "Contract Status",
        dalName: "DAL Name",
        dmName: "DM Name",
        addName: "ADD Name",
        buHead: "BU Head",
        status: "Status",
      };

      const displayFieldName = fieldDisplayNames[fieldName] || fieldName;

      console.log("?? Adding to history:", {
        candidate_id: candidateId,
        candidate_name: candidate?.candidateName || "",
        action_type: actionType,
        field_name: fieldName,
        display_field_name: displayFieldName,
        old_value: oldValue,
        new_value: newValue,
        change_reason: reason,
        change_comments:
          comments ||
          `${displayFieldName} updated from "${oldValue}" to "${newValue}"`,
      });

      const response = await fetch(
        `${API_BASE_URL}/add_candidate_history.php`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            candidate_id: candidateId,
            candidate_name: candidate?.candidateName || "",
            candidate_email: candidate?.candidateEmail || "",
            action_type: actionType,
            field_name: fieldName,
            old_value: String(oldValue || ""),
            new_value: String(newValue || ""),
            change_reason: reason,
            change_comments:
              comments ||
              `${displayFieldName} updated from "${oldValue || "Not set"}" to "${newValue}"`,
            created_by: currentUser
              ? `${currentUser.first_name} ${currentUser.last_name}`
              : "Unknown User",
            user_id: currentUser ? currentUser.id : null,
            user_role: currentUser ? currentUser.role : null,
          }),
        },
      );

      const result = await response.json();
      if (!result.success) {
        console.error("Failed to add history:", result.message);
      } else {
        console.log(
          "? History record added successfully for:",
          displayFieldName,
        );
      }
    } catch (error) {
      console.error("Error adding history:", error);
    }
  };

  const fetchCandidateHistory = async (candidateId) => {
    try {
      setHistoryLoading(true);
      console.log("?? Fetching history for candidate:", candidateId); // ADD THIS

      const response = await fetch(
        `${API_BASE_URL}/get_candidate_history.php?candidate_id=${candidateId}`,
      );
      const result = await response.json();

      console.log("?? History API response:", result); // ADD THIS

      if (result.success) {
        setCandidateHistory(result.data);
        console.log("? History set:", result.data); // ADD THIS
      } else {
        error("Failed to fetch candidate history");
      }
    } catch (err) {
      console.error("Error fetching history:", err);
      error("Failed to fetch candidate history");
    } finally {
      setHistoryLoading(false);
    }
  };

  // TEMPORARY DEBUG VERSION - Replace your handleBulkUpdateWithHistory with this to test
  const handleBulkUpdateWithHistory = async (updateData) => {
    try {
      setIsLoading(true);

      const promises = selectedRollOffCandidates.map(async (candidateId) => {
        const candidate = candidates.find((c) => c.id === candidateId);
        if (!candidate) return;

        // ?? MINIMAL UPDATE TEST - Only send essential fields + the update
        const minimalUpdateData = {
          id: candidateId,
          candidateName: candidate.candidateName || "",
          candidateEmail: candidate.candidateEmail || "",
          status: candidate.status || "Active",
          ...updateData, // Add only the fields we're trying to update
        };

        console.log(
          `?? TESTING minimal update for ${candidate.candidateName}:`,
          minimalUpdateData,
        );

        // Update candidate first
        const response = await fetch(`${API_BASE_URL}/update_candidate.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(
            withCandidateUpdateMeta(candidateId, minimalUpdateData),
          ),
        });

        console.log(`?? Response for ${candidate.candidateName}:`, {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`? FULL ERROR for ${candidate.candidateName}:`, {
            status: response.status,
            statusText: response.statusText,
            errorBody: errorText,
            sentData: minimalUpdateData,
          });

          // Try to parse error response if it's JSON
          try {
            const errorJson = JSON.parse(errorText);
            console.error(`?? Parsed error:`, errorJson);
          } catch (e) {
            console.error(`?? Raw error text:`, errorText);
          }

          throw new Error(
            `Failed to update ${candidate.candidateName}: ${response.status} - ${errorText}`,
          );
        }

        const result = await response.json();
        console.log(`? SUCCESS for ${candidate.candidateName}:`, result);

        return result;
      });

      await Promise.all(promises);

      success(
        `Successfully updated ${selectedRollOffCandidates.length} candidates!`,
      );
      setSelectedRollOffCandidates([]);
      setShowBulkUpdateModal(false);
      await fetchCandidates();
    } catch (err) {
      console.error("Bulk update error:", err);
      error(`Failed to update candidates: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ? FIX: Update your handleRollOffUpdateWithHistory function

  const handleRollOffUpdateWithHistory = async (candidateId, rollOffData) => {
    try {
      setIsLoading(true);

      const currentCandidate = candidates.find((c) => c.id === candidateId);
      if (!currentCandidate) {
        throw new Error("Candidate not found");
      }

      let updatedData = { id: candidateId };

      // ? FEEDBACK-ONLY UPDATE
      if (rollOffData.enableFeedbackTracking === true) {
        updatedData = {
          id: candidateId,
          clientRating: rollOffData.clientRating,
          clientFeedback: rollOffData.clientFeedback,
          improvementArea1: rollOffData.improvementArea1,
          improvementArea2: rollOffData.improvementArea2,
          improvementArea3: rollOffData.improvementArea3,
          enableFeedbackTracking: true,
          enableExtensionStatusCalc: false,
          enableRDForwarding: false,
        };

        console.log("?? FEEDBACK UPDATE - Clean payload:", updatedData);
      }
      // ? EXTENSION-ONLY UPDATE
      else if (
        rollOffData.enableExtensionStatusCalc === true ||
        rollOffData.enableRDForwarding === true
      ) {
        updatedData = {
          id: candidateId,
          contactedWith: rollOffData.contactedWith || "",
          extensionRequested: rollOffData.extensionRequested,
          pocName: rollOffData.pocName || "",
          extensionDate: rollOffData.extensionDate || "",
          extensionDuration: rollOffData.extensionDuration || "",
          extensionComments: rollOffData.extensionComments || "",
          noExtensionReason: rollOffData.noExtensionReason || "",
          pendingExtensionReason: rollOffData.pendingExtensionReason || "",
          // ? CRITICAL: Include email trigger flags
          enableExtensionStatusCalc: true,
          enableRDForwarding: rollOffData.extensionRequested === false,
          enableFeedbackTracking: false,
          // ? ADD: Include email trigger data for PHP
          sentToRD: rollOffData.extensionRequested === false,
          sentToRDDate:
            rollOffData.extensionRequested === false
              ? new Date().toISOString().slice(0, 19).replace("T", " ")
              : null,
        };

        console.log("?? EXTENSION UPDATE - Clean payload:", updatedData);
      }

      // ? FIXED: SOW Extension Logic - ONLY update extension_status when dates change
      if (
        !rollOffData.enableFeedbackTracking &&
        rollOffData.extensionDate &&
        rollOffData.extensionDate !== currentCandidate.sowEndDate
      ) {
        updatedData.sowEndDate = rollOffData.extensionDate;
        updatedData.sow_end_date = rollOffData.extensionDate;

        // ? ONLY SET extension_status when extending SOW date
        updatedData.extensionStatus = "Yes";
        updatedData.extension_status = "Yes";

        await addToHistory(
          candidateId,
          "sow_extension",
          "sowEndDate",
          currentCandidate.sowEndDate || "Not set",
          rollOffData.extensionDate,
          rollOffData.extensionComments || "SOW extension",
          `SOW extended from ${currentCandidate.sowEndDate || "Not set"} to ${rollOffData.extensionDate}. Reason: ${rollOffData.extensionComments || "Not specified"}`,
        );
      }

      // ? FIXED: End Date Logic - ONLY update extension_status when setting end date
      if (
        !rollOffData.enableFeedbackTracking &&
        rollOffData.endDate &&
        rollOffData.endDate !== currentCandidate.endDate
      ) {
        updatedData.endDate = rollOffData.endDate;
        updatedData.end_date = rollOffData.endDate;

        // ? ONLY SET extension_status when ending contract
        updatedData.extensionStatus = "No";
        updatedData.extension_status = "No";
        updatedData.status = "Inactive";

        await addToHistory(
          candidateId,
          "end_date_update",
          "endDate",
          currentCandidate.endDate || "Not set",
          rollOffData.endDate,
          "Contract end date updated",
          `End date updated from ${currentCandidate.endDate || "Not set"} to ${rollOffData.endDate}`,
        );
      }

      // Track client rating changes (only for feedback updates)
      if (
        rollOffData.enableFeedbackTracking &&
        rollOffData.clientRating &&
        rollOffData.clientRating !== currentCandidate.clientRating
      ) {
        await addToHistory(
          candidateId,
          "general_update",
          "clientRating",
          currentCandidate.clientRating || "Not rated",
          rollOffData.clientRating,
          "Client rating update",
          `Client rating updated to ${rollOffData.clientRating}/5`,
        );
      }

      // Track client feedback changes (only for feedback updates)
      if (
        rollOffData.enableFeedbackTracking &&
        rollOffData.clientFeedback &&
        rollOffData.clientFeedback !== currentCandidate.clientFeedback
      ) {
        await addToHistory(
          candidateId,
          "general_update",
          "clientFeedback",
          currentCandidate.clientFeedback ? "Previous feedback" : "No feedback",
          "Feedback provided",
          "Client feedback update",
          `Client feedback provided: ${rollOffData.clientFeedback.substring(0, 100)}${rollOffData.clientFeedback.length > 100 ? "..." : ""}`,
        );
      }

      // Track extension request changes (only for extension updates)
      if (
        !rollOffData.enableFeedbackTracking &&
        rollOffData.extensionRequested !== undefined &&
        rollOffData.extensionRequested !== currentCandidate.extensionRequested
      ) {
        // ? FIXED: Proper mapping for extension_requested values
        // 0 = Pending, 1 = Yes, 2 = No
        const getExtensionText = (value) => {
          if (value === 1 || value === "1") return "Yes";
          if (value === 2 || value === "2") return "No";
          return "Pending";
        };

        const oldValue = getExtensionText(
          currentCandidate.extensionRequested ||
            currentCandidate.extension_requested,
        );
        const newValue = getExtensionText(rollOffData.extensionRequested);

        await addToHistory(
          candidateId,
          "status_change",
          "extensionRequested",
          oldValue,
          newValue,
          rollOffData.extensionRequested === 1
            ? "Extension requested"
            : "Extension not requested",
          rollOffData.extensionRequested === 1
            ? `Extension requested with comments: ${rollOffData.extensionComments || "No comments"}`
            : `Extension not requested. Reason: ${rollOffData.noExtensionReason || "Not specified"}`,
        );
      }

      // Track extension status changes (only for extension updates)
      if (
        !rollOffData.enableFeedbackTracking &&
        rollOffData.extensionStatus &&
        rollOffData.extensionStatus !== currentCandidate.extensionStatus
      ) {
        await addToHistory(
          candidateId,
          "status_change",
          "extensionStatus",
          currentCandidate.extensionStatus || "Not set",
          rollOffData.extensionStatus,
          "Extension status update",
          `Extension status changed to: ${rollOffData.extensionStatus}`,
        );
      }

      // RD forwarding history (only for extension updates)
      if (
        !rollOffData.enableFeedbackTracking &&
        rollOffData.extensionRequested === false
      ) {
        updatedData.sentToRD = true;
        updatedData.rdReason =
          rollOffData.noExtensionReason || "Extension not requested";
        updatedData.sentToRDDate = new Date()
          .toISOString()
          .slice(0, 19)
          .replace("T", " ");

        // Add history for RD forwarding
        await addToHistory(
          candidateId,
          "status_change",
          "sentToRD",
          "No",
          "Yes",
          rollOffData.noExtensionReason || "Extension not requested",
          `Candidate forwarded to RD team. Reason: ${rollOffData.noExtensionReason || "Not specified"}`,
        );
      }

      const response = await fetch(`${API_BASE_URL}/update_candidate.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(withCandidateUpdateMeta(candidateId, updatedData)),
      });

      const result = await response.json();

      if (result.success) {
        console.log(
          "?? DEBUG - rollOffData.extensionRequested:",
          rollOffData.extensionRequested,
        );
        console.log(
          "?? DEBUG - rollOffData.enableFeedbackTracking:",
          rollOffData.enableFeedbackTracking,
        );
        console.log(
          "?? DEBUG - rollOffData.enableRDForwarding:",
          rollOffData.enableRDForwarding,
        );

        // REMOVED: The separate forwardToRDTeam call - let update_candidate.php handle it
        if (
          rollOffData.extensionRequested === 2 ||
          rollOffData.enableRDForwarding === true
        ) {
          success(
            "Extension decision updated and candidate forwarded to RD team!",
          );
        } else if (rollOffData.enableFeedbackTracking) {
          success("Client feedback updated successfully!");
        } else if (rollOffData.extensionDate) {
          success("Extension date updated and extension_status set to Yes!");
        } else if (rollOffData.endDate) {
          success("End date updated and extension_status set to No!");
        } else if (rollOffData.extensionRequested === 1) {
          success("Extension request sent to contracts team!");
        } else {
          success("Information updated successfully!");
        }

        await fetchCandidates();
        return result;
      }
    } catch (err) {
      console.error("Update roll-off info error:", err);
      error(`Failed to update: ${err.message}`);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateNotes = async (candidateId, notes) => {
    try {
      setIsLoading(true);

      const response = await fetch(`${API_BASE_URL}/save_candidate_notes.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          candidate_id: candidateId,
          candidate_name: selectedCandidate.candidateName,
          candidate_email: selectedCandidate.candidateEmail,
          notes: notes,
          added_by: currentUser
            ? `${currentUser.first_name} ${currentUser.last_name}`
            : "Unknown User",
        }),
      });

      const result = await response.json();

      if (result.success) {
        success("Notes updated successfully!");
        await fetchCandidateNotes(candidateId);
        setShowNotesModal(false);
        setSelectedCandidate(null);
        setCandidateNotes("");
      } else {
        error(`Failed to update notes: ${result.message}`);
      }
    } catch (error) {
      console.error("Update notes error:", error);
      error(`Failed to update notes: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddNote = async (
    candidateId,
    noteText,
    noteType = "general",
    isImportant = false,
  ) => {
    try {
      setIsLoading(true);

      console.log("?? Adding note with values:", {
        noteText,
        noteType,
        isImportant,
        candidateId,
      });

      const response = await fetch(
        `${API_BASE_URL}/manage_candidate_notes.php`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            candidate_id: candidateId,
            candidate_name: selectedCandidate.candidateName,
            candidate_email: selectedCandidate.candidateEmail,
            note_text: noteText,
            note_type: noteType,
            is_important: isImportant ? 1 : 0, // Only send 1 for important, 0 for not important
            added_by: currentUser
              ? `${currentUser.first_name} ${currentUser.last_name}`
              : "Unknown User",
          }),
        },
      );

      const result = await response.json();
      console.log("?? Add note API response:", result);

      if (result.success) {
        success("Note added successfully!");
        // Refresh notes for the candidate
        await fetchCandidateNotes(candidateId);

        // Reset form state
        setCandidateNotes("");
        setNoteType("general");
        setIsImportantNote(false);

        console.log("?? Form reset - isImportantNote should be false");
      } else {
        error(`Failed to add note: ${result.message}`);
      }
    } catch (error) {
      console.error("Add note error:", error);
      error(`Failed to add note: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCandidateNotes = async (candidateId) => {
    try {
      setNotesLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/manage_candidate_notes.php?candidate_id=${candidateId}`,
      );
      const result = await response.json();

      if (result.success) {
        setCandidateNotesList(result.data || []);

        // Update candidate state with notes count
        setCandidates((prevCandidates) =>
          prevCandidates.map((c) =>
            c.id === candidateId
              ? {
                  ...c,
                  notesCount: result.total_notes || 0,
                  latestNote: result.data?.[0],
                }
              : c,
          ),
        );
      }
    } catch (error) {
      console.error("Fetch notes error:", error);
    } finally {
      setNotesLoading(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/manage_candidate_notes.php`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ note_id: noteId }),
        },
      );

      const result = await response.json();
      if (result.success) {
        success("Note deleted successfully!");
        await fetchCandidateNotes(selectedCandidate.id);
      }
    } catch (error) {
      console.error("Delete note error:", error);
      error("Failed to delete note");
    }
  };

  const getDaysRemaining = (candidate) => {
    // ? Simply use sowEndDate
    if (!candidate.sowEndDate) return 0;

    const endDate = new Date(candidate.sowEndDate);
    const today = new Date();

    if (isNaN(endDate.getTime()) || isNaN(today.getTime())) {
      return 0;
    }

    return Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
  };

  // Function to get recent candidates (last 30 days)
  const getRecentCandidates = () => {
    if (!candidates || candidates.length === 0) {
      return [];
    }

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    return candidates
      .filter((candidate) => {
        if (!candidate.created_at) return false;
        const createdDate = new Date(candidate.created_at);
        return createdDate >= thirtyDaysAgo;
      })
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  // Function to send email to account managers
  const sendAccountManagerNotification = async (
  selectedCandidates,
  emailType = 'account_manager_notification'
) => {
  try {
    setSendingAccountManagerEmail(true);

    // Group candidates by account managers
    const accountManagerGroups = {};

    selectedCandidates.forEach((candidate) => {
      const managers = [
        candidate.account_manager_1,
        candidate.account_manager_2,
        candidate.account_manager_3,
        candidate.account_manager_4,
        candidate.account_manager_5,
        candidate.account_manager_6,
        candidate.account_manager_7,
        candidate.account_manager_8,
      ].filter((email) => email && email.trim());

      managers.forEach((manager) => {
        if (!accountManagerGroups[manager]) {
          accountManagerGroups[manager] = [];
        }
        accountManagerGroups[manager].push(candidate);
      });
    });

    if (Object.keys(accountManagerGroups).length === 0) {
      warning(
        'No account managers found for the selected candidates. Please ensure account manager emails are set.'
      );
      return;
    }

    let successCount = 0;
    let errorCount = 0;

    for (const [managerEmail, candidates] of Object.entries(accountManagerGroups)) {
      try {
        const payload = {
          emailType: emailType,
          managerEmail: managerEmail,
          candidates: candidates,
          totalCount: candidates.length,
        };

        console.log(`📤 Sending to ${managerEmail}:`, JSON.stringify(payload, null, 2));

        const response = await fetch(`${API_BASE_URL}/send_email.php`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });

        // Always read raw text first so we can debug non-JSON PHP errors
        const rawText = await response.text();
        console.log(`📥 Raw response for ${managerEmail} (status ${response.status}):`, rawText);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${rawText.substring(0, 200)}`);
        }

        let result;
        try {
          result = JSON.parse(rawText);
        } catch {
          throw new Error(`Non-JSON response: ${rawText.substring(0, 200)}`);
        }

        if (!result.success) {
          throw new Error(result.message || 'PHP returned success:false');
        }

        console.log(`✅ Email sent to ${managerEmail}`);
        successCount++;
      } catch (emailError) {
        console.error(`❌ Failed for ${managerEmail}:`, emailError.message);
        errorCount++;
      }
    }

    if (successCount > 0 && errorCount === 0) {
      success(
        `Account manager notifications sent to ${successCount} manager(s) for ${selectedCandidates.length} candidate(s)!`
      );
    } else if (successCount > 0 && errorCount > 0) {
      warning(
        `Partially sent: ${successCount} succeeded, ${errorCount} failed. Check console for details.`
      );
    } else {
      error('All notifications failed. Check the browser console for details.');
    }
  } catch (err) {
    console.error('❌ sendAccountManagerNotification error:', err);
    error('Failed to send account manager notifications: ' + err.message);
  } finally {
    setSendingAccountManagerEmail(false);
  }
};

  const mapJSFieldsToDBColumns = (candidates) => {
    console.log("?? Original candidate sample:", candidates[0]);

    const mapped = candidates.map((candidate) => {
      const mapped = {
        // Basic Information
        plc_code: candidate.plcCode || candidate.plc_code || "",
        sap_id: candidate.sapId || candidate.sap_id || "",
        candidate_name:
          candidate.candidateName || candidate.candidate_name || "",
        candidate_phone:
          candidate.candidatePhone || candidate.candidate_phone || "",
        candidate_email:
          candidate.candidateEmail || candidate.candidate_email || "",
        work_email: candidate.workEmail || candidate.work_email || "",
        status: candidate.status || "Active",

        // Project Details
        client: candidate.client || "",
        client_track: candidate.clientTrack || candidate.client_track || "",
        end_client: candidate.endClient || candidate.end_client || "",
        job_title: candidate.jobTitle || candidate.job_title || "",
        primary_skills:
          candidate.primarySkills || candidate.primary_skills || "",
        secondary_skills:
          candidate.secondarySkills || candidate.secondary_skills || "",
        project_city: candidate.projectCity || candidate.project_city || "",
        project_state: candidate.projectState || candidate.project_state || "",
        project_country:
          candidate.projectCountry || candidate.project_country || "USA",
        contractor_status:
          candidate.contractorStatus || candidate.contractor_status || "C2C",
        project_tech_stack:
          candidate.projectTechStack || candidate.project_tech_stack || "",
        project_key_technologies:
          candidate.projectKeyTechnologies ||
          candidate.project_key_technologies ||
          "",
        project_business_problem:
          candidate.projectBusinessProblem ||
          candidate.project_business_problem ||
          "",
        project_scope_objectives:
          candidate.projectScopeObjectives ||
          candidate.project_scope_objectives ||
          "",
        project_role_responsibility:
          candidate.projectRoleResponsibility ||
          candidate.project_role_responsibility ||
          "",
        project_experience_rating:
          candidate.projectExperienceRating ||
          candidate.project_experience_rating ||
          0,

        // Financial Information
        pay_rate: candidate.payRate || candidate.pay_rate || "",
        client_rate: candidate.clientRate || candidate.client_rate || "",
        margin_value: candidate.marginValue || candidate.margin_value || "",
        pay_rate_currency:
          candidate.payRateCurrency || candidate.pay_rate_currency || "USD",
        client_rate_currency:
          candidate.clientRateCurrency ||
          candidate.client_rate_currency ||
          "USD",
        pay_rate_payment_basis:
          candidate.payRatePaymentBasis ||
          candidate.pay_rate_payment_basis ||
          "Hourly",
        client_rate_payment_basis:
          candidate.clientRatePaymentBasis ||
          candidate.client_rate_payment_basis ||
          "Hourly",

        // Dates
        start_date: candidate.startDate || candidate.start_date || "",
        end_date: candidate.endDate || candidate.end_date || "",
        sow_end_date: candidate.sowEndDate || candidate.sow_end_date || "",
        sow_end_month: candidate.sowEndMonth || candidate.sow_end_month || "",
        extended_sow_date:
          candidate.extendedSowDate || candidate.extended_sow_date || "",
        extension_date:
          candidate.extensionDate || candidate.extension_date || "",

        // Management Team
        reporting_manager_name:
          candidate.reportingManagerName ||
          candidate.reporting_manager_name ||
          "",
        reporting_manager_email:
          candidate.reportingManagerEmail ||
          candidate.reporting_manager_email ||
          "",
        reporting_manager_phone:
          candidate.reportingManagerPhone ||
          candidate.reporting_manager_phone ||
          "",
        reporting_comments:
          candidate.reportingComments || candidate.reporting_comments || "",
        dal_name: candidate.dalName || candidate.dal_name || "",
        dm_name: candidate.dmName || candidate.dm_name || "",
        add_name: candidate.addName || candidate.add_name || "",
        bu_head: candidate.buHead || candidate.bu_head || "",
        bu_head_emailid:
          candidate.buHeadEmailid || candidate.bu_head_emailid || "",
        client_project_manager:
          candidate.clientProjectManager ||
          candidate.client_project_manager ||
          "",
        client_resource_manager:
          candidate.clientResourceManager ||
          candidate.client_resource_manager ||
          "",

        // Vendor Information
        contractor_vendor_name:
          candidate.contractorVendorName ||
          candidate.contractor_vendor_name ||
          "",
        vendor_contact_person:
          candidate.vendorContactPerson ||
          candidate.vendor_contact_person ||
          "",
        vendor_contact_number:
          candidate.vendorContactNumber ||
          candidate.vendor_contact_number ||
          "",
        vendor_contact_email:
          candidate.vendorContactEmail || candidate.vendor_contact_email || "",

        // Account Managers - FIXED
        account_manager_1:
          candidate.accountManager1 || candidate.account_manager_1 || "",
        account_manager_2:
          candidate.accountManager2 || candidate.account_manager_2 || "",
        account_manager_3:
          candidate.accountManager3 || candidate.account_manager_3 || "",
        account_manager_4:
          candidate.accountManager4 || candidate.account_manager_4 || "",
        account_manager_5:
          candidate.accountManager5 || candidate.account_manager_5 || "",
        account_manager_6:
          candidate.accountManager6 || candidate.account_manager_6 || "",
        account_manager_7:
          candidate.accountManager7 || candidate.account_manager_7 || "",
        account_manager_8:
          candidate.accountManager8 || candidate.account_manager_8 || "",

        // Client Feedback & Extensions
        client_rating: candidate.clientRating || candidate.client_rating || "",
        client_feedback:
          candidate.clientFeedback || candidate.client_feedback || "",
        improvement_area_1:
          candidate.improvementArea1 || candidate.improvement_area_1 || "",
        improvement_area_2:
          candidate.improvementArea2 || candidate.improvement_area_2 || "",
        improvement_area_3:
          candidate.improvementArea3 || candidate.improvement_area_3 || "",
        extension_requested:
          candidate.extensionRequested || candidate.extension_requested || 0,
        extension_status:
          candidate.extensionStatus || candidate.extension_status || "",
        extension_comments:
          candidate.extensionComments || candidate.extension_comments || "",
        extension_duration:
          candidate.extensionDuration || candidate.extension_duration || "",
        no_extension_reason:
          candidate.noExtensionReason || candidate.no_extension_reason || "",
        contacted_with:
          candidate.contactedWith || candidate.contacted_with || "",

        // Project Info Status
        project_info_status: candidate.project_info_status || "pending",
        project_info_submitted_at: candidate.project_info_submitted_at || "",

        // System Tracking
        created_at: candidate.created_at || "",
        updated_at: candidate.updated_at || "",
        reporting_updated_at: candidate.reporting_updated_at || "",
        reporting_updated_by: candidate.reporting_updated_by || "",
        project_updated_at: candidate.project_updated_at || "",
        project_updated_by: candidate.project_updated_by || "",
        feedback_updated_at: candidate.feedback_updated_at || "",
        feedback_updated_by: candidate.feedback_updated_by || "",
        project_form_sent_at: candidate.project_form_sent_at || "",
        project_form_completed_at: candidate.project_form_completed_at || "",
        project_form_reminder_count: candidate.project_form_reminder_count || 0,
        project_form_last_reminder_at:
          candidate.project_form_last_reminder_at || "",
        sent_to_rd: candidate.sentToRD || candidate.sent_to_rd || 0,
        sent_to_rd_date: candidate.sent_to_rd_date || "",
      };

      return mapped;
    });

    console.log("?? Mapped candidate sample:", mapped[0]);
    console.log("?? Available fields in mapped data:", Object.keys(mapped[0]));

    return mapped;
  };

  // REPLACE your handleAdvancedExport function with this version:
  const handleAdvancedExport = async (exportConfig) => {
    try {
      setExportLoading(true);

      const { candidates: exportCandidates, fields, format, filters } = exportConfig;

const NEGO_FIELDS = [
  'job_id', 'submission_id', 'applicant_id', 'interview_id',
  'negotiated_rate', 'reduced_rate', 'original_candidate_rate',
  'interview_status', 'leaders_handling', 'phone_number', 'email_id',
  'candidate_location', 'job_code', 'core_skills', 'client_name'
];

const isNegoExport = fields.some(f => NEGO_FIELDS.includes(f));
let candidates = exportCandidates;

      // Build user ID → name map for nego export
      let userNameMap = {};
      if (isNegoExport) {
        try {
          const usersResp = await fetch(`${API_BASE_URL}/get_users.php`, { credentials: 'include' });
          const usersResult = await usersResp.json();
          if (usersResult.success && Array.isArray(usersResult.data)) {
            usersResult.data.forEach(u => {
              userNameMap[String(u.id)] = `${u.first_name} ${u.last_name}`.trim();
            });
          }
        } catch { /* silently skip if users can't be fetched */ }
      }

      if (isNegoExport) {
  const negoResp = await fetch(`${API_BASE_URL}/api_get_negotiation.php?export_all=1`);
  if (!negoResp.ok) throw new Error(`Negotiation API error: HTTP ${negoResp.status}`);
  const negoText = await negoResp.text();
  let negoResult;
  try {
    negoResult = JSON.parse(negoText);
  } catch {
    throw new Error(`Negotiation API returned non-JSON: ${negoText.substring(0, 150)}`);
  }
  console.log('Negotiation API response:', negoResult);
  if (!negoResult.success) throw new Error('Failed to fetch negotiation data: ' + negoResult.message);
  candidates = Array.isArray(negoResult.data) ? negoResult.data : [];
  if (candidates.length === 0) throw new Error('No negotiation records found in the database.');
  console.log('Negotiation records loaded:', candidates.length);
}

console.log("EXPORT DEBUG - Starting:", {
        candidateCount: candidates.length,
        fieldCount: fields.length,
        format,
        firstCandidate: candidates[0],
      });

      // CRITICAL: Map React camelCase to database snake_case
      const exportData = candidates.map((candidate, index) => {
  const mappedRow = {};

  // ✅ Negotiation export — columns are already snake_case, map directly
  if (isNegoExport) {
        const EXCLUDE_NEGO_FIELDS = ['job_id', 'submission_id', 'applicant_id', 'interview_id'];
        fields
          .filter(f => !EXCLUDE_NEGO_FIELDS.includes(f))
          .forEach((field) => {
            if ((field === 'created_by' || field === 'updated_by') && candidate[field]) {
              mappedRow[field] = userNameMap[String(candidate[field])] || candidate[field];
            } else {
              mappedRow[field] = candidate[field] ?? "";
            }
          });
        return mappedRow;
      }

  // Complete field mapping based on your database schema
  const fieldMapping = {
    // Basic Information
    plc_code: candidate.plcCode || candidate.plc_code || "",
    sap_id: candidate.sapId || candidate.sap_id || "",
    candidate_name: candidate.candidateName || candidate.candidate_name || "",
    candidate_phone: candidate.candidatePhone || candidate.candidate_phone || "",
    candidate_email: candidate.candidateEmail || candidate.candidate_email || "",
    dob: candidate.dob || candidate.dateOfBirth || "",
    work_email: candidate.workEmail || candidate.work_email || "",
    status: candidate.status || "Active",

    // Reporting Information
    reporting_manager_name: candidate.reportingManagerName || candidate.reporting_manager_name || "",
    reporting_manager_email: candidate.reportingManagerEmail || candidate.reporting_manager_email || "",
    reporting_manager_phone: candidate.reportingManagerPhone || candidate.reporting_manager_phone || "",
    reporting_comments: candidate.reportingComments || candidate.reporting_comments || "",

    // Project Details
    client: candidate.client || "",
    client_track: candidate.clientTrack || candidate.client_track || "",
    end_client: candidate.endClient || candidate.end_client || "",
    job_title: candidate.jobTitle || candidate.job_title || "",
    primary_skills: candidate.primarySkills || candidate.primary_skills || "",
    secondary_skills: candidate.secondarySkills || candidate.secondary_skills || "",
    project_tech_stack: candidate.projectTechStack || candidate.project_tech_stack || "",
    project_overview: candidate.projectOverview || candidate.project_overview || "",
    project_goals: candidate.projectGoals || candidate.project_goals || "",
    project_role_responsibility: candidate.projectRoleResponsibility || candidate.project_role_responsibility || "",
    key_achievement: candidate.keyAchievement || candidate.key_achievement || "",
    project_experience_rating: candidate.projectExperienceRating || candidate.project_experience_rating || 0,

    // Dates
    start_date: candidate.startDate || candidate.start_date || "",
    end_date: candidate.endDate || candidate.end_date || "",
    sow_end_date: candidate.sowEndDate || candidate.sow_end_date || "",
    sow_end_month: candidate.sowEndMonth || candidate.sow_end_month || "",
    extended_sow_date: candidate.extendedSowDate || candidate.extended_sow_date || "",
    extended_count: candidate.extendedCount || candidate.extended_count || 0,

    // Contract Information
    contractor_status: candidate.contractorStatus || candidate.contractor_status || "C2C",
    project_city: candidate.projectCity || candidate.project_city || "",
    project_state: candidate.projectState || candidate.project_state || "",
    project_country: candidate.projectCountry || candidate.project_country || "USA",

    // Financial Information
    pay_rate_currency: candidate.payRateCurrency || candidate.pay_rate_currency || "USD",
    pay_rate: candidate.payRate || candidate.pay_rate || "",
    pay_rate_payment_basis: candidate.payRatePaymentBasis || candidate.pay_rate_payment_basis || "Hourly",
    client_rate_currency: candidate.clientRateCurrency || candidate.client_rate_currency || "USD",
    client_rate: candidate.clientRate || candidate.client_rate || "",
    client_rate_payment_basis: candidate.clientRatePaymentBasis || candidate.client_rate_payment_basis || "Hourly",
    margin_value: candidate.marginValue || candidate.margin_value || "",

    // Vendor Information
    contractor_vendor_name: candidate.contractorVendorName || candidate.contractor_vendor_name || "",
    vendor_contact_person: candidate.vendorContactPerson || candidate.vendor_contact_person || "",
    vendor_contact_number: candidate.vendorContactNumber || candidate.vendor_contact_number || "",
    vendor_contact_email: candidate.vendorContactEmail || candidate.vendor_contact_email || "",

    // Management Team
    dal_name: candidate.dalName || candidate.dal_name || "",
    dm_name: candidate.dmName || candidate.dm_name || "",
    add_name: candidate.addName || candidate.add_name || "",
    bu_head: candidate.buHead || candidate.bu_head || "",
    bu_head_emailid: candidate.buHeadEmailid || candidate.bu_head_emailid || "",
    cal_name: candidate.calName || candidate.cal_name || "",
    passthrough_owner: candidate.passthroughOwner || candidate.passthrough_owner || "",
    passthrough_support: candidate.passthroughSupport || candidate.passthrough_support || "",
    lead_recruiter: candidate.leadRecruiter || candidate.lead_recruiter || "",
    team_lead_name: candidate.teamLeadName || candidate.team_lead_name || "",
    recruiter_name: candidate.recruiterName || candidate.recruiter_name || "",
    recruiter_emp_id: candidate.recruiterEmpId || candidate.recruiter_emp_id || "",
    deal_pt_ptr: candidate.dealPtPtr || candidate.deal_pt_ptr || "",
    client_project_manager: candidate.clientProjectManager || candidate.client_project_manager || "",
    client_resource_manager: candidate.clientResourceManager || candidate.client_resource_manager || "",

    // Account Managers
    account_manager_1: candidate.accountManager1 || candidate.account_manager_1 || "",
    account_manager_2: candidate.accountManager2 || candidate.account_manager_2 || "",
    account_manager_3: candidate.accountManager3 || candidate.account_manager_3 || "",
    account_manager_4: candidate.accountManager4 || candidate.account_manager_4 || "",
    account_manager_5: candidate.accountManager5 || candidate.account_manager_5 || "",
    account_manager_6: candidate.accountManager6 || candidate.account_manager_6 || "",
    account_manager_7: candidate.accountManager7 || candidate.account_manager_7 || "",
    account_manager_8: candidate.accountManager8 || candidate.account_manager_8 || "",

    // Client Feedback & Extensions
    client_rating: candidate.clientRating || candidate.client_rating || "",
    client_feedback: candidate.clientFeedback || candidate.client_feedback || "",
    improvement_area_1: candidate.improvementArea1 || candidate.improvement_area_1 || "",
    improvement_area_2: candidate.improvementArea2 || candidate.improvement_area_2 || "",
    improvement_area_3: candidate.improvementArea3 || candidate.improvement_area_3 || "",
    extension_requested: candidate.extensionRequested || candidate.extension_requested || 0,
    extension_status: candidate.extensionStatus || candidate.extension_status || "",
    poc_name: candidate.pocName || candidate.poc_name || "",
    extension_date: candidate.extensionDate || candidate.extension_date || "",
    extension_duration: candidate.extensionDuration || candidate.extension_duration || "",
    extension_comments: candidate.extensionComments || candidate.extension_comments || "",
    no_extension_reason: candidate.noExtensionReason || candidate.no_extension_reason || "",

    // Project Form Tracking
    project_info_status: candidate.projectInfoStatus || candidate.project_info_status || "pending",
    project_info_submitted_at: candidate.projectInfoSubmittedAt || candidate.project_info_submitted_at || "",
    project_form_sent_at: candidate.project_form_sent_at || "",
    project_form_completed_at: candidate.project_form_completed_at || "",
    project_form_reminder_count: candidate.project_form_reminder_count || 0,
    project_form_last_reminder_at: candidate.project_form_last_reminder_at || "",
    contacted_with: candidate.contactedWith || candidate.contacted_with || "",

    // System Tracking
    reporting_updated_at: candidate.reporting_updated_at || "",
    reporting_updated_by: candidate.reporting_updated_by || "",
    project_updated_at: candidate.project_updated_at || "",
    project_updated_by: candidate.project_updated_by || "",
    feedback_updated_at: candidate.feedback_updated_at || "",
    feedback_updated_by: candidate.feedback_updated_by || "",
    created_at: candidate.created_at || "",
    updated_at: candidate.updated_at || "",
    sent_to_rd: candidate.sentToRD || candidate.sent_to_rd || 0,
    sent_to_rd_date: candidate.sent_to_rd_date || "",
    pending_extension_reason: candidate.pendingExtensionReason || candidate.pending_extension_reason || "",
  };

  // Only include the fields that were selected for export
  fields.forEach((field) => {
    mappedRow[field] = fieldMapping[field] ?? "";
  });

  // Debug first few records
  if (index < 2) {
    console.log(`Export Record ${index + 1}:`, {
      originalName: candidate.candidateName || candidate.candidate_name,
      mappedName: mappedRow.candidate_name,
      fieldsPopulated: Object.keys(mappedRow).filter((key) => mappedRow[key] !== "").length,
      totalFields: Object.keys(mappedRow).length,
    });
  }

  return mappedRow;
});

      console.log("EXPORT DEBUG - Processed:", {
        originalCount: candidates.length,
        processedCount: exportData.length,
        fieldsSelected: fields.length,
        firstProcessed: exportData[0],
        hasAllFields: fields.every((field) => field in (exportData[0] || {})),
      });

      // Create field labels with proper database field names
      const fieldLabels = {};
      const labelMapping = {
        plc_code: "PLC Code",
        sap_id: "SAP ID",
        candidate_name: "Candidate Name",
        candidate_phone: "Phone Number",
        candidate_email: "Email Address",
        work_email: "Work Email",
        reporting_manager_name: "Reporting Manager Name",
        reporting_manager_email: "Reporting Manager Email",
        client: "Client",
        job_title: "Job Title",
        primary_skills: "Primary Skills",
        start_date: "Start Date",
        end_date: "End Date",
        sow_end_date: "SOW End Date",
        project_city: "Project City",
        project_state: "Project State",
        pay_rate: "Pay Rate",
        client_rate: "Client Rate",
        margin_value: "Margin %",
        bu_head: "BU Head",
        bu_head_emailid: "BU Head Email ID",
        cal_name: "CAL Name",
        passthrough_owner: "Passthrough Owner",
        passthrough_support: "Passthrough Support",
        lead_recruiter: "Lead Recruiter",
        team_lead_name: "Team Lead Name",
        recruiter_name: "Recruiter Name",
        recruiter_emp_id: "Recruiter Employee ID",
        deal_pt_ptr: "Deal PT PTR",
        dob: "Date of Birth",

        account_manager_1: "Account Manager 1",
        account_manager_2: "Account Manager 2",
        account_manager_3: "Account Manager 3",
        account_manager_4: "Account Manager 4",
        account_manager_5: "Account Manager 5",
        account_manager_6: "Account Manager 6",
        account_manager_7: "Account Manager 7",
        account_manager_8: "Account Manager 8",
        client_rating: "Client Rating",
        extension_requested: "Extension Requested",
        extension_status: "Extension Status",
        created_at: "Created Date",
        updated_at: "Last Updated",
      };

      fields.forEach((field) => {
        fieldLabels[field] =
          labelMapping[field] ||
          field.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
      });

      if (exportData.length === 0) {
        throw new Error('No records to export. Check filters or ensure the data table is populated.');
      }

      // Send to export API
      const response = await fetch(`${API_BASE_URL}/advanced_export.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          data: exportData,
          fields: fields,
          fieldLabels: fieldLabels,
          format: format,
          filters: filters,
          filename: `candidates_export_${new Date().toISOString().split("T")[0]}`,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Export API Error:", errorText);
        throw new Error(
          `Export failed: HTTP ${response.status} - ${errorText}`,
        );
      }

      // Handle file download
      const blob = await response.blob();

      if (blob.size === 0) {
        throw new Error("Export generated an empty file");
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      // Set filename based on format
      const timestamp = new Date().toISOString().split("T")[0];
      const extension = format === "excel" ? "xlsx" : format;
      a.download = `candidates_export_${timestamp}.${extension}`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      success(
        `Successfully exported ${candidates.length} candidates to ${format.toUpperCase()} format!`,
      );
      setShowAdvancedExportModal(false);
    } catch (err) {
  console.error("Export Error:", err);
  error(`Export failed: ${err.message}`);
} finally {
      setExportLoading(false);
    }
  };

  // User Management Modal Component
  const UserManagementModal = () => {
    const [modalUsers, setModalUsers] = useState([]);
    const [modalUsersLoading, setModalUsersLoading] = useState(false);

    // Move fetchUsers inside the modal component
    const fetchModalUsers = async () => {
      try {
        console.log("?? Starting fetchModalUsers...");
        console.log("?? API_BASE_URL:", API_BASE_URL);
        console.log("?? Full URL:", `${API_BASE_URL}/get_users.php`);
        setModalUsersLoading(true);

        const response = await fetch(`${API_BASE_URL}/get_users.php`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        console.log("?? Response status:", response.status);
        console.log("?? Response ok:", response.ok);

        if (!response.ok) {
          const errorText = await response.text();
          console.error("?? Response error text:", errorText);
          throw new Error(
            `HTTP error! status: ${response.status} - ${errorText}`,
          );
        }

        const responseText = await response.text();
        console.log("?? Raw response text:", responseText);

        let result;
        try {
          result = JSON.parse(responseText);
        } catch (parseError) {
          console.error("?? JSON parse error:", parseError);
          throw new Error(
            "Invalid JSON response from server: " +
              responseText.substring(0, 100),
          );
        }

        console.log("?? Parsed result:", result);

        if (result.success) {
          console.log("? Users data:", result.data);
          console.log("? Number of users:", result.data?.length || 0);
          setModalUsers(result.data || []);
        } else {
          console.error("? API error:", result.message);
          error(
            "Failed to fetch users: " + (result.message || "Unknown error"),
          );
        }
      } catch (fetchError) {
        console.error("? Fetch error:", fetchError);
        error("Failed to load users: " + fetchError.message);
      } finally {
        setModalUsersLoading(false);
      }
    };

    useEffect(() => {
      fetchModalUsers();

      // Set up refresh function for edit modal
      window.refreshUserList = fetchModalUsers;

      // Cleanup
      return () => {
        delete window.refreshUserList;
      };
    }, []); // Only run once when modal mounts

    const getRoleBadgeColor = (role) => {
      switch (role) {
        case "Admin":
          return "bg-red-100 text-red-800 border-red-200";
        case "Team Lead":
          return "bg-purple-100 text-purple-800 border-purple-200";
        case "BU Head":
          return "bg-orange-100 text-orange-800 border-orange-200";
        case "Account Manager":
          return "bg-blue-100 text-blue-800 border-blue-200";
        default:
          return "bg-gray-100 text-gray-800 border-gray-200";
      }
    };

    const getStatusBadgeColor = (status) => {
      switch (status) {
        case "active":
          return "bg-green-100 text-green-800 border-green-200";
        case "inactive":
          return "bg-gray-100 text-gray-800 border-gray-200";
        case "suspended":
          return "bg-red-100 text-red-800 border-red-200";
        default:
          return "bg-gray-100 text-gray-800 border-gray-200";
      }
    };

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">User Management</h2>
                  <p className="text-blue-100 text-sm">
                    Manage user roles and permissions
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowUserManagementModal(false)}
                className="p-2 hover:bg-white/20 rounded-xl transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-6 max-h-[calc(95vh-120px)] overflow-y-auto">
            {modalUsersLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400 mr-3" />
                <span className="text-slate-500">Loading users...</span>
              </div>
            ) : modalUsers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  No Users Found
                </h3>
                <p className="text-slate-500 mb-4">
                  No users are available in the system.
                </p>
                <button
                  onClick={fetchModalUsers}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Try Again
                </button>
                <div className="mt-4 text-xs text-slate-400">
                  Debug: API URL = {API_BASE_URL}/get_users.php
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Login
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {modalUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                              <span className="text-sm font-bold text-white">
                                {user.first_name.charAt(0)}
                                {user.last_name.charAt(0)}
                              </span>
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">
                                {user.first_name} {user.last_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                ID: {user.id}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getRoleBadgeColor(user.role)}`}
                          >
                            {user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusBadgeColor(user.status)}`}
                          >
                            {user.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.last_login
                            ? new Date(user.last_login).toLocaleDateString(
                                "en-US",
                                {
                                  month: "2-digit",
                                  day: "2-digit",
                                  year: "numeric",
                                },
                              )
                            : "Never"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setShowEditUserModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>

                            {user.status === "active" ? (
                              <button
                                onClick={async () => {
                                  await handleUpdateUserStatus(
                                    user.id,
                                    "suspended",
                                  );
                                  fetchModalUsers(); // Use the modal's fetch function
                                }}
                                className="text-red-600 hover:text-red-900"
                                title="Suspend User"
                              >
                                <UserX className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={async () => {
                                  await handleUpdateUserStatus(
                                    user.id,
                                    "active",
                                  );
                                  fetchModalUsers(); // Use the modal's fetch function
                                }}
                                className="text-green-600 hover:text-green-900"
                                title="Activate User"
                              >
                                <UserCheck className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Edit User Modal Component
  const EditUserModal = () => {
    const [editRole, setEditRole] = useState(selectedUser?.role || "");

    useEffect(() => {
      if (selectedUser) {
        setEditRole(selectedUser.role);
      }
    }, [selectedUser]);

    if (!selectedUser) return null;

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 text-white">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Edit User Role</h2>
              <button
                onClick={() => {
                  setShowEditUserModal(false);
                  setSelectedUser(null);
                }}
                className="p-2 hover:bg-white/20 rounded-xl transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedUser.first_name} {selectedUser.last_name}
              </h3>
              <p className="text-sm text-gray-600">{selectedUser.email}</p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                User Role
              </label>
              <select
                value={editRole}
                onChange={(e) => setEditRole(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="Account Manager">Account Manager</option>
                <option value="BU Head">BU Head</option>
                <option value="Team Lead">Team Lead</option>
                <option value="Admin">Admin</option>
              </select>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowEditUserModal(false);
                  setSelectedUser(null);
                }}
                className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await handleUpdateUserRole(selectedUser.id, editRole);
                  setShowEditUserModal(false);
                  setSelectedUser(null);
                  // Get parent modal component and refresh
                  if (window.refreshUserList) {
                    window.refreshUserList();
                  }
                }}
                disabled={isLoading || editRole === selectedUser.role}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all disabled:opacity-50"
              >
                {isLoading ? "Updating..." : "Update Role"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const RecentlyOnboardedTable = () => {
    const [recentPage, setRecentPage] = useState(1);
    const recentItemsPerPage = 10;

    const paginatedRecent = recentlyOnboardedCandidates.slice(
      (recentPage - 1) * recentItemsPerPage,
      recentPage * recentItemsPerPage,
    );

    const totalRecentPages = Math.ceil(
      recentlyOnboardedCandidates.length / recentItemsPerPage,
    );

    const getDaysOnboarded = (startDate) => {
      const today = new Date();
      const start = new Date(startDate);
      const diffTime = today - start;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    };

    const getOnboardingBadge = (daysOnboarded) => {
      if (daysOnboarded === 0)
        return "bg-green-100 text-green-800 border-green-200";
      if (daysOnboarded <= 3)
        return "bg-blue-100 text-blue-800 border-blue-200";
      return "bg-purple-100 text-purple-800 border-purple-200";
    };

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                Recently Onboarded Candidates (
                {recentlyOnboardedCandidates.length})
              </h3>
              <p className="text-sm text-slate-600">
                Candidates who started in the last 7 days
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={fetchCandidates}
                className="flex items-center px-3 py-2 text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 text-sm"
                disabled={dataLoading}
              >
                <RefreshCw
                  className={`w-4 h-4 mr-2 ${dataLoading ? "animate-spin" : ""}`}
                />
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Candidate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Project Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Start Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Days Since Start
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Rate Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {dataLoading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center">
                    <div className="flex items-center justify-center">
                      <RefreshCw className="w-6 h-6 animate-spin text-slate-400 mr-2" />
                      <span className="text-slate-500">
                        Loading recently onboarded candidates...
                      </span>
                    </div>
                  </td>
                </tr>
              ) : paginatedRecent.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center">
                    <div className="text-slate-500">
                      No candidates were onboarded in the last 7 days.
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedRecent.map((candidate) => {
                  const daysOnboarded = getDaysOnboarded(candidate.startDate);
                  return (
                    <tr
                      key={candidate.id}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                            <span className="text-sm font-medium text-slate-600">
                              {candidate.candidateName
                                .split(" ")
                                .map((n) => n[0])
                                .join("")}
                            </span>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-slate-900">
                              {candidate.candidateName}
                            </div>
                            <div className="text-sm text-slate-500">
                              {candidate.candidateEmail}
                            </div>
                            {candidate.workEmail && (
                              <div className="text-xs text-slate-400">
                                Work: {candidate.workEmail}
                              </div>
                            )}
                            <div className="text-xs text-slate-400">
                              {candidate.plcCode}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-slate-900">
                          {candidate.client}
                        </div>
                        <div className="text-sm text-slate-500">
                          {candidate.jobTitle}
                        </div>
                        <div className="text-xs text-slate-400">
                          {candidate.projectCity}, {candidate.projectState}
                        </div>
                        <div className="text-xs text-slate-400">
                          {candidate.contractorStatus}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-900">
                          {candidate.startDate}
                        </div>
                        <div className="text-xs text-slate-500">
                          End: {candidate.endDate}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getOnboardingBadge(daysOnboarded)}`}
                        >
                          {daysOnboarded === 0
                            ? "Today"
                            : `${daysOnboarded} day${daysOnboarded === 1 ? "" : "s"}`}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-slate-900">
                          ${candidate.payRate}/hr
                        </div>
                        <div className="text-xs text-slate-500">
                          Client: ${candidate.clientRate}/hr
                        </div>
                        <div className="text-xs text-green-600">
                          Margin: {candidate.marginValue}%
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => handleReportingInfoClick(candidate)}
                            className="p-1 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded transition-colors"
                            title="Update Reporting Info"
                          >
                            <Users2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedCandidate(candidate);
                              setShowEditModal(true);
                            }}
                            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                            title="Edit Candidate"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedCandidate(candidate);
                              setShowCandidateDetails(true);
                            }}
                            className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={async () => {
                              await sendEmail("welcome", candidate.id);
                            }}
                            className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors"
                            title="Send Welcome Email"
                          >
                            <Mail className="w-4 h-4" />
                          </button>

                          <div className="relative">
                            <button
                              onClick={() =>
                                setShowMoreActions(
                                  showMoreActions === candidate.id
                                    ? null
                                    : candidate.id,
                                )
                              }
                              className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded transition-colors"
                              title="More Actions"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                            {showMoreActions === candidate.id && (
                              <MoreActionsDropdown
                                candidate={candidate}
                                onClose={() => setShowMoreActions(null)}
                                position="left"
                              />
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalRecentPages > 1 && !dataLoading && (
          <div className="px-6 py-4 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-slate-700">
              Showing {(recentPage - 1) * recentItemsPerPage + 1} to{" "}
              {Math.min(
                recentPage * recentItemsPerPage,
                recentlyOnboardedCandidates.length,
              )}{" "}
              of {recentlyOnboardedCandidates.length} results
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setRecentPage(Math.max(1, recentPage - 1))}
                disabled={recentPage === 1}
                className="p-2 border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {[...Array(Math.min(5, totalRecentPages))].map((_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setRecentPage(pageNum)}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      recentPage === pageNum
                        ? "bg-slate-900 text-white"
                        : "text-slate-700 hover:bg-slate-50 border border-slate-300"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() =>
                  setRecentPage(Math.min(totalRecentPages, recentPage + 1))
                }
                disabled={recentPage === totalRecentPages}
                className="p-2 border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const CandidateTable = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex flex-col gap-4">
          {/* Title and Toggle */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <h3 className="text-lg font-semibold text-slate-900">
                {showRecentOnboarded
                  ? "Recently Onboarded Candidates"
                  : "All Candidates"}{" "}
                ({filteredCandidates.length})
                {dataLoading && (
                  <span className="ml-2">
                    <RefreshCw className="w-4 h-4 animate-spin inline" />
                  </span>
                )}
              </h3>

              {/* Toggle Switch */}
              <div className="flex items-center gap-2">
                <span
                  className={`text-sm ${!showRecentOnboarded ? "font-medium text-slate-900" : "text-slate-500"}`}
                >
                  All
                </span>
                <button
                  onClick={() => setShowRecentOnboarded(!showRecentOnboarded)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 ${
                    showRecentOnboarded ? "bg-slate-900" : "bg-slate-200"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      showRecentOnboarded ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
                <span
                  className={`text-sm ${showRecentOnboarded ? "font-medium text-slate-900" : "text-slate-500"}`}
                >
                  Recent
                </span>
              </div>
            </div>

            <button
              onClick={fetchCandidates}
              className="flex items-center px-3 py-2 text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 text-sm"
              disabled={dataLoading}
            >
              <RefreshCw
                className={`w-4 h-4 mr-2 ${dataLoading ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
          </div>

          {/* Search and Filters Row */}
          <div className="flex flex-col lg:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name, client, email, job title, skills, PLC code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 text-sm w-full"
              />
            </div>

            {/* Basic Filters */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 text-sm"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            {/* Advanced Filters Toggle */}
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={`flex items-center px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                showAdvancedFilters
                  ? "bg-blue-50 text-blue-700 border-blue-300"
                  : "text-slate-700 border-slate-300 hover:bg-slate-50"
              }`}
            >
              <Filter className="w-4 h-4 mr-2" />
              Advanced Filters
              <ChevronDown
                className={`w-4 h-4 ml-2 transition-transform ${showAdvancedFilters ? "rotate-180" : ""}`}
              />
            </button>
          </div>

          {/* Advanced Filters Panel */}
          {showAdvancedFilters && (
            <div className="bg-gradient-to-r from-slate-50 to-blue-50 p-6 rounded-xl border border-slate-200">
              <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                <Settings className="w-5 h-5 mr-2 text-blue-600" />
                Advanced Filter Options
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {/* Client Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Client
                  </label>
                  <select
                    value={clientFilter}
                    onChange={(e) => setClientFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="all">All Clients</option>
                    {uniqueClients.map((client) => (
                      <option key={client} value={client}>
                        {client}
                      </option>
                    ))}
                  </select>
                </div>

                {/* BU Head Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Business Unit
                  </label>
                  <select
                    value={buHeadFilter}
                    onChange={(e) => setBuHeadFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="all">All BUs</option>
                    {uniqueBUHeads.map((bu) => (
                      <option key={bu} value={bu}>
                        {bu}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Skills Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Skills
                  </label>
                  <input
                    type="text"
                    placeholder="Filter by skills..."
                    value={skillsFilter}
                    onChange={(e) => setSkillsFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>

                {/* Location Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Location
                  </label>
                  <select
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="all">All Locations</option>
                    {uniqueLocations.map((location) => (
                      <option key={location} value={location}>
                        {location}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Contract Type Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Contract Type
                  </label>
                  <select
                    value={contractTypeFilter}
                    onChange={(e) => setContractTypeFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="all">All Types</option>
                    {uniqueContractTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Rate Range Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Rate Range
                  </label>
                  <select
                    value={rateRangeFilter}
                    onChange={(e) => setRateRangeFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="all">All Ranges</option>
                    <option value="low">Low (&lt;$100)</option>
                    <option value="medium">Medium ($100-$200)</option>
                    <option value="high">High ($200+)</option>
                  </select>
                </div>

                {/* Margin Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Margin Range
                  </label>
                  <select
                    value={marginFilter}
                    onChange={(e) => setMarginFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="all">All Margins</option>
                    <option value="low">Low (&lt;20%)</option>
                    <option value="medium">Medium (20-35%)</option>
                    <option value="high">High (35%+)</option>
                  </select>
                </div>

                {/* Reporting Status Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Reporting Status
                  </label>
                  <select
                    value={reportingStatusFilter}
                    onChange={(e) => setReportingStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="all">All Status</option>
                    <option value="completed">Completed</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>

                {/* Project Status Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Project Status
                  </label>
                  <select
                    value={projectStatusFilter}
                    onChange={(e) => setProjectStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="all">All Status</option>
                    <option value="completed">Completed</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>

                {/* End Date Range Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Contract Ending
                  </label>
                  <select
                    value={endDateRangeFilter}
                    onChange={(e) => setEndDateRangeFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="all">All Contracts</option>
                    <option value="ending_soon">Ending Soon (30 days)</option>
                    <option value="ending_later">
                      Ending Later (60+ days)
                    </option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>
              </div>

              {/* Custom End Date Range */}
              {endDateRangeFilter === "custom" && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      End Date From
                    </label>
                    <input
                      type="date"
                      value={customEndDateStart}
                      onChange={(e) => setCustomEndDateStart(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      End Date To
                    </label>
                    <input
                      type="date"
                      value={customEndDateEnd}
                      onChange={(e) => setCustomEndDateEnd(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                </div>
              )}

              {/* Clear Filters Button */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => {
                    setClientFilter("all");
                    setBuHeadFilter("all");
                    setSkillsFilter("");
                    setLocationFilter("all");
                    setContractTypeFilter("all");
                    setRateRangeFilter("all");
                    setMarginFilter("all");
                    setReportingStatusFilter("all");
                    setProjectStatusFilter("all");
                    setEndDateRangeFilter("all");
                    setCustomEndDateStart("");
                    setCustomEndDateEnd("");
                  }}
                  className="px-4 py-2 text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors text-sm"
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Candidate
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Client & Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Start Date
              </th>
              {showRecentOnboarded && (
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Days Since Start
                </th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Rates
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {dataLoading ? (
              <TableLoading columns={showRecentOnboarded ? 6 : 5} rows={5} />
            ) : paginatedCandidates.length === 0 ? (
              <tr>
                <td
                  colSpan={showRecentOnboarded ? "6" : "5"}
                  className="px-6 py-8 text-center"
                >
                  <div className="text-slate-500">
                    {filteredCandidates.length === 0 && candidates.length > 0
                      ? "No candidates match your search criteria"
                      : showRecentOnboarded
                        ? "No recently onboarded candidates found"
                        : "No candidates found. Add some candidates to get started."}
                  </div>
                </td>
              </tr>
            ) : (
              paginatedCandidates.map((candidate) => {
                const daysOnboarded = Math.floor(
                  (new Date() - new Date(candidate.startDate)) /
                    (1000 * 60 * 60 * 24),
                );
                const getOnboardingBadge = (days) => {
                  if (days === 0)
                    return "bg-green-100 text-green-800 border-green-200";
                  if (days <= 3)
                    return "bg-blue-100 text-blue-800 border-blue-200";
                  if (days <= 7)
                    return "bg-purple-100 text-purple-800 border-purple-200";
                  return "bg-slate-100 text-slate-800 border-slate-200";
                };

                return (
                  <tr
                    key={candidate.id}
                    className="hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                          <span className="text-sm font-medium text-slate-600">
                            {candidate.candidateName
                              .split(" ")
                              .map((n) => n[0])
                              .join("")}
                          </span>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-slate-900">
                            {candidate.candidateName}
                          </div>
                          <div className="text-xs text-slate-400">
                            {candidate.plcCode} ? {candidate.sapId}
                          </div>
                          <div className="text-xs text-slate-500">
                            {candidate.primarySkills}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-900">
                        {candidate.candidatePhone}
                      </div>
                      <div className="text-sm text-slate-600">
                        {candidate.candidateEmail}
                      </div>
                      {candidate.workEmail && (
                        <div className="text-xs text-slate-500">
                          Work: {candidate.workEmail}
                        </div>
                      )}
                    </td>

                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-slate-900">
                        {candidate.client}
                      </div>
                      <div className="text-sm text-slate-600">
                        {candidate.jobTitle}
                      </div>
                      <div className="text-xs text-slate-500">
                        {candidate.projectCity}, {candidate.projectState}
                      </div>
                      <div className="text-xs text-slate-400">
                        {candidate.contractorStatus}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-900">
                        {new Date(candidate.startDate).toLocaleDateString(
                          "en-US",
                          {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                          },
                        )}
                      </div>
                      <div className="text-xs text-slate-500">
                        End:{" "}
                        {new Date(candidate.endDate).toLocaleDateString(
                          "en-US",
                          {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                          },
                        )}
                      </div>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          candidate.status === "Active"
                            ? "bg-green-100 text-green-800"
                            : "bg-slate-100 text-slate-800"
                        }`}
                      >
                        {candidate.status}
                      </span>
                    </td>

                    {showRecentOnboarded && (
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getOnboardingBadge(daysOnboarded)}`}
                        >
                          {daysOnboarded === 0
                            ? "Today"
                            : `${daysOnboarded} day${daysOnboarded === 1 ? "" : "s"}`}
                        </span>
                      </td>
                    )}

                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-slate-900">
                        ${candidate.payRate}/hr
                      </div>
                      <div className="text-xs text-slate-500">
                        Client: ${candidate.clientRate}/hr
                      </div>
                      <div className="text-xs text-green-600">
                        Margin: {candidate.marginValue}%
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleReportingInfoClick(candidate)}
                          className="p-1 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded transition-colors"
                          title="Update Reporting Info"
                        >
                          <Users2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedCandidate(candidate);
                            setShowCandidateDetails(true);
                          }}
                          className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedCandidate(candidate);
                            setShowEditModal(true);
                          }}
                          className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedCandidate(candidate);
                            setShowDeleteModal(true);
                          }}
                          className="p-1 text-slate-400 hover:text-red-600 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <div className="relative">
                          <button
                            onClick={() =>
                              setShowMoreActions(
                                showMoreActions === candidate.id
                                  ? null
                                  : candidate.id,
                              )
                            }
                            className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                            title="More Actions"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          {showMoreActions === candidate.id && (
                            <MoreActionsDropdown
                              candidate={candidate}
                              onClose={() => setShowMoreActions(null)}
                            />
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && !dataLoading && (
        <div className="px-6 py-4 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-slate-700">
            Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
            {Math.min(currentPage * itemsPerPage, filteredCandidates.length)} of{" "}
            {filteredCandidates.length} results
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="p-2 border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    currentPage === pageNum
                      ? "bg-slate-900 text-white"
                      : "text-slate-700 hover:bg-slate-50 border border-slate-300"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
              className="p-2 border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const BulkUpdateModal = ({ type, candidates, onClose, onUpdate }) => {
    const [updateDate, setUpdateDate] = useState("");

    const handleSubmit = (e) => {
      e.preventDefault();
      if (!updateDate) {
        warning("Please select a date");
        return;
      }

      // ? Update sowEndDate directly instead of extendedSowDate
      const updateData =
        type === "extend"
          ? {
              sowEndDate: updateDate,
              extension_requested: 0, // Reset to no decision
              extensionRequested: 0, // Reset to no decision
              extensionStatus: "", // Clear extension status
              extension_status: "", // Clear extension status
            }
          : {
              endDate: updateDate,
              status: "Inactive",
              extension_requested: 0, // Reset to no decision
              extensionRequested: 0, // Reset to no decision
              extensionStatus: "No", // Clear extension status
              extension_status: "No", // Clear extension status
            };

      onUpdate(updateData);
    };

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 text-white">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">
                {type === "extend"
                  ? "Bulk Extend SOW Date"
                  : "Bulk Update End Date"}
              </h3>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">
                Update {type === "extend" ? "SOW end date" : "end date"} for{" "}
                {candidates.length} selected candidates
              </p>

              <label className="block text-sm font-medium text-gray-700 mb-2">
                {type === "extend" ? "New SOW End Date" : "New End Date"}
              </label>
              <input
                type="date"
                value={updateDate}
                onChange={(e) => setUpdateDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min={new Date().toISOString().split("T")[0]}
                required
              />
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-yellow-800">
                  {type === "extend"
                    ? "This will update the SOW end date for all selected candidates."
                    : "This will mark all selected candidates as inactive and update their end dates."}
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all"
              >
                Update All
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const RollOffTable = () => {
    const [rollOffPage, setRollOffPage] = useState(1);
    const [rollOffSearchTerm, setRollOffSearchTerm] = useState("");
    const rollOffItemsPerPage = 10;

    // Filter roll-off candidates based on search
    const filteredRollOffCandidates = rollOffCandidates.filter((candidate) => {
      const matchesSearch =
        candidate.candidateName
          .toLowerCase()
          .includes(rollOffSearchTerm.toLowerCase()) ||
        candidate.client
          .toLowerCase()
          .includes(rollOffSearchTerm.toLowerCase()) ||
        candidate.jobTitle
          .toLowerCase()
          .includes(rollOffSearchTerm.toLowerCase()) ||
        candidate.candidateEmail
          .toLowerCase()
          .includes(rollOffSearchTerm.toLowerCase());
      return matchesSearch;
    });

    const paginatedRollOff = filteredRollOffCandidates.slice(
      (rollOffPage - 1) * rollOffItemsPerPage,
      rollOffPage * rollOffItemsPerPage,
    );

    const totalRollOffPages = Math.ceil(
      filteredRollOffCandidates.length / rollOffItemsPerPage,
    );

    const getDaysRemaining = (candidate) => {
      // ? Simply use sowEndDate
      if (!candidate.sowEndDate) return 0;

      const endDate = new Date(candidate.sowEndDate);
      const today = new Date();

      if (isNaN(endDate.getTime()) || isNaN(today.getTime())) {
        return 0;
      }

      return Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
    };

    const getUrgencyBadge = (daysRemaining) => {
      if (daysRemaining <= 7) return "bg-red-100 text-red-800 border-red-200";
      if (daysRemaining <= 30)
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      return "bg-blue-100 text-blue-800 border-blue-200";
    };

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                Upcoming Roll-off Candidates ({filteredRollOffCandidates.length}
                )
              </h3>
              <p className="text-sm text-slate-600">
                Candidates ending within 60 days
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search roll-off candidates..."
                  value={rollOffSearchTerm}
                  onChange={(e) => setRollOffSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 text-sm w-full sm:w-64"
                />
              </div>

              <button
                onClick={fetchCandidates}
                className="flex items-center px-3 py-2 text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 text-sm"
                disabled={dataLoading}
              >
                <RefreshCw
                  className={`w-4 h-4 mr-2 ${dataLoading ? "animate-spin" : ""}`}
                />
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Candidate Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Contact Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  End Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Job Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  End Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Days Remaining
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {dataLoading ? (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center">
                    <div className="flex items-center justify-center">
                      <RefreshCw className="w-6 h-6 animate-spin text-slate-400 mr-2" />
                      <span className="text-slate-500">
                        Loading roll-off candidates...
                      </span>
                    </div>
                  </td>
                </tr>
              ) : paginatedRollOff.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center">
                    <div className="text-slate-500">
                      {filteredRollOffCandidates.length === 0 &&
                      rollOffCandidates.length > 0
                        ? "No roll-off candidates match your search criteria"
                        : "No candidates are scheduled to roll off in the next 60 days."}
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedRollOff.map((candidate) => {
                  const daysRemaining = getDaysRemaining(candidate);
                  return (
                    <tr
                      key={candidate.id}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                            <span className="text-sm font-medium text-slate-600">
                              {candidate.candidateName
                                .split(" ")
                                .map((n) => n[0])
                                .join("")}
                            </span>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-slate-900">
                              {candidate.candidateName}
                            </div>
                            <div className="text-xs text-slate-400">
                              {candidate.plcCode}
                            </div>
                            {candidate.clientRating && (
                              <div className="flex items-center mt-1">
                                <Star className="w-3 h-3 text-yellow-400 fill-current" />
                                <span className="text-xs text-slate-500 ml-1">
                                  {candidate.clientRating}/5
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-900">
                          {candidate.candidatePhone}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-900">
                          {candidate.candidateEmail}
                        </div>
                        {candidate.workEmail && (
                          <div className="text-xs text-slate-500">
                            Work: {candidate.workEmail}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-slate-900">
                          {candidate.client}
                        </div>
                        <div className="text-xs text-slate-500">
                          {candidate.clientTrack}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-900">
                          {candidate.endClient}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-900">
                          {candidate.jobTitle}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-900">
                          {candidate.endDate}
                        </div>
                        {candidate.extendedSowDate && (
                          <div className="text-xs text-green-600">
                            Extended: {candidate.extendedSowDate}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getUrgencyBadge(daysRemaining)}`}
                        >
                          {daysRemaining} days
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => {
                              setSelectedCandidate(candidate);
                              setShowRollOffEditModal(true);
                            }}
                            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                            title="Edit Roll-off Details"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedCandidate(candidate);
                              setShowCandidateDetails(true);
                            }}
                            className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          <div className="relative">
                            <button
                              onClick={() =>
                                setShowRollOffMoreActions(
                                  showRollOffMoreActions === candidate.id
                                    ? null
                                    : candidate.id,
                                )
                              }
                              className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded transition-colors"
                              title="More Actions"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                            {showRollOffMoreActions === candidate.id && (
                              <MoreActionsDropdown
                                candidate={candidate}
                                onClose={() => setShowRollOffMoreActions(null)}
                                position="left"
                              />
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalRollOffPages > 1 && !dataLoading && (
          <div className="px-6 py-4 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-slate-700">
              Showing {(rollOffPage - 1) * rollOffItemsPerPage + 1} to{" "}
              {Math.min(
                rollOffPage * rollOffItemsPerPage,
                filteredRollOffCandidates.length,
              )}{" "}
              of {filteredRollOffCandidates.length} results
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setRollOffPage(Math.max(1, rollOffPage - 1))}
                disabled={rollOffPage === 1}
                className="p-2 border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {[...Array(Math.min(5, totalRollOffPages))].map((_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setRollOffPage(pageNum)}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      rollOffPage === pageNum
                        ? "bg-slate-900 text-white"
                        : "text-slate-700 hover:bg-slate-50 border border-slate-300"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() =>
                  setRollOffPage(Math.min(totalRollOffPages, rollOffPage + 1))
                }
                disabled={rollOffPage === totalRollOffPages}
                className="p-2 border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="min-h-screen bg-slate-100"
      style={{ fontFamily: "Montserrat, system-ui, -apple-system, sans-serif" }}
    >
      <Header
        currentUser={currentUser}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        rollOffCandidates={rollOffCandidates}
        candidates={candidates}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        showProfileDropdown={showProfileDropdown}
        setShowProfileDropdown={setShowProfileDropdown}
        showExportDropdown={showExportDropdown}
        setShowExportDropdown={setShowExportDropdown}
        setShowActivityLogModal={setShowActivityLogModal}
        setShowUserManagementModal={setShowUserManagementModal}
        setShowImportInstructions={setShowImportInstructions}
        setShowAdvancedExportModal={setShowAdvancedExportModal}
        handleFileSelect={handleFileSelect}
        downloadTemplate={downloadTemplate}
        isLoading={isLoading}
        success={success}
        API_BASE_URL={API_BASE_URL}
        setShowSyncModal={setShowSyncModal}
        isSyncing={isSyncing}
        setShowTutorialModal={setShowTutorialModal}
      />

      <main className="pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {userLoading ? (
          <div className="min-h-screen flex items-center justify-center">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">
                Loading Dashboard
              </h2>
              <p className="text-slate-600">
                Verifying your access permissions...
              </p>
            </div>
          </div>
        ) : !currentUser ? (
          <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
            <div className="text-center max-w-md bg-white p-8 rounded-2xl shadow-xl border border-slate-200">
              <div className="w-20 h-20 bg-gradient-to-r from-orange-400 to-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                {sessionExpired ? "Session Timeout" : "Authentication Required"}
              </h2>
              <p className="text-slate-600 mb-6">
                {sessionExpired
                  ? "Your session has expired due to inactivity. Please log in again to continue."
                  : "You need to be logged in to access this dashboard."}
              </p>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log("?? Redirecting to login...");

                  // Clear all authentication data
                  localStorage.clear();
                  sessionStorage.clear();

                  // Call parent logout handler if available
                  if (window.handleAppLogout) {
                    window.handleAppLogout();
                  } else {
                    // Fallback: reload to trigger App.jsx auth check
                    setTimeout(() => {
                      window.location.href = "/";
                    }, 100);
                  }
                }}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all font-medium"
              >
                Go to Login
              </button>
            </div>
          </div>
        ) : (
          <>
            {activeTab === "dashboard" &&
              (() => {
                const userRole = currentUser?.role;
                const isAdmin = userRole === "Admin";
                const isTeamLead = userRole === "Team Lead";
                const isBUHead = userRole === "BU Head";
                const isAccountManager = userRole === "Account Manager";

                console.log("?? Rendering Dashboard for role:", {
                  role: userRole,
                  isAdmin,
                  isTeamLead,
                  isBUHead,
                  isAccountManager,
                });

                // Admin, Team Lead, and BU Head see AdminDashboard
                if (isAdmin || isTeamLead || isBUHead) {
                  console.log("?? Loading AdminDashboard...");
                  return (
                    <AdminDashboard
                      candidates={candidates}
                      currentUser={currentUser}
                      dataLoading={dataLoading}
                      API_BASE_URL={API_BASE_URL}
                      insightsData={insightsData}
                      buFilter={buFilter}
                      setBuFilter={setBuFilter}
                      viewMode={viewMode}
                      setViewMode={setViewMode}
                      uniqueBUHeads={uniqueBUHeads}
                      setSelectedReasonModal={setSelectedReasonModal}
                      setShowBUDrilldown={setShowBUDrilldown}
                      setShowClientDrilldown={setShowClientDrilldown}
                      showBUDrilldown={showBUDrilldown}
                      showClientDrilldown={showClientDrilldown}
                      clientFilter={clientFilter}
                      setClientFilter={setClientFilter}
                    />
                  );
                }

                // Account Manager sees regular Dashboard
                else if (isAccountManager) {
                  console.log("?? Loading Account Manager Dashboard...");
                  return (
                    <Dashboard
                      candidates={candidates}
                      currentUser={currentUser}
                      dataLoading={dataLoading}
                      insightsData={insightsData}
                      buFilter={buFilter}
                      setBuFilter={setBuFilter}
                      viewMode={viewMode}
                      setViewMode={setViewMode}
                      uniqueBUHeads={uniqueBUHeads}
                      setSelectedReasonModal={setSelectedReasonModal}
                      setShowBUDrilldown={setShowBUDrilldown}
                      setShowClientDrilldown={setShowClientDrilldown}
                      showBUDrilldown={showBUDrilldown}
                      showClientDrilldown={showClientDrilldown}
                      clientFilter={clientFilter}
                      setClientFilter={setClientFilter}
                    />
                  );
                }

                // Unknown role - show error
                else {
                  console.log("? Unknown role:", userRole);
                  return (
                    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
                      <div className="text-center max-w-md bg-white p-8 rounded-2xl shadow-xl border border-slate-200">
                        <div className="w-20 h-20 bg-gradient-to-r from-red-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6">
                          <AlertTriangle className="w-10 h-10 text-white" />
                        </div>
                        <h2 className="text-2xl font-bold text-slate-900 mb-2">
                          Unknown Role
                        </h2>
                        <p className="text-slate-600 mb-6">
                          Your account role is not recognized. Please contact
                          your administrator.
                        </p>
                        <p className="text-sm text-slate-500 mb-6">
                          Your role:{" "}
                          <span className="font-semibold text-red-600">
                            {userRole || "Not set"}
                          </span>
                        </p>
                        <button
                          onClick={() => {
                            localStorage.clear();
                            sessionStorage.clear();
                            if (window.handleAppLogout) {
                              window.handleAppLogout();
                            } else {
                              window.location.href = "/";
                            }
                          }}
                          className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all font-medium"
                        >
                          Sign Out
                        </button>
                      </div>
                    </div>
                  );
                }
              })()}

            {activeTab === "candidates" && (
              <CandidatesTab
                // Data props
                candidates={candidates}
                filteredCandidates={filteredCandidates}
                paginatedCandidates={paginatedCandidates}
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                dataLoading={dataLoading}
                currentUser={currentUser}

                API_BASE_URL={API_BASE_URL}
                // Search and filter states
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                searchScope={searchScope}
                setSearchScope={setSearchScope}
                filterStatus={filterStatus}
                setFilterStatus={setFilterStatus}
                showAdvancedFilters={showAdvancedFilters}
                setShowAdvancedFilters={setShowAdvancedFilters}
                // Advanced filter states
                clientFilter={clientFilter}
                setClientFilter={setClientFilter}
                buHeadFilter={buHeadFilter}
                setBuHeadFilter={setBuHeadFilter}
                skillsFilter={skillsFilter}
                setSkillsFilter={setSkillsFilter}
                locationFilter={locationFilter}
                setLocationFilter={setLocationFilter}
                contractTypeFilter={contractTypeFilter}
                setContractTypeFilter={setContractTypeFilter}
                rateRangeFilter={rateRangeFilter}
                setRateRangeFilter={setRateRangeFilter}
                marginFilter={marginFilter}
                setMarginFilter={setMarginFilter}
                reportingStatusFilter={reportingStatusFilter}
                setReportingStatusFilter={setReportingStatusFilter}
                projectStatusFilter={projectStatusFilter}
                setProjectStatusFilter={setProjectStatusFilter}
                endDateRangeFilter={endDateRangeFilter}
                setEndDateRangeFilter={setEndDateRangeFilter}
                customEndDateStart={customEndDateStart}
                setCustomEndDateStart={setCustomEndDateStart}
                customEndDateEnd={customEndDateEnd}
                setCustomEndDateEnd={setCustomEndDateEnd}
                clientProjectManagerFilter={clientProjectManagerFilter}
                setClientProjectManagerFilter={setClientProjectManagerFilter}
                clientResourceManagerFilter={clientResourceManagerFilter}
                setClientResourceManagerFilter={setClientResourceManagerFilter}
                reportingManagerFilter={reportingManagerFilter}
                setReportingManagerFilter={setReportingManagerFilter}
                extensionStatusFilter={extensionStatusFilter}
                setExtensionStatusFilter={setExtensionStatusFilter}
                certificationFilter={certificationFilter}
                setCertificationFilter={setCertificationFilter}
                startDateFilter={startDateFilter}
                setStartDateFilter={setStartDateFilter}
                endDateFilter={endDateFilter}
                setEndDateFilter={setEndDateFilter}
                showRecentOnboarded={showRecentOnboarded}
                setShowRecentOnboarded={setShowRecentOnboarded}
                // Unique values for filters
                uniqueClients={uniqueClients}
                uniqueBUHeads={uniqueBUHeads}
                uniqueLocations={uniqueLocations}
                uniqueContractTypes={uniqueContractTypes}
                uniqueClientProjectManagers={uniqueClientProjectManagers}
                uniqueClientResourceManagers={uniqueClientResourceManagers}
                uniqueReportingManagers={uniqueReportingManagers}
                uniqueCertifications={uniqueCertifications}
                // Action handlers
                handleReportingInfoClick={handleReportingInfoClick}
                handleProjectDetailsClick={handleProjectDetailsClick}
                handleMarkAsInterviewPanel={handleMarkAsInterviewPanel}
                setSelectedCandidate={setSelectedCandidate}
                setShowEditModal={setShowEditModal}
                setShowCandidateDetails={setShowCandidateDetails}
                setShowDeleteModal={setShowDeleteModal}
                setShowRollOffEditModal={setShowRollOffEditModal}
                setCurrentPage={setCurrentPage}
                fetchCandidates={fetchCandidates}
                setShowAddModal={setShowAddModal}
                setShowBulkEmailPanel={setShowBulkEmailPanel}
                setShowAccountManagerModal={setShowAccountManagerModal}
                getRecentCandidates={getRecentCandidates}
                setRecentCandidates={setRecentCandidates}
                setSelectedCandidatesForEmail={setSelectedCandidatesForEmail}
                sendAccountManagerNotification={sendAccountManagerNotification}
                handleEditCandidate={handleEditCandidate}
                updateCandidateInfo={handleEditCandidate}
                // Modal states
                showMoreActions={showMoreActions}
                setShowMoreActions={setShowMoreActions}
                setSelectedCandidateHistory={setSelectedCandidateHistory}
                fetchCandidateHistory={fetchCandidateHistory}
                setShowHistoryModal={setShowHistoryModal}
                fetchCandidateNotes={fetchCandidateNotes}
                setShowNotesModal={setShowNotesModal}
                // Components to pass through
                MoreActionsDropdown={MoreActionsDropdown}
                ProjectFormStatus={ProjectFormStatus}
                TableLoading={TableLoading}
              />
            )}

            {activeTab === "rolloff" && (
  <RollOffTab
    // Data props
    rollOffCandidates={rollOffCandidates}
    dataLoading={dataLoading}
    currentUser={currentUser}
    // Filter states
    extensionFilter={extensionFilter}
    setExtensionFilter={setExtensionFilter}
    // Selection states
    selectedRollOffCandidates={selectedRollOffCandidates}
    setSelectedRollOffCandidates={setSelectedRollOffCandidates}
    // Modal states
    showRollOffMoreActions={showRollOffMoreActions}
    setShowRollOffMoreActions={setShowRollOffMoreActions}
    setSelectedCandidate={setSelectedCandidate}
    setShowRollOffEditModal={setShowRollOffEditModal}
    setShowCandidateDetails={setShowCandidateDetails}
    setShowBulkUpdateModal={setShowBulkUpdateModal}
    setBulkUpdateType={setBulkUpdateType}
    setShowExtensionNotificationModal={setShowExtensionNotificationModal}
    getExtensionPendingCandidates={getExtensionPendingCandidates}
    // Handlers
    fetchCandidates={fetchCandidates}
    getDaysRemaining={getDaysRemaining}
    // Components
    MoreActionsDropdown={MoreActionsDropdown}
    // Unified modal
    updateCandidateInfo={handleEditCandidate}
    API_BASE_URL={API_BASE_URL}
  />
)}

            {activeTab === "interviewed" && (
              <InterviewedCandidates
                candidates={candidates}
                onViewCandidate={(candidate) => {
                  setSelectedCandidate(candidate);
                  setShowCandidateDetails(true);
                }}
                currentUser={currentUser}
                API_BASE_URL={API_BASE_URL}
              />
            )}

            {activeTab === "rd" && (
              <div className="space-y-6">
                <div className="rounded-3xl border border-blue-100 bg-gradient-to-br from-blue-50 via-white to-indigo-50 px-6 py-4 shadow-sm">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-blue-500">
                        RD Team Workspace
                      </p>
                      <h2 className="text-lg font-semibold text-slate-900">
                        RD Team
                      </h2>
                      <p className="text-xs text-slate-500">
                        Toggle between candidate view, redeployment analytics,
                        hotlist, and active requirements.
                      </p>
                    </div>
                    <div className="inline-flex rounded-2xl bg-white/80 p-1 shadow-inner ring-1 ring-blue-100">
                      <button
                        type="button"
                        onClick={() => setRdViewMode("active-requirements")}
                        className={`rounded-xl px-4 py-2 text-xs font-semibold transition-all ${
                          rdViewMode === "active-requirements"
                            ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow"
                            : "text-slate-500 hover:text-slate-700"
                        }`}
                      >
                        Active Requirements
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setRdViewMode("all-candidates");
                          setRdBenchFilter("all");
                        }}
                        className={`rounded-xl px-4 py-2 text-xs font-semibold transition-all ${
                          rdViewMode === "all-candidates"
                            ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow"
                            : "text-slate-500 hover:text-slate-700"
                        }`}
                      >
                        Project End Candidates
                      </button>

                       <button
                        type="button"
                        onClick={openBenchCandidatesMainPage}

                         className={`rounded-xl px-4 py-2 text-xs font-semibold transition-all ${

                          rdViewMode === "candidates"

                            ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow"

                            : "text-slate-500 hover:text-slate-700"

                        }`}
                      >
                        Bench Candidate
                      </button>
                      <button
                        type="button"
                        onClick={() => setRdViewMode("dashboard")}
                        className={`rounded-xl px-4 py-2 text-xs font-semibold transition-all ${
                          rdViewMode === "dashboard"
                            ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow"
                            : "text-slate-500 hover:text-slate-700"
                        }`}
                      >
                        RD Dashboard
                      </button>

                      <button
                        type="button"
                        onClick={() => setRdViewMode("ai")}
                        className={`rounded-xl px-4 py-2 text-xs font-semibold transition-all ${
                          rdViewMode === "ai"
                            ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow"
                            : "text-slate-500 hover:text-slate-700"
                        }`}
                      >
                        AI Search
                      </button>

                      <button
                        type="button"
                        onClick={() => setRdViewMode("hotlist")}
                        className={`rounded-xl px-4 py-2 text-xs font-semibold transition-all ${
                          rdViewMode === "hotlist"
                            ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow"
                            : "text-slate-500 hover:text-slate-700"
                        }`}
                      >
                        <span className="inline-flex items-center gap-2">
                          <BsFire className="h-6 w-6 text-red-500" />
                          Hotlist
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
                {rdViewMode === "dashboard" ? (
                  <RDDashboard
                    rdCandidates={rdCandidates}
                    rdSubmissions={rdSubmissions}
                  />
                ) : rdViewMode === "hotlist" ? (
                  <HotlistHub
                    candidates={rdVisibleCandidates}
                    initialHotlist={rdHotlistSelection}
                    onHotlistSelect={setRdHotlistSelection}
                    actionBy={currentUser?.id}
                  />
                ) : rdViewMode === "all-candidates" ? (
                  <RDAllCandidatesSection
                    candidates={rdAllCandidates}
                    dataLoading={rdAllCandidatesLoading}
                    searchTerm={rdAllCandidatesSearchTerm}
                    setSearchTerm={setRdAllCandidatesSearchTerm}
                    getRdAvailabilityStatus={getRdAvailabilityStatus}
                    rdSubmissions={rdSubmissions}
                    currentUser={currentUser}
                    setRdCandidates={setRdAllCandidates}
                    setSelectedRdCandidate={setSelectedRdCandidate}
                    setRdSidebarCandidate={setRdSidebarCandidate}
                    rdApiBaseUrl={RD_API_BASE_URL}
                    onBenchUpdated={async () => {
                      await Promise.all([
                        fetchRDAllCandidates(),
                        fetchRDCandidates(),
                      ]);
                    }}
                     onNotInterestedUpdated={async () => {

                      await fetchRDAllCandidates();

                    }}
                  />
                ) : rdViewMode === "ai" ? (
                  aiPreviewCandidate ? (
                    <RDTeamSection
                      rdOverviewCandidate={aiPreviewCandidate}
                      rdSubmissions={rdSubmissions}
                      setRdSubmissions={setRdSubmissions}
                      getRdAvailabilityStatus={getRdAvailabilityStatus}
                      searchTerm={rdBenchSearchTerm}
                      setSearchTerm={setRdBenchSearchTerm}
                      rdAdvancedFilters={rdAdvancedFilters}
                      setRdAdvancedFilters={setRdAdvancedFilters}
                      rdLocationOptions={rdLocationOptions}
                      rdExperienceOptions={rdExperienceOptions}
                      onOpenBulkEmail={() => setShowBulkEmailPanel(true)}
                      selectedRdCandidate={aiPreviewCandidate}
                      setSelectedRdCandidate={setAiPreviewCandidate}
                      setSubmissionForm={setSubmissionForm}
                      getQuarterDefaults={getQuarterDefaults}
                      setShowSubmissionModal={setShowSubmissionModal}
                      rdCvInputRef={rdCvInputRef}
                      rdCvUploads={rdCvUploads}
                      setRdCvUploads={setRdCvUploads}
                      getRdPipelineStage={getRdPipelineStage}
                      pipelineSelectedStage={pipelineSelectedStage}
                      setPipelineSelectedStage={setPipelineSelectedStage}
                      showExperienceModal={showExperienceModal}
                      setShowExperienceModal={setShowExperienceModal}
                      newExperience={newExperience}
                      setNewExperience={setNewExperience}
                      showSubmissionModal={showSubmissionModal}
                      submissionForm={submissionForm}
                      submissionRoleOptions={submissionRoleOptions}
                      setSubmissionRoleOptions={setSubmissionRoleOptions}
                      submissionClientOptions={submissionClientOptions}
                      setSubmissionClientOptions={setSubmissionClientOptions}
                      currentUser={currentUser}
                      rdVisibleCandidates={rdVisibleCandidates}
                      dataLoading={dataLoading}
                      rdBenchFilter={rdBenchFilter}
                      setRdBenchFilter={setRdBenchFilter}
                      rdStatusOptions={rdStatusOptions}
                      setShowAddRdModal={setShowAddRdModal}
                      showAddRdModal={showAddRdModal}
                      newRdCandidate={newRdCandidate}
                      setNewRdCandidate={setNewRdCandidate}
                      setRdCandidates={setRdCandidates}
                      setRdSidebarCandidate={setRdSidebarCandidate}
                      onAddToHotlist={handleRdHotlistAdd}
                      onSaveCandidateInfo={handleSaveCandidateInfo}
                      
                       onBenchUpdated={async () => {
                        await fetchRDCandidates();
                      }}
                      workAuthMap={workAuthMap}
                      workAuthMapStatus={workAuthMapStatus}
                      workAuthMapError={workAuthMapError}
                      rdApiBaseUrl={RD_API_BASE_URL}
                      rdAllCandidatesCount={rdAllCandidates.length}
                      previewOnly
                      onClosePreview={() => setAiPreviewCandidate(null)}
                    />
                  ) : (
                    <AISearchPanel 
                      rdAiSearchEndpoint={RD_SEARCH_ENDPOINT}
                      onFetchCandidateById={fetchRDCandidateById}
                     
                      onOpenCandidate={handleOpenAiCandidatePreview}
                    />
                  )
                ) : rdViewMode === "active-requirements" ? (
                  <ActiveRequirements
                    currentUser={currentUser}
                    API_BASE_URL={API_BASE_URL}
                    rdCandidates={rdAllCandidates}
                  />
                ) : (
                  <RDTeamSection
                    rdOverviewCandidate={rdOverviewCandidate}
                    rdSubmissions={rdSubmissions}
                    setRdSubmissions={setRdSubmissions}
                    getRdAvailabilityStatus={getRdAvailabilityStatus}
                    searchTerm={rdBenchSearchTerm}
                    setSearchTerm={setRdBenchSearchTerm}
                    rdAdvancedFilters={rdAdvancedFilters}
                    setRdAdvancedFilters={setRdAdvancedFilters}
                    rdLocationOptions={rdLocationOptions}
                    rdExperienceOptions={rdExperienceOptions}
                    onOpenBulkEmail={() => setShowBulkEmailPanel(true)}
                    selectedRdCandidate={selectedRdCandidate}
                    setSelectedRdCandidate={setSelectedRdCandidate}
                    setSubmissionForm={setSubmissionForm}
                    getQuarterDefaults={getQuarterDefaults}
                    setShowSubmissionModal={setShowSubmissionModal}
                    rdCvInputRef={rdCvInputRef}
                    rdCvUploads={rdCvUploads}
                    setRdCvUploads={setRdCvUploads}
                    getRdPipelineStage={getRdPipelineStage}
                    pipelineSelectedStage={pipelineSelectedStage}
                    setPipelineSelectedStage={setPipelineSelectedStage}
                    showExperienceModal={showExperienceModal}
                    setShowExperienceModal={setShowExperienceModal}
                    newExperience={newExperience}
                    setNewExperience={setNewExperience}
                    showSubmissionModal={showSubmissionModal}
                    submissionForm={submissionForm}
                    submissionRoleOptions={submissionRoleOptions}
                    setSubmissionRoleOptions={setSubmissionRoleOptions}
                    submissionClientOptions={submissionClientOptions}
                    setSubmissionClientOptions={setSubmissionClientOptions}
                    currentUser={currentUser}
                    rdVisibleCandidates={rdVisibleCandidates}
                    dataLoading={dataLoading}
                    rdBenchFilter={rdBenchFilter}
                    setRdBenchFilter={setRdBenchFilter}
                    rdStatusOptions={rdStatusOptions}
                    setShowAddRdModal={setShowAddRdModal}
                    showAddRdModal={showAddRdModal}
                    newRdCandidate={newRdCandidate}
                    setNewRdCandidate={setNewRdCandidate}
                    setRdCandidates={setRdCandidates}
                    setRdSidebarCandidate={setRdSidebarCandidate}
                    onAddToHotlist={handleRdHotlistAdd}
                    onSaveCandidateInfo={handleSaveCandidateInfo}
                    onBenchUpdated={async () => {

                      await fetchRDCandidates();

                    }}
                    workAuthMap={workAuthMap}
                    workAuthMapStatus={workAuthMapStatus}
                    workAuthMapError={workAuthMapError}
                    rdApiBaseUrl={RD_API_BASE_URL}
                    rdAllCandidatesCount={rdAllCandidates.length}
                  />
                )}
              </div>
            )}
          </>
        )}
        {activeTab === "interview" && (
          <InterviewPanel
            currentUser={currentUser}
            API_BASE_URL={API_BASE_URL}
          />
        )}

        {activeTab === "survey" && (
          <Survey
            candidates={candidates}
            currentUser={currentUser}
            dataLoading={dataLoading}
            API_BASE_URL={API_BASE_URL}
            success={success}
            error={error}
          />
        )}
      </main>

      {showAddModal && (
        <div style={{ zIndex: 10000 }}>
          <AddEditCandidateModal
            onSave={handleAddCandidate}
            onClose={() => setShowAddModal(false)}
            isEdit={false}
          />
        </div>
      )}
      {showEditModal && (
        <div style={{ zIndex: 10000 }}>
          <AddEditCandidateModal
            candidate={selectedCandidate}
            isEdit={!!selectedCandidate}
            onSave={handleEditCandidate}
            onClose={() => {
              setShowEditModal(false);
              setSelectedCandidate(null);
            }}
          />
        </div>
      )}
      {showReportingInfoModal && (
        <div style={{ zIndex: 10000 }}>
          <ReportingInfoModal
            candidate={selectedCandidate}
            onSave={handleUpdateReportingInfo}
            onClose={() => {
              setShowReportingInfoModal(false);
              setSelectedCandidate(null);
            }}
          />
        </div>
      )}

      {showProjectDetailsModal && (
        <div style={{ zIndex: 10000 }}>
          <ProjectDetailsModal
            candidate={selectedCandidate}
            onSave={handleUpdateProjectInfo}
            onClose={() => {
              setShowProjectDetailsModal(false);
              setSelectedCandidate(null);
            }}
            useToast={useToast}
            API_BASE_URL={API_BASE_URL}
            fetchCandidates={fetchCandidates}
          />
        </div>
      )}
      {showRollOffEditModal && (
        <div style={{ zIndex: 10000 }}>
          <RollOffEditModal
            candidate={selectedCandidate}
            onSave={handleRollOffUpdateWithHistory}
            onClose={() => {
              setShowRollOffEditModal(false);
              setSelectedCandidate(null);
            }}
            useToast={useToast}
            sendEmail={sendEmail}
            API_BASE_URL={API_BASE_URL}
            tabOrder={
              activeTab === "candidates" ? "feedback-first" : "extension-first"
            } // ? CHANGE THIS
          />
        </div>
      )}

      {showDeleteModal && (
        <div style={{ zIndex: 10000 }}>
          <DeleteConfirmationModal
            candidate={selectedCandidate}
            onConfirm={handleDeleteCandidate}
            onCancel={() => {
              setShowDeleteModal(false);
              setSelectedCandidate(null);
            }}
          />
        </div>
      )}

      {showImportInstructions && (
        <ImportInstructionsModal
          onClose={() => setShowImportInstructions(false)}
        />
      )}

      {showCandidateDetails && (
        <CandidateDetailsModal
          candidate={selectedCandidate}
          onClose={() => {
            setShowCandidateDetails(false);
            setSelectedCandidate(null);
          }}
        />
      )}

      <LoadingOverlay
        show={isLoading && (showAddModal || showEditModal)}
        message={showAddModal ? "Adding candidate..." : "Updating candidate..."}
        type="save"
      />

      <LoadingOverlay
        show={isLoading && showDeleteModal}
        message="Deleting candidate..."
        type="delete"
      />

      <LoadingOverlay
        show={isLoading && (showReportingInfoModal || showRollOffEditModal)}
        message="Saving information..."
        type="save"
      />

      <LoadingOverlay
        show={
          isLoading &&
          !showAddModal &&
          !showEditModal &&
          !showDeleteModal &&
          !showReportingInfoModal &&
          !showRollOffEditModal
        }
        message="Sending email..."
        type="email"
      />

      <footer className="bg-slate-900 text-white mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Candidate Management</h3>
                  <p className="text-slate-400 text-sm">
                    Account Manager Dashboard
                  </p>
                </div>
              </div>
              <p className="text-slate-300 mb-6 max-w-md">
                Streamline your candidate lifecycle management with proactive
                insights, automated workflows, and comprehensive analytics.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-6">General Queries</h4>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-slate-400" />
                  <span className="text-slate-300">cx@vdartinc.com</span>
                </div>

                <div className="flex items-center space-x-3">
                  <Users2 className="w-5 h-5 text-slate-400" />
                  <span className="text-slate-300">CX Team</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-6">Technical Support</h4>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-slate-400" />
                  <span className="text-slate-300">
                    dev.delivery@vdartinc.com
                  </span>
                </div>

                <div className="flex items-center space-x-3">
                  <Users2 className="w-5 h-5 text-slate-400" />
                  <span className="text-slate-300">Delivery Excellence</span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-800 mt-12 pt-8 text-center">
            <p className="text-slate-400">
               &copy; {new Date().getFullYear()} VDart. This portal contains
              confidential and proprietary information. Internal use only.
            </p>
          </div>
        </div>
      </footer>
      {/* Toast Notifications Container - FIXED VERSION */}
      <div
        className="fixed top-4 right-4 space-y-3 max-w-md pointer-events-none"
        style={{
          zIndex: 999999, // Extremely high z-index
          position: "fixed", // Ensure it's fixed positioning
        }}
      >
        {toasts.map((toast, index) => (
          <div
            key={toast.id}
            style={{
              transform: `translateY(${index * 10}px)`,
              zIndex: 999999 - index,
              pointerEvents: "auto",
            }}
            className="pointer-events-auto"
          >
            <Toast
              message={toast.message}
              type={toast.type}
              duration={toast.duration}
              onClose={() => removeToast(toast.id)}
            />
          </div>
        ))}
      </div>

      {/* Enhanced Complete Candidate Details Modal */}
      {showCandidateDetails && selectedCandidate && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ zIndex: 9999 }}
        >
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden">
            {/* Modern Header */}
            <div className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 px-8 py-6">
              <button
                onClick={() => {
                  setShowCandidateDetails(false);
                  setSelectedCandidate(null);
                }}
                className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full transition-all text-white/80 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex items-center space-x-6">
                {/* Modern Avatar */}
                <div className="relative">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-purple-500 rounded-2xl flex items-center justify-center text-white font-bold text-2xl shadow-xl">
                    {selectedCandidate.candidateName
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </div>
                  <div
                    className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-3 border-white ${
                      selectedCandidate.status === "Active"
                        ? "bg-green-500"
                        : "bg-gray-400"
                    }`}
                  ></div>
                </div>

                {/* Candidate Info */}
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-white mb-1">
                    {selectedCandidate.candidateName}
                  </h1>
                  <p className="text-xl text-blue-100 mb-2">
                    {selectedCandidate.jobTitle || "Position not specified"}
                  </p>
                  <div className="flex items-center space-x-4 text-sm">
                    <span className="text-blue-200 flex items-center">
                      <Building className="w-4 h-4 mr-1" />
                      {selectedCandidate.client || "Client not specified"}
                    </span>
                    <span className="text-blue-200 flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      {selectedCandidate.projectCity},{" "}
                      {selectedCandidate.projectState}
                    </span>
                    <span className="text-blue-200">
                      {selectedCandidate.plcCode}
                    </span>
                    <span className="text-blue-200">
                      {selectedCandidate.sapId}
                    </span>
                  </div>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-white">
                      {Math.max(
                        0,
                        Math.ceil(
                          (new Date(
                            selectedCandidate.sowEndDate ||
                              selectedCandidate.endDate,
                          ) -
                            new Date()) /
                            (1000 * 60 * 60 * 24),
                        ),
                      )}
                    </div>
                    <div className="text-xs text-blue-200">Days Left</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-400">
                      {selectedCandidate.marginValue || 0}%
                    </div>
                    <div className="text-xs text-blue-200">Margin</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-yellow-400">
                      {selectedCandidate.clientRating
                        ? `${selectedCandidate.clientRating}/5`
                        : "N/A"}
                    </div>
                    <div className="text-xs text-blue-200">Rating</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabbed Content Area */}
            <div className="flex">
              {/* Tab Navigation */}
              <div className="w-64 bg-gray-50 border-r border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-4">
                  Information Categories
                </h3>
                <div className="space-y-2">
                  {[
                    { id: "overview", label: "Overview", icon: User },
                    { id: "contact", label: "Contact Details", icon: Phone },
                    {
                      id: "project",
                      label: "Project Information",
                      icon: Briefcase,
                    },
                    {
                      id: "financial",
                      label: "Financial Details",
                      icon: DollarSign,
                    },
                    { id: "management", label: "Management Team", icon: Users },
                    {
                      id: "vendor",
                      label: "Vendor Information",
                      icon: Building,
                    },
                    {
                      id: "feedback",
                      label: "Client Feedback",
                      icon: MessageSquare,
                    },
                    {
                      id: "extensions",
                      label: "Extensions & Roll-off",
                      icon: Calendar,
                    },
                    {
                      id: "technical",
                      label: "Technical Details",
                      icon: Award,
                    },
                    {
                      id: "timeline",
                      label: "Timeline & Tracking",
                      icon: Clock,
                    },
                  ].map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        className="w-full flex items-center space-x-3 px-3 py-2 text-sm font-medium text-left rounded-lg hover:bg-blue-50 hover:text-blue-700 transition-colors"
                        onClick={() => {
                          document.getElementById(tab.id)?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                        }}
                      >
                        <Icon className="w-4 h-4" />
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Main Content */}
              <div className="flex-1 p-6 max-h-[calc(95vh-140px)] overflow-y-auto">
                <div className="space-y-8">
                  {/* Overview Section */}
                  <section id="overview" className="scroll-mt-4">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <User className="w-5 h-5 mr-3 text-blue-600" />
                        Candidate Overview
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="space-y-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Full Name
                            </label>
                            <div className="text-lg font-semibold text-gray-900">
                              {selectedCandidate.candidateName}
                            </div>
                          </div>

                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Date of Birth
                            </label>
                            <div className="text-gray-900">
                              {selectedCandidate.dob ||
                                selectedCandidate.dateOfBirth ||
                                "Not provided"}
                            </div>
                          </div>

                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              PLC Code
                            </label>
                            <div className="font-mono text-gray-900 bg-gray-100 px-2 py-1 rounded">
                              {selectedCandidate.plcCode}
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              SAP ID
                            </label>
                            <div className="font-mono text-gray-900 bg-gray-100 px-2 py-1 rounded">
                              {selectedCandidate.sapId}
                            </div>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Status
                            </label>
                            <div>
                              <span
                                className={`px-3 py-1 text-sm font-medium rounded-full ${
                                  selectedCandidate.status === "Active"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {selectedCandidate.status}
                              </span>
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Contract Type
                            </label>
                            <div className="text-gray-900">
                              {selectedCandidate.contractorStatus ||
                                "Not specified"}
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Business Unit
                            </label>
                            <div className="text-gray-900">
                              {selectedCandidate.buHead || "Not assigned"}
                            </div>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Created
                            </label>
                            <div className="text-gray-900">
                              {selectedCandidate.created_at
                                ? new Date(
                                    selectedCandidate.created_at,
                                  ).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  })
                                : "Not available"}
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Last Updated
                            </label>
                            <div className="text-gray-900">
                              {selectedCandidate.updated_at
                                ? new Date(
                                    selectedCandidate.updated_at,
                                  ).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  })
                                : "Not available"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Contact Details Section */}
                  <section id="contact" className="scroll-mt-4">
                    <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <Phone className="w-5 h-5 mr-3 text-green-600" />
                        Contact Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <div className="flex items-center p-4 bg-gray-50 rounded-xl">
                            <Phone className="w-5 h-5 text-gray-500 mr-3 flex-shrink-0" />
                            <div>
                              <label className="text-sm font-medium text-gray-600">
                                Phone Number
                              </label>
                              <div className="text-gray-900 font-medium">
                                {selectedCandidate.candidatePhone}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center p-4 bg-gray-50 rounded-xl">
                            <Mail className="w-5 h-5 text-gray-500 mr-3 flex-shrink-0" />
                            <div className="flex-1">
                              <label className="text-sm font-medium text-gray-600">
                                Personal Email
                              </label>
                              <div className="text-gray-900 font-medium break-all">
                                {selectedCandidate.candidateEmail}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="space-y-4">
                          {selectedCandidate.workEmail && (
                            <div className="flex items-center p-4 bg-blue-50 rounded-xl">
                              <Building className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0" />
                              <div className="flex-1">
                                <label className="text-sm font-medium text-blue-600">
                                  Work Email
                                </label>
                                <div className="text-blue-900 font-medium break-all">
                                  {selectedCandidate.workEmail}
                                </div>
                              </div>
                            </div>
                          )}
                          {selectedCandidate.reportingManagerName && (
                            <div className="p-4 bg-purple-50 rounded-xl">
                              <label className="text-sm font-medium text-purple-600">
                                Reporting Manager
                              </label>
                              <div className="text-purple-900 font-medium">
                                {selectedCandidate.reportingManagerName}
                              </div>
                              {selectedCandidate.reportingManagerEmail && (
                                <div className="text-purple-700 text-sm">
                                  {selectedCandidate.reportingManagerEmail}
                                </div>
                              )}
                              {selectedCandidate.reportingManagerPhone && (
                                <div className="text-purple-700 text-sm">
                                  {selectedCandidate.reportingManagerPhone}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Project Information Section */}
                  <section id="project" className="scroll-mt-4">
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <Briefcase className="w-5 h-5 mr-3 text-purple-600" />
                        Project Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div className="space-y-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Client
                            </label>
                            <div className="text-lg font-semibold text-gray-900">
                              {selectedCandidate.client}
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Job Title
                            </label>
                            <div className="text-gray-900">
                              {selectedCandidate.jobTitle}
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Client Track
                            </label>
                            <div className="text-gray-900">
                              {selectedCandidate.clientTrack || "Not specified"}
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              End Client
                            </label>
                            <div className="text-gray-900">
                              {selectedCandidate.endClient || "Not specified"}
                            </div>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Project Location
                            </label>
                            <div className="text-gray-900">
                              {selectedCandidate.projectCity},{" "}
                              {selectedCandidate.projectState}
                              {selectedCandidate.projectCountry &&
                                `, ${selectedCandidate.projectCountry}`}
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Client Project Manager
                            </label>
                            <div className="text-gray-900">
                              {selectedCandidate.clientProjectManager ||
                                "Not assigned"}
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Client Resource Manager
                            </label>
                            <div className="text-gray-900">
                              {selectedCandidate.clientResourceManager ||
                                "Not assigned"}
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Project Overview
                            </label>
                            <div className="text-gray-900 text-sm">
                              {selectedCandidate.projectOverview ||
                                selectedCandidate.project_overview ||
                                "Not provided"}
                            </div>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Experience Rating
                            </label>
                            <div className="flex items-center space-x-2">
                              <div className="flex items-center">
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    className={`w-4 h-4 ${
                                      i <
                                      (selectedCandidate.projectExperienceRating ||
                                        selectedCandidate.project_experience_rating ||
                                        0)
                                        ? "text-yellow-400 fill-current"
                                        : "text-gray-300"
                                    }`}
                                  />
                                ))}
                              </div>
                              <span className="text-sm text-gray-600">
                                (
                                {selectedCandidate.projectExperienceRating ||
                                  selectedCandidate.project_experience_rating ||
                                  0}
                                /5)
                              </span>
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Key Achievement
                            </label>
                            <div className="text-gray-900 text-sm">
                              {selectedCandidate.keyAchievement ||
                                selectedCandidate.key_achievement ||
                                "Not specified"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Financial Details Section */}
                  <section id="financial" className="scroll-mt-4">
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <DollarSign className="w-5 h-5 mr-3 text-green-600" />
                        Financial Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-white p-4 rounded-xl border border-green-200">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-green-600">
                              ${selectedCandidate.payRate || 0}
                            </div>
                            <div className="text-sm text-gray-600">
                              Pay Rate (
                              {selectedCandidate.payRatePaymentBasis ||
                                "hourly"}
                              )
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {selectedCandidate.payRateCurrency || "USD"}
                            </div>
                          </div>
                        </div>
                        <div className="bg-white p-4 rounded-xl border border-blue-200">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">
                              ${selectedCandidate.clientRate || 0}
                            </div>
                            <div className="text-sm text-gray-600">
                              Client Rate (
                              {selectedCandidate.clientRatePaymentBasis ||
                                "hourly"}
                              )
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {selectedCandidate.clientRateCurrency || "USD"}
                            </div>
                          </div>
                        </div>
                        <div className="bg-white p-4 rounded-xl border border-yellow-200">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-yellow-600">
                              {selectedCandidate.marginValue || 0}%
                            </div>
                            <div className="text-sm text-gray-600">Margin</div>
                            <div className="text-xs text-gray-500 mt-1">
                              $
                              {(
                                (selectedCandidate.clientRate || 0) -
                                (selectedCandidate.payRate || 0)
                              ).toFixed(2)}
                              /hr
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Management Team Section */}
                  <section id="management" className="scroll-mt-4">
                    <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <Users className="w-5 h-5 mr-3 text-indigo-600" />
                        Management Team
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div className="space-y-3">
                          <h4 className="font-semibold text-gray-700">
                            Delivery Team
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className="text-gray-500">DAL:</span>
                              <span className="ml-2 font-medium">
                                {selectedCandidate.dalName || "Not assigned"}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">DM:</span>
                              <span className="ml-2 font-medium">
                                {selectedCandidate.dmName || "Not assigned"}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">ADD:</span>
                              <span className="ml-2 font-medium">
                                {selectedCandidate.addName || "Not assigned"}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">BU Head:</span>
                              <span className="ml-2 font-medium">
                                {selectedCandidate.buHead || "Not assigned"}
                              </span>
                            </div>

                            <div>
                              <span className="text-gray-500">CAL:</span>
                              <span className="ml-2 font-medium">
                                {selectedCandidate.calName ||
                                  selectedCandidate.cal_name ||
                                  "Not assigned"}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">Team Lead:</span>
                              <span className="ml-2 font-medium">
                                {selectedCandidate.teamLeadName ||
                                  selectedCandidate.team_lead_name ||
                                  "Not assigned"}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <h4 className="font-semibold text-gray-700">
                            Client Contacts
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className="text-gray-500">
                                Project Manager:
                              </span>
                              <span className="ml-2 font-medium">
                                {selectedCandidate.clientProjectManager ||
                                  "Not specified"}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">
                                Resource Manager:
                              </span>
                              <span className="ml-2 font-medium">
                                {selectedCandidate.clientResourceManager ||
                                  "Not specified"}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <h4 className="font-semibold text-gray-700">
                            Recruiting Team
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className="text-gray-500">
                                Lead Recruiter:
                              </span>
                              <span className="ml-2 font-medium">
                                {selectedCandidate.leadRecruiter ||
                                  selectedCandidate.lead_recruiter ||
                                  "Not assigned"}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">Recruiter:</span>
                              <span className="ml-2 font-medium">
                                {selectedCandidate.recruiterName ||
                                  selectedCandidate.recruiter_name ||
                                  "Not assigned"}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">
                                Recruiter ID:
                              </span>
                              <span className="ml-2 font-medium">
                                {selectedCandidate.recruiterEmpId ||
                                  selectedCandidate.recruiter_emp_id ||
                                  "Not assigned"}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">
                                Passthrough Owner:
                              </span>
                              <span className="ml-2 font-medium">
                                {selectedCandidate.passthroughOwner ||
                                  selectedCandidate.passthrough_owner ||
                                  "Not assigned"}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">
                                Passthrough Support:
                              </span>
                              <span className="ml-2 font-medium">
                                {selectedCandidate.passthroughSupport ||
                                  selectedCandidate.passthrough_support ||
                                  "Not assigned"}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">
                                Deal PT PTR:
                              </span>
                              <span className="ml-2 font-medium">
                                {selectedCandidate.dealPtPtr ||
                                  selectedCandidate.deal_pt_ptr ||
                                  "Not assigned"}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <h4 className="font-semibold text-gray-700">
                            Account Managers
                          </h4>
                          <div className="space-y-1 text-sm">
                            {[
                              selectedCandidate.accountManager1 ||
                                selectedCandidate.account_manager_1,
                              selectedCandidate.accountManager2 ||
                                selectedCandidate.account_manager_2,
                              selectedCandidate.accountManager3 ||
                                selectedCandidate.account_manager_3,
                              selectedCandidate.accountManager4 ||
                                selectedCandidate.account_manager_4,
                              selectedCandidate.accountManager5 ||
                                selectedCandidate.account_manager_5,
                              selectedCandidate.accountManager6 ||
                                selectedCandidate.account_manager_6,
                              selectedCandidate.accountManager7 ||
                                selectedCandidate.account_manager_7,
                              selectedCandidate.accountManager8 ||
                                selectedCandidate.account_manager_8,
                            ]
                              .filter(Boolean)
                              .map((manager, index) => (
                                <div
                                  key={index}
                                  className="flex items-center space-x-2"
                                >
                                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                  <span className="font-medium">{manager}</span>
                                </div>
                              )) || (
                              <div className="text-gray-500 italic">
                                No account managers assigned
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Vendor Information Section */}
                  <section id="vendor" className="scroll-mt-4">
                    <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-2xl p-6 border border-gray-200">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <Building className="w-5 h-5 mr-3 text-gray-600" />
                        Vendor Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Vendor Name
                            </label>
                            <div className="text-gray-900 font-medium">
                              {selectedCandidate.contractorVendorName ||
                                "Not specified"}
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Contact Person
                            </label>
                            <div className="text-gray-900">
                              {selectedCandidate.vendorContactPerson ||
                                "Not specified"}
                            </div>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Contact Number
                            </label>
                            <div className="text-gray-900">
                              {selectedCandidate.vendorContactNumber ||
                                "Not specified"}
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Contact Email
                            </label>
                            <div className="text-gray-900 break-all">
                              {selectedCandidate.vendorContactEmail ||
                                "Not specified"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Skills & Technical Details Section */}
                  <section id="technical" className="scroll-mt-4">
                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-6 border border-purple-100">
                      <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                        <Award className="w-5 h-5 mr-3 text-purple-600" />
                        Technical Skills & Details
                      </h3>

                      <div className="space-y-6">
                        {/* Primary Skills */}
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">
                            Primary Skills
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {selectedCandidate.primarySkills ? (
                              selectedCandidate.primarySkills
                                .split(",")
                                .map((skill, index) => (
                                  <span
                                    key={index}
                                    className="px-3 py-1.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm font-medium rounded-lg shadow-sm"
                                  >
                                    {skill.trim()}
                                  </span>
                                ))
                            ) : (
                              <span className="text-gray-500 italic">
                                No primary skills specified
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Secondary Skills */}
                        {selectedCandidate.secondarySkills && (
                          <div>
                            <h4 className="text-sm font-semibold text-gray-700 mb-3">
                              Secondary Skills
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {selectedCandidate.secondarySkills
                                .split(",")
                                .map((skill, index) => (
                                  <span
                                    key={index}
                                    className="px-3 py-1.5 bg-gradient-to-r from-purple-500 to-purple-600 text-white text-sm font-medium rounded-lg shadow-sm"
                                  >
                                    {skill.trim()}
                                  </span>
                                ))}
                            </div>
                          </div>
                        )}

                        {/* Technical Stack */}
                        {(selectedCandidate.projectTechStack ||
                          selectedCandidate.project_tech_stack) && (
                          <div>
                            <h4 className="text-sm font-semibold text-gray-700 mb-3">
                              Project Tech Stack
                            </h4>
                            <div className="bg-white p-4 rounded-xl border border-purple-200">
                              <div className="text-gray-900 text-sm leading-relaxed">
                                {selectedCandidate.projectTechStack ||
                                  selectedCandidate.project_tech_stack}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Role & Responsibilities */}
                        {(selectedCandidate.projectRoleResponsibility ||
                          selectedCandidate.project_role_responsibility) && (
                          <div>
                            <h4 className="text-sm font-semibold text-gray-700 mb-3">
                              Role & Responsibilities
                            </h4>
                            <div className="bg-white p-4 rounded-xl border border-purple-200">
                              <div className="text-gray-900 text-sm leading-relaxed">
                                {selectedCandidate.projectRoleResponsibility ||
                                  selectedCandidate.project_role_responsibility}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </section>

                  {/* Client Feedback Section */}
                  <section id="feedback" className="scroll-mt-4">
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <MessageSquare className="w-5 h-5 mr-3 text-green-600" />
                        Client Feedback & Performance
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Rating */}
                        <div className="bg-white p-6 rounded-xl border border-green-200">
                          <div className="text-center">
                            <div className="text-3xl font-bold text-green-600 mb-2">
                              {selectedCandidate.clientRating || "N/A"}
                              {selectedCandidate.clientRating && "/5"}
                            </div>
                            <div className="flex justify-center items-center space-x-1 mb-2">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`w-5 h-5 ${
                                    i <
                                    parseFloat(
                                      selectedCandidate.clientRating || 0,
                                    )
                                      ? "text-yellow-400 fill-current"
                                      : "text-gray-300"
                                  }`}
                                />
                              ))}
                            </div>
                            <div className="text-sm text-gray-600">
                              Client Rating
                            </div>
                          </div>
                        </div>

                        {/* Feedback Text */}
                        <div className="space-y-4">
                          {selectedCandidate.clientFeedback && (
                            <div>
                              <label className="text-sm font-semibold text-gray-700">
                                Client Comments
                              </label>
                              <div className="bg-white p-4 rounded-xl border border-green-200 mt-2">
                                <p className="text-gray-700 leading-relaxed italic">
                                  "{selectedCandidate.clientFeedback}"
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Improvement Areas */}
                          {(selectedCandidate.improvementArea1 ||
                            selectedCandidate.improvementArea2 ||
                            selectedCandidate.improvementArea3) && (
                            <div>
                              <label className="text-sm font-semibold text-gray-700">
                                Areas for Improvement
                              </label>
                              <div className="mt-2 space-y-2">
                                {[
                                  selectedCandidate.improvementArea1,
                                  selectedCandidate.improvementArea2,
                                  selectedCandidate.improvementArea3,
                                ]
                                  .filter(Boolean)
                                  .map((area, index) => (
                                    <div
                                      key={index}
                                      className="flex items-center space-x-2 text-sm"
                                    >
                                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                                      <span className="text-gray-700">
                                        {area}
                                      </span>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Extensions & Roll-off Section */}
                  <section id="extensions" className="scroll-mt-4">
                    <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-6 border border-orange-100">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <Calendar className="w-5 h-5 mr-3 text-orange-600" />
                        Contract Extensions & Roll-off
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {/* Extension Status */}
                        <div className="bg-white p-4 rounded-xl border border-orange-200">
                          <div className="text-center">
                            <div className="text-lg font-bold mb-2">
                              {selectedCandidate.extensionStatus ||
                                selectedCandidate.extension_status ||
                                "Pending"}
                            </div>
                            <div className="text-sm text-gray-600">
                              Extension Status
                            </div>
                            {selectedCandidate.extended_count > 0 && (
                              <div className="text-xs text-green-600 mt-1">
                                Extended {selectedCandidate.extended_count} time
                                {selectedCandidate.extended_count !== 1
                                  ? "s"
                                  : ""}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Contract Dates */}
                        <div className="space-y-3">
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Original End Date
                            </label>
                            <div className="font-semibold text-gray-900">
                              {selectedCandidate.endDate
                                ? new Date(
                                    selectedCandidate.endDate,
                                  ).toLocaleDateString("en-US", {
                                    month: "2-digit",
                                    day: "2-digit",
                                    year: "numeric",
                                  })
                                : "Not set"}
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              SOW End Date
                            </label>
                            <div className="font-semibold text-gray-900">
                              {selectedCandidate.sowEndDate
                                ? new Date(
                                    selectedCandidate.sowEndDate,
                                  ).toLocaleDateString("en-US", {
                                    month: "2-digit",
                                    day: "2-digit",
                                    year: "numeric",
                                  })
                                : "Not set"}
                            </div>
                          </div>
                          {selectedCandidate.extendedSowDate && (
                            <div>
                              <label className="text-sm font-medium text-gray-600">
                                Extended Date
                              </label>
                              <div className="font-semibold text-green-600">
                                {new Date(
                                  selectedCandidate.extendedSowDate,
                                ).toLocaleDateString("en-US", {
                                  month: "2-digit",
                                  day: "2-digit",
                                  year: "numeric",
                                })}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Extension Details */}
                        <div className="space-y-3">
                          {selectedCandidate.extensionComments && (
                            <div>
                              <label className="text-sm font-medium text-gray-600">
                                Extension Comments
                              </label>
                              <div className="text-sm text-gray-900 bg-white p-3 rounded-lg border">
                                {selectedCandidate.extensionComments}
                              </div>
                            </div>
                          )}
                          {selectedCandidate.noExtensionReason && (
                            <div>
                              <label className="text-sm font-medium text-gray-600">
                                Non-Extension Reason
                              </label>
                              <div className="text-sm text-red-700 bg-red-50 p-3 rounded-lg border border-red-200">
                                {selectedCandidate.noExtensionReason}
                              </div>
                            </div>
                          )}
                          {selectedCandidate.contactedWith && (
                            <div>
                              <label className="text-sm font-medium text-gray-600">
                                Contacted With
                              </label>
                              <div className="text-sm text-gray-900">
                                {selectedCandidate.contactedWith}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Timeline & Tracking Section */}
                  <section id="timeline" className="scroll-mt-4">
                    <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <Clock className="w-5 h-5 mr-3 text-blue-600" />
                        Timeline & System Tracking
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {/* Project Timeline */}
                        <div className="space-y-3">
                          <h4 className="font-semibold text-gray-700">
                            Project Timeline
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className="text-gray-500">Start Date:</span>
                              <span className="ml-2 font-medium">
                                {selectedCandidate.startDate
                                  ? new Date(
                                      selectedCandidate.startDate,
                                    ).toLocaleDateString("en-US", {
                                      month: "2-digit",
                                      day: "2-digit",
                                      year: "numeric",
                                    })
                                  : "Not set"}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">
                                Days Active:
                              </span>
                              <span className="ml-2 font-medium">
                                {selectedCandidate.startDate
                                  ? Math.ceil(
                                      (new Date() -
                                        new Date(selectedCandidate.startDate)) /
                                        (1000 * 60 * 60 * 24),
                                    )
                                  : 0}{" "}
                                days
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">
                                Days Remaining:
                              </span>
                              <span className="ml-2 font-medium">
                                {Math.max(
                                  0,
                                  Math.ceil(
                                    (new Date(
                                      selectedCandidate.sowEndDate ||
                                        selectedCandidate.endDate,
                                    ) -
                                      new Date()) /
                                      (1000 * 60 * 60 * 24),
                                  ),
                                )}{" "}
                                days
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* System Updates */}
                        <div className="space-y-3">
                          <h4 className="font-semibold text-gray-700">
                            System Updates
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className="text-gray-500">
                                Reporting Updated:
                              </span>
                              <div className="text-gray-900">
                                {selectedCandidate.reporting_updated_at
                                  ? new Date(
                                      selectedCandidate.reporting_updated_at,
                                    ).toLocaleDateString()
                                  : "Never"}
                              </div>
                              {selectedCandidate.reporting_updated_by && (
                                <div className="text-xs text-gray-500">
                                  by {selectedCandidate.reporting_updated_by}
                                </div>
                              )}
                            </div>
                            <div>
                              <span className="text-gray-500">
                                Project Updated:
                              </span>
                              <div className="text-gray-900">
                                {selectedCandidate.project_updated_at
                                  ? new Date(
                                      selectedCandidate.project_updated_at,
                                    ).toLocaleDateString()
                                  : "Never"}
                              </div>
                              {selectedCandidate.project_updated_by && (
                                <div className="text-xs text-gray-500">
                                  by {selectedCandidate.project_updated_by}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Project Form Status */}
                        <div className="space-y-3">
                          <h4 className="font-semibold text-gray-700">
                            Project Form Status
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className="text-gray-500">Form Sent:</span>
                              <div className="text-gray-900">
                                {selectedCandidate.project_form_sent_at
                                  ? new Date(
                                      selectedCandidate.project_form_sent_at,
                                    ).toLocaleDateString()
                                  : "Not sent"}
                              </div>
                            </div>
                            <div>
                              <span className="text-gray-500">
                                Form Completed:
                              </span>
                              <div className="text-gray-900">
                                {selectedCandidate.project_form_completed_at
                                  ? new Date(
                                      selectedCandidate.project_form_completed_at,
                                    ).toLocaleDateString()
                                  : "Not completed"}
                              </div>
                            </div>
                            <div>
                              <span className="text-gray-500">
                                Reminders Sent:
                              </span>
                              <div className="text-gray-900">
                                {selectedCandidate.project_form_reminder_count ||
                                  0}
                              </div>
                            </div>
                            {selectedCandidate.project_form_last_reminder_at && (
                              <div>
                                <span className="text-gray-500">
                                  Last Reminder:
                                </span>
                                <div className="text-gray-900">
                                  {new Date(
                                    selectedCandidate.project_form_last_reminder_at,
                                  ).toLocaleDateString()}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Quick Actions Section */}
                  <section className="scroll-mt-4">
                    <div className="bg-gradient-to-r from-slate-50 to-gray-50 rounded-2xl p-6 border border-gray-200">
                      <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                        <Zap className="w-5 h-5 mr-3 text-yellow-600" />
                        Quick Actions
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* Update Reporting Info */}
                        <button
                          onClick={() => {
                            setShowCandidateDetails(false);
                            setTimeout(() => {
                              handleReportingInfoClick(selectedCandidate);
                            }, 100);
                          }}
                          className="group flex items-center p-4 bg-white rounded-xl hover:bg-purple-50 hover:border-purple-200 border border-gray-200 transition-all duration-200 hover:shadow-md"
                        >
                          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-purple-200 transition-colors">
                            <Users className="w-6 h-6 text-purple-600" />
                          </div>
                          <div className="text-left">
                            <div className="font-semibold text-gray-900 text-sm">
                              Update Reporting
                            </div>
                            <div className="text-xs text-gray-500">
                              Manager details
                            </div>
                          </div>
                        </button>

                        {/* Project Details */}
                        <button
                          onClick={() => {
                            setShowCandidateDetails(false);
                            setTimeout(() => {
                              handleProjectDetailsClick(selectedCandidate);
                            }, 100);
                          }}
                          className="group flex items-center p-4 bg-white rounded-xl hover:bg-blue-50 hover:border-blue-200 border border-gray-200 transition-all duration-200 hover:shadow-md"
                        >
                          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-blue-200 transition-colors">
                            <Briefcase className="w-6 h-6 text-blue-600" />
                          </div>
                          <div className="text-left">
                            <div className="font-semibold text-gray-900 text-sm">
                              Project Details
                            </div>
                            <div className="text-xs text-gray-500">
                              Tech stack & scope
                            </div>
                          </div>
                        </button>

                        {/* Client Feedback */}
                        <button
                          onClick={() => {
                            const tempCandidate = selectedCandidate;
                            setShowCandidateDetails(false);
                            setTimeout(() => {
                              setSelectedCandidate(tempCandidate);
                              setShowRollOffEditModal(true);
                            }, 100);
                          }}
                          className="group flex items-center p-4 bg-white rounded-xl hover:bg-orange-50 hover:border-orange-200 border border-gray-200 transition-all duration-200 hover:shadow-md"
                        >
                          <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-orange-200 transition-colors">
                            <MessageSquare className="w-6 h-6 text-orange-600" />
                          </div>
                          <div className="text-left">
                            <div className="font-semibold text-gray-900 text-sm">
                              Client Feedback
                            </div>
                            <div className="text-xs text-gray-500">
                              Rate & review
                            </div>
                          </div>
                        </button>

                        {/* Edit Details */}
                        {/* <button
                          onClick={() => {
                            const tempCandidate = selectedCandidate;
                            setShowCandidateDetails(false);
                            setTimeout(() => {
                              setSelectedCandidate(tempCandidate);
                              setShowEditModal(true);
                            }, 100);
                          }}
                          className="group flex items-center p-4 bg-white rounded-xl hover:bg-gray-50 hover:border-gray-300 border border-gray-200 transition-all duration-200 hover:shadow-md"
                        >
                          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-gray-200 transition-colors">
                            <Edit2 className="w-6 h-6 text-gray-600" />
                          </div>
                          <div className="text-left">
                            <div className="font-semibold text-gray-900 text-sm">
                              Edit Details
                            </div>
                            <div className="text-xs text-gray-500">
                              Basic information
                            </div>
                          </div>
                        </button> */}

                        {/* View History */}
                        <button
                          onClick={() => {
                            const tempCandidate = selectedCandidate;
                            setShowCandidateDetails(false);
                            setTimeout(() => {
                              setSelectedCandidateHistory(tempCandidate);
                              fetchCandidateHistory(tempCandidate.id);
                              setShowHistoryModal(true);
                            }, 100);
                          }}
                          className="group flex items-center p-4 bg-white rounded-xl hover:bg-green-50 hover:border-green-200 border border-gray-200 transition-all duration-200 hover:shadow-md"
                        >
                          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-green-200 transition-colors">
                            <Clock className="w-6 h-6 text-green-600" />
                          </div>
                          <div className="text-left">
                            <div className="font-semibold text-gray-900 text-sm">
                              View History
                            </div>
                            <div className="text-xs text-gray-500">
                              Change log
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>
                  </section>

                  {/* Additional Information Cards */}
                  {(selectedCandidate.reportingComments ||
                    selectedCandidate.projectBusinessProblem ||
                    selectedCandidate.project_business_problem ||
                    selectedCandidate.projectScopeObjectives ||
                    selectedCandidate.project_scope_objectives) && (
                    <section className="scroll-mt-4">
                      <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                          <FileText className="w-5 h-5 mr-3 text-gray-600" />
                          Additional Notes & Information
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {selectedCandidate.reportingComments && (
                            <div>
                              <label className="text-sm font-semibold text-gray-700 mb-2 block">
                                Reporting Comments
                              </label>
                              <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                                <p className="text-gray-700 text-sm leading-relaxed">
                                  {selectedCandidate.reportingComments}
                                </p>
                              </div>
                            </div>
                          )}

                          {(selectedCandidate.projectBusinessProblem ||
                            selectedCandidate.project_business_problem) && (
                            <div>
                              <label className="text-sm font-semibold text-gray-700 mb-2 block">
                                Business Problem
                              </label>
                              <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
                                <p className="text-gray-700 text-sm leading-relaxed">
                                  {selectedCandidate.projectBusinessProblem ||
                                    selectedCandidate.project_business_problem}
                                </p>
                              </div>
                            </div>
                          )}

                          {(selectedCandidate.projectScopeObjectives ||
                            selectedCandidate.project_scope_objectives) && (
                            <div className="md:col-span-2">
                              <label className="text-sm font-semibold text-gray-700 mb-2 block">
                                Project Scope & Objectives
                              </label>
                              <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                                <p className="text-gray-700 text-sm leading-relaxed">
                                  {selectedCandidate.projectScopeObjectives ||
                                    selectedCandidate.project_scope_objectives}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </section>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Candidate Modal Placeholder */}
      {showAddModal && (
        <div style={{ zIndex: 10000 }}>
          <AddEditCandidateModal
            onSave={handleAddCandidate}
            onClose={() => setShowAddModal(false)}
            isEdit={false}
          />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedCandidate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mr-4">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">
                  Confirm Deletion
                </h3>
                <p className="text-sm text-slate-600">
                  This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-slate-700 mb-2">
                Are you sure you want to delete the following candidate?
              </p>
              <div className="bg-slate-50 p-3 rounded-xl">
                <p className="font-semibold text-slate-900">
                  {selectedCandidate.candidateName}
                </p>
                <p className="text-sm text-slate-600">
                  {selectedCandidate.candidateEmail}
                </p>
                <p className="text-sm text-slate-600">
                  {selectedCandidate.client} ? {selectedCandidate.jobTitle}
                </p>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedCandidate(null);
                }}
                className="flex-1 px-4 py-2 text-slate-700 border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteCandidate(selectedCandidate.id)}
                disabled={isLoading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extension Reason Modal */}
      {selectedExtensionReason && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white flex items-center justify-between">
              <h2 className="text-xl font-bold">
                {selectedExtensionReason === "EXTENDED"
                  ? "Extended Candidates"
                  : selectedExtensionReason === "NOT_EXTENDED"
                    ? "Not Extended Candidates"
                    : `Not Extended - ${selectedExtensionReason}`}
              </h2>
              <button
                onClick={() => setSelectedExtensionReason(null)}
                className="text-white hover:text-gray-200"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Candidate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Client
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        BU
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        End Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(selectedExtensionReason === "EXTENDED"
                      ? extensionReasonsData.extendedCandidates
                      : selectedExtensionReason === "NOT_EXTENDED"
                        ? extensionReasonsData.notExtendedCandidates
                        : extensionReasonsData.notExtendedCandidates.filter(
                            (c) =>
                              c.noExtensionReason === selectedExtensionReason,
                          )
                    ).map((candidate, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {candidate.candidateName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {candidate.client}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            {candidate.buHead}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {candidate.endDate}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              selectedExtensionReason === "EXTENDED"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {selectedExtensionReason === "EXTENDED"
                              ? "Extended"
                              : selectedExtensionReason}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Risk Level Modal */}
      {selectedRiskLevel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 bg-gradient-to-r from-red-500 to-pink-600 text-white flex items-center justify-between">
              <h2 className="text-xl font-bold">
                {selectedRiskLevel.charAt(0).toUpperCase() +
                  selectedRiskLevel.slice(1)}{" "}
                Risk Candidates
              </h2>
              <button
                onClick={() => setSelectedRiskLevel(null)}
                className="text-white hover:text-gray-200"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Candidate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Client
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        End Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Risk Score
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Days Left
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {riskAnalytics[selectedRiskLevel + "Risk"].map(
                      (candidate, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {candidate.candidateName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {candidate.client}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {candidate.endDate}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full ${
                                candidate.riskLevel === "High"
                                  ? "bg-red-100 text-red-800"
                                  : candidate.riskLevel === "Medium"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-green-100 text-green-800"
                              }`}
                            >
                              {candidate.riskScore}%
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {Math.ceil(
                              (new Date(candidate.endDate) - new Date()) /
                                (1000 * 60 * 60 * 24),
                            )}{" "}
                            days
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* BU Detail Modal */}
      {selectedBUDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white flex items-center justify-between">
              <h2 className="text-xl font-bold">
                {selectedBUDetail.buHead} - Performance Details
              </h2>
              <button
                onClick={() => setSelectedBUDetail(null)}
                className="text-white hover:text-gray-200"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-blue-50 rounded-xl">
                  <div className="text-2xl font-bold text-blue-600">
                    {selectedBUDetail.totalCandidates}
                  </div>
                  <div className="text-sm text-gray-600">Total Candidates</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-xl">
                  <div className="text-2xl font-bold text-green-600">
                    {selectedBUDetail.activeCandidates}
                  </div>
                  <div className="text-sm text-gray-600">Active</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-xl">
                  <div className="text-2xl font-bold text-yellow-600">
                    {selectedBUDetail.rollingOff}
                  </div>
                  <div className="text-sm text-gray-600">Rolling Off</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-xl">
                  <div className="text-2xl font-bold text-purple-600">
                    {selectedBUDetail.successRate}%
                  </div>
                  <div className="text-sm text-gray-600">Success Rate</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Client Detail Modal */}
      {selectedClientDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white flex items-center justify-between">
              <h2 className="text-xl font-bold">
                {selectedClientDetail.client} - Client Details
              </h2>
              <button
                onClick={() => setSelectedClientDetail(null)}
                className="text-white hover:text-gray-200"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-blue-50 rounded-xl">
                  <div className="text-2xl font-bold text-blue-600">
                    {selectedClientDetail.totalCandidates}
                  </div>
                  <div className="text-sm text-gray-600">Total</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-xl">
                  <div className="text-2xl font-bold text-green-600">
                    {selectedClientDetail.extended}
                  </div>
                  <div className="text-sm text-gray-600">Extended</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-xl">
                  <div className="text-2xl font-bold text-red-600">
                    {selectedClientDetail.rollingOff -
                      selectedClientDetail.extended}
                  </div>
                  <div className="text-sm text-gray-600">Not Extended</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-xl">
                  <div className="text-2xl font-bold text-purple-600">
                    {selectedClientDetail.successRate}%
                  </div>
                  <div className="text-sm text-gray-600">Success</div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Candidate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Job Title
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        End Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedClientDetail.candidates.map((candidate, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {candidate.candidateName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {candidate.jobTitle}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              candidate.status === "Active"
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {candidate.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {candidate.endDate}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reason Modal */}
      {selectedReasonModal && (
        <CandidatesModal
          reason={selectedReasonModal.reason}
          bu={selectedReasonModal.bu}
          onClose={() => setSelectedReasonModal(null)}
          clientData={insightsData.clientData}
          mainCandidates={candidates}
        />
      )}

      {/* BU Detail Modal */}
      {selectedBUModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white flex items-center justify-between">
              <h2 className="text-xl font-bold">
                {selectedBUModal.bu} - Performance Details
              </h2>
              <button
                onClick={() => setSelectedBUModal(null)}
                className="text-white hover:text-gray-200"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-blue-50 rounded-xl">
                  <div className="text-2xl font-bold text-blue-600">
                    {selectedBUModal.totalCandidates}
                  </div>
                  <div className="text-sm text-gray-600">Total</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-xl">
                  <div className="text-2xl font-bold text-green-600">
                    {selectedBUModal.totalExtended}
                  </div>
                  <div className="text-sm text-gray-600">Extended</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-xl">
                  <div className="text-2xl font-bold text-red-600">
                    {selectedBUModal.totalNotExtended}
                  </div>
                  <div className="text-sm text-gray-600">Not Extended</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-xl">
                  <div className="text-2xl font-bold text-purple-600">
                    {selectedBUModal.successRate}%
                  </div>
                  <div className="text-sm text-gray-600">Success Rate</div>
                </div>
              </div>

              <ReasonsPieChart
                data={selectedBUModal.reasons}
                title={`${selectedBUModal.bu} - Non-Extension Reasons`}
                onReasonClick={(reason) => {
                  setSelectedBUModal(null);
                  setSelectedReasonModal({ reason, bu: selectedBUModal.bu });
                }}
                bu={selectedBUModal.bu}
              />
            </div>
          </div>
        </div>
      )}

      {/* Client Detail Modal */}
      {selectedClientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white flex items-center justify-between">
              <h2 className="text-xl font-bold">
                {selectedClientModal.client} - Candidate Details
              </h2>
              <button
                onClick={() => setSelectedClientModal(null)}
                className="text-white hover:text-gray-200"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-blue-50 rounded-xl">
                  <div className="text-2xl font-bold text-blue-600">
                    {selectedClientModal.totalCandidates}
                  </div>
                  <div className="text-sm text-gray-600">Total</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-xl">
                  <div className="text-2xl font-bold text-green-600">
                    {selectedClientModal.extended}
                  </div>
                  <div className="text-sm text-gray-600">Extended</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-xl">
                  <div className="text-2xl font-bold text-red-600">
                    {selectedClientModal.notExtended}
                  </div>
                  <div className="text-sm text-gray-600">Not Extended</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-xl">
                  <div className="text-2xl font-bold text-purple-600">
                    {selectedClientModal.successRate}%
                  </div>
                  <div className="text-sm text-gray-600">Success</div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        End Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Extended
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Reason
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Rating
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedClientModal.candidates
                      .filter((c) => c.isEnding)
                      .map((candidate, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {candidate.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                              {candidate.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {candidate.projectEndDate}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full ${
                                candidate.isExtended
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {candidate.isExtended ? "Yes" : "No"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {candidate.reason ? (
                              <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                                {candidate.reason}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-500">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Star className="h-4 w-4 text-yellow-400 mr-1" />
                              <span className="text-sm text-gray-900">
                                {candidate.rating}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAccountManagerModal && (
        <AccountManagerModal
          onClose={() => setShowAccountManagerModal(false)}
          recentCandidates={recentCandidates}
          selectedCandidatesForEmail={selectedCandidatesForEmail}
          setSelectedCandidatesForEmail={setSelectedCandidatesForEmail}
          sendingAccountManagerEmail={sendingAccountManagerEmail}
          sendAccountManagerNotification={sendAccountManagerNotification}
        />
      )}

      {/* Bulk Email Modal */}
      {showBulkEmailPanel && (
        <BulkEmailModal
          candidates={candidates}
          currentUser={currentUser}
          onClose={() => setShowBulkEmailPanel(false)}
          useToast={useToast}
          sendEmail={sendEmail}
          API_BASE_URL={API_BASE_URL}
        />
      )}

      {showActivityLogModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[95vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <Activity className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">System Activity Log</h2>
                    <p className="text-blue-100 text-sm">
                      Track all system activities and user actions
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowActivityLogModal(false)}
                  className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 max-h-[calc(95vh-120px)] overflow-y-auto">
              <ActivityLogSystem />
            </div>
          </div>
        </div>
      )}

{showExtensionNotificationModal && (
  <ExtensionNotificationModal
    onClose={() => setShowExtensionNotificationModal(false)}
    getExtensionPendingCandidates={getExtensionPendingCandidates}
    getDaysRemaining={getDaysRemaining}
    sendAccountManagerNotification={sendAccountManagerNotification}
    error={error}
    success={success}
  />
)}

      {showBulkUpdateModal && (
        <BulkUpdateModal
          type={bulkUpdateType}
          candidates={selectedRollOffCandidates}
          onClose={() => setShowBulkUpdateModal(false)}
          onUpdate={handleBulkUpdateWithHistory} // Changed from handleBulkUpdate
        />
      )}

      {showAdvancedExportModal && (
  <AdvancedExportModal
    onClose={() => setShowAdvancedExportModal(false)}
    candidates={candidates}
    onExport={handleAdvancedExport}
    isExporting={exportLoading}
    uniqueBUHeads={uniqueBUHeads}
  />
)}

      {/* User Management Modal */}
      {showUserManagementModal && <UserManagementModal />}

      {/* Edit User Modal */}
      {showEditUserModal && <EditUserModal />}

      {showHistoryModal && selectedCandidateHistory && (
        <div style={{ zIndex: 10000 }}>
          <CandidateHistoryModal
            candidate={selectedCandidateHistory}
            onClose={() => {
              setShowHistoryModal(false);
              setSelectedCandidateHistory(null);
              setCandidateHistory([]);
            }}
            historyLoading={historyLoading}
            candidateHistory={candidateHistory}
            fetchCandidateHistory={fetchCandidateHistory}
          />
        </div>
      )}

      {/* Enhanced Notes Modal */}
      {showNotesModal && selectedCandidate && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ zIndex: 10000 }}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Candidate Notes</h2>
                    <p className="text-blue-100 text-sm">
                      {selectedCandidate.candidateName} ?{" "}
                      {candidateNotesList.length} notes
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowNotesModal(false);
                    setSelectedCandidate(null);
                    setCandidateNotes("");
                    setCandidateNotesList([]);
                  }}
                  className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex h-[calc(90vh-120px)]">
              {/* Add New Note Panel */}
              <div className="w-1/2 p-6 border-r border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">
                  Add New Note
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Note Type
                    </label>
                    <select
                      value={noteType}
                      onChange={(e) => setNoteType(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="general">General</option>
                      <option value="performance">Performance</option>
                      <option value="client_feedback">Client Feedback</option>
                      <option value="technical">Technical</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Note Content
                    </label>
                    <textarea
                      value={candidateNotes}
                      onChange={(e) => setCandidateNotes(e.target.value)}
                      rows={8}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Add your note here..."
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="important"
                      checked={isImportantNote}
                      onChange={(e) => setIsImportantNote(e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label
                      htmlFor="important"
                      className="ml-2 text-sm text-slate-700"
                    >
                      Mark as important
                    </label>
                  </div>

                  <button
                    onClick={() =>
                      handleAddNote(
                        selectedCandidate.id,
                        candidateNotes,
                        noteType,
                        isImportantNote,
                      )
                    }
                    disabled={isLoading || !candidateNotes.trim()}
                    className="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50"
                  >
                    {isLoading ? "Adding..." : "Add Note"}
                  </button>
                </div>
              </div>

              {/* Notes History Panel */}
              <div className="w-1/2 p-6 overflow-y-auto">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">
                  Notes History
                </h3>

                {notesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-slate-400 mr-2" />
                    <span className="text-slate-500">Loading notes...</span>
                  </div>
                ) : candidateNotesList.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    No notes yet. Add your first note!
                  </div>
                ) : (
                  <div className="space-y-4">
                    {candidateNotesList.map((note, index) => (
                      <div
                        key={note.id}
                        className={`p-4 rounded-lg border-2 ${
                          note.is_important
                            ? "border-red-200 bg-red-50"
                            : "border-slate-200 bg-slate-50"
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full ${
                                note.note_type === "performance"
                                  ? "bg-green-100 text-green-800"
                                  : note.note_type === "client_feedback"
                                    ? "bg-blue-100 text-blue-800"
                                    : note.note_type === "technical"
                                      ? "bg-purple-100 text-purple-800"
                                      : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {note.note_type.replace("_", " ")}
                            </span>
                            {(note.is_important === 1 ||
                              note.is_important === true ||
                              note.is_important === "1") && (
                              <span className="text-red-500 text-xs font-bold">
                                IMPORTANT
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => deleteNote(note.id)}
                            className="text-red-500 hover:text-red-700 text-xs"
                          >
                            Delete
                          </button>
                        </div>

                        <p className="text-sm text-slate-700 mb-2">
                          {note.note_text}
                        </p>

                        <div className="text-xs text-slate-500">
                          {new Date(note.added_at).toLocaleDateString("en-US", {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                          })}{" "}
                          {new Date(note.added_at).toLocaleTimeString()} ? by{" "}
                          {note.added_by}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <GoogleSheetsSyncModal
        isOpen={showSyncModal}
        onClose={() => setShowSyncModal(false)}
        API_BASE_URL={API_BASE_URL}
        onSyncComplete={handleSyncComplete}
      />

      {showTutorialModal && (
        <div style={{ zIndex: 10000 }}>
          <TutorialModal
            isOpen={showTutorialModal}
            onClose={() => setShowTutorialModal(false)}
            currentUser={currentUser}
          />
        </div>
      )}
    {showSessionExpiredBanner && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[999999]">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center mx-4">
            <div className="w-20 h-20 bg-gradient-to-r from-orange-400 to-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Session Expired</h2>
            <p className="text-slate-600 mb-6">
              Your session has expired due to inactivity. Please log in again to continue.
            </p>
            <button
              onClick={() => {
                localStorage.clear();
                sessionStorage.clear();
                setShowSessionExpiredBanner(false);
                if (window.handleAppLogout) {
                  window.handleAppLogout();
                } else {
                  window.location.href = '/';
                }
              }}
              className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all font-medium"
            >
              Go to Login
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Wrap the component with Error Boundary
const WrappedLandingPage = () => (
  <ErrorBoundary>
    <LandingPage />
  </ErrorBoundary>
);

export default WrappedLandingPage;